/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { ConversationPii, ConversationSummary, Transcription } from 'src/languageStudio/core/types';

export type DemoSample = { title: string; document: string; language?: Language['code'] };
export type QuestionsAnswersDemoSample = DemoSample & { question: string };
export type CallCenterDemoSample = DemoSample & {
    audioLink: string;
    textInput: string;
    sampleType: 'audio' | 'text';
    isSingleConversation?: boolean;
    output: {
        transcription: Transcription;
        conversationAnalyticsResults: { conversationSummaryResults: ConversationSummary; conversationPiiResults: ConversationPii };
    };
};

export const questionsAnsweringDemoSamples: QuestionsAnswersDemoSample[] = [
    {
        language: 'en',
        title: 'Sample 1',
        question: 'what is the size of surface laptop 4 models?',
        document:
            'Surface Laptop 4 is, first and foremost, optimized for Microsoft experiences. It retains the iconic design, details and materials that our customers love. With our signature 3:2 PixelSense touchscreen displays in 13.5” or 15” models, customers can choose from Alcantara or metal finishes in a variety of bold colors you can’t get anywhere else, including a new Ice Blue finish. Surface Laptop 4 offers a built-in HD front-facing camera with incredible low-light capability and a studio microphone array, ensuring you’ll come through loud and clear as you connect with loved ones or participate in a crucial virtual work meeting. With the 201 PPI, high-contrast touchscreen display, and Dolby® Atmos™ Omnisonic speakers, you’ll get a cinematic experience that immerses you in your favorite movies and shows from the comfort of anywhere. Whether through the vibrant touchscreen display, large trackpad with gesture support, or its industry-leading typing experience, Surface Laptop can adjust to your preferred workflow. Surface Laptop 4 offers a choice between either the 11th Gen Intel® Core™ processors, or AMD Ryzen™ Mobile Processors with Radeon™ Graphics Microsoft Surface® Edition (8 cores); in either case, you’ll find a smooth experience that powers modern, multitasking demands. Across both options, we’ve partnered to customize silicon in ways that boost performance while offering improved battery life. On the outside, you see style and sophistication. Effortlessly lift the screen, and this product will draw you into your flow. From the office, living room, coffee shop or classroom, nothing comes close to this product’s look and feel.'
    },
    {
        language: 'en',
        title: 'Sample 2',
        question: 'What are sustainable development goals?',
        document: `The Sustainable Development Goals (SDGs), also known as the Global Goals, were adopted by all United Nations Member States in 2015 as a universal call to action to end poverty, protect the planet and ensure that all people enjoy peace and prosperity by 2030. The 17 SDGs are integrated—that is, they recognize that action in one area will affect outcomes in others, and that development must balance social, economic and environmental sustainability.


        Through the pledge to Leave No One Behind, countries have committed to fast-track progress for those furthest behind first. That is why the SDGs are designed to bring the world to several life-changing ‘zeros’, including zero poverty, hunger, AIDS and discrimination against women and girls. Everyone is needed to reach these ambitious targets. The creativity, knowhow, technology and financial resources from all of society is necessary to achieve the SDGs in every context.`
    },
    {
        language: 'en',
        title: 'Sample 3',
        question: 'What is oculus?',
        document: `The Space Needle is one of the most recognizable landmarks in the world and is a treasured Seattle icon. Built for the 1962 World’s Fair—the Century 21 Exposition whose theme was “The Age of Space”—the tower’s futuristic design was inspired by the idea that the fair needed a structure to symbolize humanity’s Space Age aspirations. Since its grand opening on April 21, 1962, the landmark continues to symbolize the innovative and forward-thinking spirit of Seattle. Located at Seattle Center, the Space Needle stands at 605’ tall and is one of the most photographed structures in the world. The tower’s 520’ saucer-shaped “top house” offers visitors Seattle’s only 360-degree indoor and outdoor panoramic views of downtown, Mount Rainier, Puget Sound, and the Cascades and Olympic mountain ranges.

        To further elevate the visitor experience, the Space Needle embarked on a multi-year venture focused on the preservation and renovation of the 57-year-old icon. The first phase of the renovation, completed in late summer 2018, includes breathtaking, multi-level, floor-to-ceiling glass viewing experiences including an upper level outdoor observation deck with open-air glass walls and Skyriser glass benches. The Oculus, a grand wood, steel, and glass staircase connects the upper-level to The Loupe, the world’s first, and only, rotating glass floor below. The new glass floor experience opened up a never-before-seen downward view of the structure and the surrounding area, putting Seattle, literally, at your feet. Visit the renovation page to learn more about how the Space Needle is further enhancing its visitor experience. The Space Needle is proud to support local organizations who make a positive difference in the Puget Sound region through its community giving and engagement programs. Visit the Space Needle Foundation page to learn more about the Space Needle’s community giving and guidelines.`
    },
    {
        language: 'en',
        title: 'Sample 4',
        question: 'what are equities?',
        document: `The term capital market broadly defines the place where various entities trade different financial instruments. These venues may include the stock market, the bond market, and the currency and foreign exchange markets. Most markets are concentrated in major financial centers including New York, London, Singapore, and Hong Kong. Capital markets are composed of the suppliers and users of funds. Suppliers include households and the institutions serving them—pension funds, life insurance companies, charitable foundations, and non-financial companies—that generate cash beyond their needs for investment. Users of funds include home and motor vehicle purchasers, non-financial companies, and governments financing infrastructure investment and operating expenses.

        Capital markets are used to sell financial products such as equities and debt securities. Equities are stocks, which are ownership shares in a company. Debt securities, such as bonds, are interest-bearing IOUs.These markets are divided into two different categories: primary markets—where new equity stock and bond issues are sold to investors—and secondary markets, which trade existing securities. Capital markets are a crucial part of a functioning modern economy because they move money from the people who have it to those who need it for productive use.`
    }
];

export const generateQuestionsDemoSamples = (type: string): DemoSample[] => {
    switch (type) {
        case 'text':
            return [
                {
                    language: 'en',
                    title: 'Global Goals',
                    document: `The Sustainable Development Goals (SDGs), also known as the Global Goals, were adopted by all United Nations Member States in 2015 as a universal call to action to end poverty, protect the planet and ensure that all people enjoy peace and prosperity by 2030. The 17 SDGs are integrated—that is, they recognize that action in one area will affect outcomes in others, and that development must balance social, economic and environmental sustainability. Through the pledge to Leave No One Behind, countries have committed to fast-track progress for those furthest behind first. That is why the SDGs are designed to bring the world to several life-changing ‘zeros’, including zero poverty, hunger, AIDS and discrimination against women and girls. Everyone is needed to reach these ambitious targets. The creativity, knowhow, technology and financial resources from all of society is necessary to achieve the SDGs in every context.`
                },
                {
                    language: 'en',
                    title: 'Surface Laptop 4',
                    document:
                        'Surface Laptop 4 is, first and foremost, optimized for Microsoft experiences. It retains the iconic design, details and materials that our customers love. With our signature 3:2 PixelSense touchscreen displays in 13.5” or 15” models, customers can choose from Alcantara or metal finishes in a variety of bold colors you can’t get anywhere else, including a new Ice Blue finish. Surface Laptop 4 offers a built-in HD front-facing camera with incredible low-light capability and a studio microphone array, ensuring you’ll come through loud and clear as you connect with loved ones or participate in a crucial virtual work meeting. With the 201 PPI, high-contrast touchscreen display, and Dolby® Atmos™ Omnisonic speakers, you’ll get a cinematic experience that immerses you in your favorite movies and shows from the comfort of anywhere. Whether through the vibrant touchscreen display, large trackpad with gesture support, or its industry-leading typing experience, Surface Laptop can adjust to your preferred workflow. Surface Laptop 4 offers a choice between either the 11th Gen Intel® Core™ processors, or AMD Ryzen™ Mobile Processors with Radeon™ Graphics Microsoft Surface® Edition (8 cores); in either case, you’ll find a smooth experience that powers modern, multitasking demands. Across both options, we’ve partnered to customize silicon in ways that boost performance while offering improved battery life. On the outside, you see style and sophistication. Effortlessly lift the screen, and this product will draw you into your flow. From the office, living room, coffee shop or classroom, nothing comes close to this product’s look and feel.'
                },
                {
                    language: 'en',
                    title: 'Neil Armstrong',
                    document: `Neil Armstrong was born on Aug. 5, 1930. He was born in Ohio. He had a brother and a sister. He was in the Boy Scouts of America. Armstrong flew in an airplane when he was 6. That flight made him love airplanes. He attended Blume High School in Ohio. Armstrong went to college at Purdue University. While he was in college, he left to serve in the U.S. Navy. He flew planes during the Korean War. Then he came back to college and finished the degree he had started. He later earned a master's degree too. Before he was an astronaut, Armstrong worked for a group that studied airplanes. That group later became part of NASA. He flew several planes for them. He also helped design planes. One of the aircraft he flew was the X-15 rocket plane. This plane flew very high and very fast. It set records. Armstrong became an astronaut in 1962. He was in the second group of astronauts ever chosen. He was the commander of Gemini 8 in 1966. He flew on that mission with David Scott. They were the first astronauts to dock, or connect, two vehicles in space. Armstrong's second flight was Apollo 11 in 1969. He was the mission commander. He flew with Buzz Aldrin and Michael Collins. Armstrong and Aldrin landed on the moon in a lander named Eagle. They were the first people to land on the moon. Collins did not land. He circled the moon in the Apollo capsule. After they landed, Armstrong and Aldrin walked on the moon. Armstrong took the first step on the moon. He said, 'That's one small step for (a) man; one giant leap for mankind.' Armstrong and Aldrin spent more than two hours outside their spacecraft on the moon. They studied the surface. They collected rocks. After almost a day, they blasted off. They docked with Collins in orbit around the moon. All three then flew back to Earth.`
                }
            ];
        case 'url':
            return [
                {
                    language: 'en',
                    title: 'Surface Book 2',
                    document: `https://blogs.windows.com/devices/2017/11/16/5-reasons-youll-love-surface-book-2/`
                }
            ];
        default:
    }
};

export const namedEntityDemoSamples: DemoSample[] = [
    {
        language: 'en',
        title: 'Restaurant Review',
        document:
            "We went to Contoso foodplace located at downtown Seattle last week for a dinner party, and we adore the spot! They provide marvelous food and they have a great menu. The chief cook happens to be the owner (I think his name is John Doe) and he is super nice, coming out of the kitchen and greeted us all. We enjoyed very much dining in the place! The pasta I ordered was tender and juicy, and the place was impeccably clean. You can even pre-order from their online menu at www.contosofoodplace.com, call 112-555-0176 or send email to order@contosofoodplace.com! The only complaint I have is the food didn't come fast enough. Overall I highly recommend it!"
    },
    {
        language: 'en',
        title: 'Travel Review',
        document: 'Our tour guide took us up the Space Needle during our trip to Seattle last week.'
    },
    {
        language: 'en',
        title: 'News Headline',
        document: "Times Square in New York is packed with revelers on New Year's Eve!"
    },
    {
        language: 'en',
        title: 'Fun Fact',
        document:
            "The moon orbits the earth at a distance of 394,400 km about 30 times the diameter of the earth and its gravity influences the earth's tides."
    },
    {
        language: 'en',
        title: 'Travel Information',
        document:
            "A trip to Alaska is simply incomplete without experiencing Denali National Park where North America's tallest mountain, the beautiful 20,310-foot Denali, is located. Countless other peaks of the greater Alaska Range forms a natural barrier between the huge glaciers and silty rivers in the south, and the rolling hills and drier taiga to the north."
    }
];

export const linkedEntitiesDemoSamples: DemoSample[] = [
    {
        language: 'en',
        title: 'Restaurant Review',
        document:
            "We went to Contoso foodplace located at downtown Seattle last week for a dinner party, and we adore the spot! They provide marvelous food and they have a great menu. The chief cook happens to be the owner (I think his name is John Doe) and he is super nice, coming out of the kitchen and greeted us all. We enjoyed very much dining in the place! The pasta I ordered was tender and juicy, and the place was impeccably clean. You can even pre-order from their online menu at www.contosofoodplace.com, call 112-555-0176 or send email to order@contosofoodplace.com! The only complaint I have is the food didn't come fast enough. Overall I highly recommend it!"
    },
    {
        language: 'en',
        title: 'Travel Review',
        document: 'Our tour guide took us up the Space Needle during our trip to Seattle last week.'
    },
    {
        language: 'en',
        title: 'News Headline',
        document: "Times Square in New York is packed with revelers on New Year's Eve!"
    },
    {
        language: 'en',
        title: 'Travel Information',
        document:
            "A trip to Alaska is simply incomplete without experiencing Denali National Park where North America's tallest mountain, the beautiful 20,310-foot Denali, is located. Countless other peaks of the greater Alaska Range forms a natural barrier between the huge glaciers and silty rivers in the south, and the rolling hills and drier taiga to the north."
    }
];

export const sentimentDemoSamples: DemoSample[] = [
    {
        language: 'en',
        title: 'Product Review (short)',
        document: `I bought a size S and it fit perfectly. I found the zipper a little bit difficult to get up & down due to the side rushing. The color and material are beautiful in person. Amazingly comfortable!`
    },
    {
        language: 'en',
        title: 'Service Review (long)',
        document: `Long waits...BUT FOR GOOD REASON. Some awesome Italian food and great vibes. Contoso Bistro always has live music or events going on to keep you entertained. The food is good enough to keep me entertained though!

        The Contoso Bistro lasagna is a classic! The outdoor back patio is such a vibe, especially in the summer. Great service as well :) Love this place and will be back for more.`
    },
    {
        language: 'en',
        title: 'Customer Complaint Email',
        document: `Hello,

        My name is Mateo Gomez and I visited Contoso Restaurant with my spouse for our anniversary last Thursday. I had heard wonderful things about your food and service from my neighbors as I lived right down the street from you at 1234 Hollywood Boulevard. After a lovely stroll along the Santa Monica Pier, we decided to go for dinner at Contoso. But when we got to the restaurant, we had to wait for 15 minutes just to get on the waiting list. After an hour of waiting to get seated, we ordered two Surf and turf platters. To our absolute surprise, the food was very cold and dry. The whole experience was just awful, and I expect a refund for our meal and an apology for that day. Please issue me a refund at the earliest and call me on 123-456-7890 for any further questions.`
    },
    {
        language: 'en',
        title: 'Social Media Post',
        document: `I can describe my experience of this game in two words: "TECHNOLOGY RECHARGED!" #bestdayever`
    },
    {
        language: 'en',
        title: 'Employee survey feedback',
        document: `The cafeteria food is getting worse by the day. $15 for a plate of burger and fries does not seem subsidized.`
    }
];

export const detectLanguageDemoSamples: DemoSample[] = [
    { title: 'English', document: 'This document is in English.' },
    {
        title: 'Mixed Languages Content',
        document:
            'Hello, I would like to take a class at your University. ¿Se ofrecen clases en español? Es mi primera lengua y más fácil para escribir. Que diriez-vous des cours en français?'
    },
    { title: 'French', document: 'Bonjour. Cette phrase est en français. Merci d’utiliser notre service.' },
    { title: 'Chinese', document: '本文件为英文' }
];

export const textAnalyticsDemoSamples: DemoSample[] = [
    {
        language: 'en',
        title: 'Legal (NDA)',
        document: `On the date of August 17th, 2019, as an employee of Contoso Restaurant, I, Mateo Gomez, residing in 1234 Hollywood Boulevard Los Angeles CA, with social security number: 123-45-6789, hereby declare to fully support and promote the top priorities delegated to me at Contoso Restaurant, and vow to never disclose any information including but not limited to trade secrets, finances, delivery schedules, and recipes.

        If I, Mateo Gomez, accidentally or with intent breach the conditions set forth in this contract, understand fully that I shall receive a written termination to the following email address mateo@contosorestaurant.com as well as a fine of up to $10,000.`
    },
    {
        language: 'en',
        title: 'Email (Review)',
        document: `Dear Sir/Madam,

        My name is Mateo Gomez and I visited Contoso Restaurant with my spouse for our anniversary on Thursday of last week. Prior to my visit, I had heard great things about your food and service from my neighbors, as I actually live right down the street from you at 1234 Hollywood Boulevard. After a lovely stroll along the Santa Monica Pier, we decided to go for dinner at a place we heard so many good things about. When we got to the restaurant last Thursday, we had to wait for 15 minutes just to get on the waiting list. After an hour of waiting to be seated, we ordered two Surf and turf platters. To our absolute surprise, the food was very cold and dry. The whole experience was just quite awful, and I feel like we are owed a refund and an apology for that day. I would appreciate it if you could call me on my number 949-555-0110.
        `
    },
    {
        language: 'en',
        title: 'Banking',
        document: `Hello, my name is Mateo Gomez. I lost my Credit card on August 17th, and I would like to request its cancellation. The last purchase I made was of a Chicken parmigiana dish at Contoso Restaurant, located near the Hollywood Museum, for $40. Below is my personal information for validation:
        Profession: Accountant
        Social Security number is 123-45-6789
        Date of birth: 9-9-1989
        Phone number: 949-555-0110
        Personal address: 1234 Hollywood Boulevard Los Angeles CA
        Linked email account: mateo@contosorestaurant.com
        Swift code: CHASUS33XXX`
    },
    {
        language: 'en',
        title: 'Travel',
        document: `My wife and I decided to visit Los Angeles for the first time on August 17th, 2019. We were staying at Contoso Hotel, located on Hollywood Boulevard, so we decided to check out nearby places to visit. We bought two tickets for the Hollywood Museum to see all the popular props, costumes, and movie scenes that we grew up watching as kids. The place was quite large, so it was convenient that we wore our sneakers that day. After hours of walking and taking pictures, we decided to go for lunch at a nearby restaurant named Contoso Restaurant. While the Chicken parmigiana was quite good, the Surf and turf platter was a bit disappointing. It was a lovely trip overall and I especially appreciated being able to see the iconic Hollywood Sign while walking around the city.`
    },
    {
        language: 'en',
        title: 'Medical Report',
        document: `Mateo Gomez, 28-year-old man, suffered a car accident driving near his home on Hollywood Boulevard on August 17th, 2022, and was admitted to Contoso General Hospital in Los Angeles California at 7:45 PM. The patient showed signs of chest trauma indicating possible rib fracture and had difficulty breathing. A chest CT scan and AP X-ray were performed to determine the damage to ribs and lungs. Results showed a pseudoaneurysm of the thoracic aorta with minor fracture to the first and third right ribs. Patient was kept in the ICU where treatment was initiated. A Stent was surgically placed to stabilize the hemorrhage until the blood oxygen level reached 95 percent. The patient was discharged on September 1st, 2022, under the supervision of his caretaker Nickolaus Schulz, passport number: B12345678.`
    }
];

export const taForHealthSamples: DemoSample[] = [
    {
        language: 'en',
        title: 'Short Prescription',
        document: `Patient needs to take 100 mg of ibuprofen, and 3 mg of potassium. Also needs to take 10 mg of Zocor.
Patient needs to take 50 mg of ibuprofen, and 2 mg of Coumadin.`
    },
    {
        language: 'en',
        title: 'Patient Report',
        document: `RECORD #333582770390100 | MH | 85986313 | | 054351 | 2/14/2001 12:00:00 AM | CORONARY ARTERY DISEASE | Signed | DIS |
Admission Date: 5/22/2001 Report Status: Signed Discharge Date: 4/24/2001 ADMISSION DIAGNOSIS: CORONARY ARTERY DISEASE.
HISTORY OF PRESENT ILLNESS: The patient is a 54-year-old gentleman with a history of progressive angina over the past several months.
The patient had a cardiac catheterization in July of this year revealing total occlusion of the RCA and 50% left main disease ,
with a strong family history of coronary artery disease with a brother dying at the age of 52 from a myocardial infarction and
another brother who is status post coronary artery bypass grafting. The patient had a stress echocardiogram done on July , 2001 ,
which showed no wall motion abnormalities , but this was a difficult study due to body habitus. The patient went for six minutes with
minimal ST depressions in the anterior lateral leads , thought due to fatigue and wrist pain , his anginal equivalent. Due to the patient's
increased symptoms and family history and history left main disease with total occasional of his RCA was referred for revascularization with open heart surgery.";`
    }
];

export const summarizationDemoSamples: DemoSample[] = [
    {
        language: 'en',
        title: 'Applied AI',
        document: `At any given moment, turnaround coordinators for German airline Lufthansa CityLine have their eyes glued to monitors displaying more than half a dozen video feeds of airplanes parked at gates around the airport. The coordinators’ job is to ensure that the planes are unloaded, refueled, cleaned, restocked and reloaded so that every passenger reaches their destination safely, on time and with their luggage.

            Minutes lost here or there in the turnaround process can add up, costing airlines millions of dollars a year. As many in the industry note, airplanes only make money while in the air.

            “Think of a pitstop in a car race, and this is pretty much the same that happens in a turnaround for an aircraft,” said Philipp Grindemann, head of business development and project management for Lufthansa CityLine. “All the processes need to be on time, need to be fast, need to be lean.”

            Lufthansa CityLine is a subsidiary of Lufthansa, one of the world’s major airline groups with a network that spans the globe. Lufthansa maintains hubs in Frankfurt and Munich, Germany. Lufthansa CityLine connects passengers with destinations around Europe to and from these hubs, flying more than 300 flights per day. Ontime arrivals and departures are essential for customer satisfaction and Lufthansa’s bottom line.

            Outside of weather, delays stem from missteps during the tightly choreographed turnaround process. Like most industry players, Lufthansa CityLine relies on manual timestamps to understand when each step of the turnaround process starts and ends and uses that manual timestamp data to glean insights on where to make adjustments for faster, leaner turnarounds.

            In a pilot phase, the airline partnered with zeroG, a Lufthansa Group consulting company founded by Lufthansa Systems to accelerate the tangible impact of artificial intelligence in operational and commercial processes at airlines around the world. One example is improving turnaround management with AI.

            ZeroG’s Deep Turnaround solution leverages Azure Video Analyzer, a new offering from Microsoft that combines capabilities from Live Video Analytics and Azure Video Indexer. For Lufthansa, it generates automatic timestamps from the video feeds and issues alerts when the turnaround goes off script.

            “With that transparency of Deep Turnaround – knowing when the caterer arrives, knowing when the bridge arrives in order to deboard the aircraft – the airline can steer the process and have much leaner processes than before,” said Manuel van Esch, lead consultant for zeroG.

            For example, when a fuel truck arrives later than predicted, Deep Turnaround alerts the turnaround coordinators and other ground operations personnel. The alert kicks off a hunt for a solution that prevents a delay, such as dispatching a second fuel truck to the plane.

            Applied AI Services
            Azure Video Analyzer is among a handful of Azure Applied AI Services that Microsoft highlighted on Tuesday during Build, the company’s annual conference for developers. These services – Azure Video Analyzer, Azure Metrics Advisor, Azure Bot Service, Azure Cognitive Search, Azure Form Recognizer and Azure Immersive Reader – accelerate the development of scenario-specific AI solutions.

            Azure Applied AI Services are built on top of AI models at the core of Azure AI products and services. That includes Azure Cognitive Services, which offer customizable AI models and tools for building AI solutions that help customers extract meaning from text, integrate speech into apps and services, identify and analyze content within images and videos, and make decisions.

            Customers can also customize these services and extend them with their own custom models from Azure Machine Learning to meet the specific needs of their business.

            Customers routinely tell Microsoft that while they see the potential of AI, building solutions are harder than they anticipated, said Eric Boyd, corporate vice president of Microsoft Azure AI in Redmond, Washington.

            “The goal with Azure Applied AI Services is to provide a bit more packaging and structure to really accelerate the development of AI solutions for common business processes,” he said.

            The Azure Video Analyzer service, for example, brings together Computer Vision from Azure Cognitive Services and an automatic captioning model along with capabilities for integrating existing closed circuit video feeds and video management systems, which make it easier for businesses to build video analytics solutions.

            Microsoft created the Azure Applied AI Services category to target common business scenarios that Boyd’s Azure AI team has seen customers repeatedly build from scratch. For example, Azure Form Recognizer builds on optical character recognition, a computer vision technology that recognizes text and is key to many business solutions from reading receipts to pulling data from intake forms.`
    },
    {
        language: 'en',
        title: 'Sample 2',
        document: `More than a year and a half after Covid-19 swept across the globe, we’re all still grappling with the enormity of the changes we have experienced and the lessons we have learned in our personal and professional lives. At this juncture of the pandemic, however, I think it’s safe to say that at least two things are clear. The first is that the millions of nonprofits around the world have made a huge difference in people’s lives by stepping forward under the most difficult circumstances to provide care and relief when and where it was needed most. The second is that the dramatic acceleration in the adoption of cloud-based tools and technologies has transformed how organizations of every kind operate – including nonprofits.

        Here at Microsoft’s Tech for Social Impact, a key part of Microsoft Philanthropies, we’ve been inspired by the resilience and commitment of the nonprofits we support. In fiscal year 2021, Microsoft provided $2.5 billion in technology grants and discounts to some 295,000 nonprofits – 45,000 more than last year. And we were excited to begin to make grants available to over 75,000 eligible aged care nonprofits that continue to be at the frontlines of the response to the pandemic.

        But we also know that, in a cloud-first world, if we are going to truly help nonprofits harness the potential of technology to transform productivity and collaboration, ensure greater flexibility and scale, improve efficiency and protect the security and privacy of donor and program participant data, we need to update our grant and discount program to focus on the capabilities of the Microsoft Cloud.

        This is why, beginning in April 2022, we are shifting to a cloud-first grant program to ensure that nonprofits of every size in every part of the globe have access to the world-class cloud technology they need to accelerate the work they are doing to accomplish their missions.

        Our grants span the full portfolio of Microsoft cloud solutions, including: Microsoft 365 Business Premium, which provides industry-leading cloud, security and on-premises productivity capabilities; recently added grants for Power Platform, which enables efficient and secure app development; Dynamics 365 Sales Enterprise, which can be used for industry specific-solutions like Fundraising and Engagement in Microsoft Cloud for Nonprofit; and a $3,500 annual Microsoft Azure grant for use across the entire portfolio of Azure solutions and services.

        To help protect nonprofits against the growing threat of cyberattacks, we are announcing the AccountGuard for Nonprofits program, which will be made available to eligible organizations at no additional cost. AccountGuard for Nonprofits proactively monitors for nation-state attacks and notifies organizations if their organizational or personal email accounts have been compromised.

        We recognize that some nonprofits are not yet ready to move to the cloud. So, while we will no longer provide grants for most of our on-premises software beginning on April 4, 2022, we will continue to provide on-premises software to nonprofits at a discount of up to 75% through Microsoft partners. For nonprofits in markets where Azure is not yet available, we will continue to provide grants for Windows Server and SQL Server.

        Additionally, to ensure nonprofits can take advantage of Windows Professional, we are simplifying our licensing options with grants that make it simple to upgrade from Windows Home to Windows Professional. And, for the first time, we will provide grants for the full version of Windows Professional. All registered nonprofits will be eligible for up to 50 granted licenses with the option to purchase additional licenses at a discount through Microsoft partners.

        We are also expanding our training programs that help nonprofits get the most out of the tools and technologies we provide. Our goal is to provide training to 500,000 nonprofit staff members and volunteers this fiscal year and to reach 1 million through the Microsoft Digital Skills Center for Nonprofits, the free live Microsoft training sessions available at the Microsoft Store, and through on-demand training that will be available as part of Microsoft Viva Learning in Microsoft Teams later this fall. Beginning in early 2022, we will provide new training to help nonprofits manage technology grants from Microsoft, administer technology and lead change management through their digital transformation.

        Because many nonprofits provide computer training labs and other public access computing opportunities, we are also excited to announce new grants that cover standard public access lab licensing options for Office Standard, Windows Professional, Project, Publisher, Visio and Visual Studio. Nonprofits will be able to take advantage of this new program through TechSoup.`
    },
    {
        language: 'en',
        title: 'Sample 3',
        document: `REDMOND, Wash. and Santa Monica, Calif. – Jan. 18, 2022 – With three billion people actively playing games today, and fueled by a new generation steeped in the joys of interactive entertainment, gaming is now the largest and fastest-growing form of entertainment. Today, Microsoft Corp. (Nasdaq: MSFT) announced plans to acquire Activision Blizzard Inc. (Nasdaq: ATVI), a leader in game development and interactive entertainment content publisher. This acquisition will accelerate the growth in Microsoft’s gaming business across mobile, PC, console and cloud and will provide building blocks for the metaverse.

        Microsoft will acquire Activision Blizzard for $95.00 per share, in an all-cash transaction valued at $68.7 billion, inclusive of Activision Blizzard’s net cash. When the transaction closes, Microsoft will become the world’s third-largest gaming company by revenue, behind Tencent and Sony. The planned acquisition includes iconic franchises from the Activision, Blizzard and King studios like “Warcraft,” “Diablo,” “Overwatch,” “Call of Duty” and “Candy Crush,” in addition to global eSports activities through Major League Gaming. The company has studios around the world with nearly 10,000 employees.

        Bobby Kotick will continue to serve as CEO of Activision Blizzard, and he and his team will maintain their focus on driving efforts to further strengthen the company’s culture and accelerate business growth. Once the deal closes, the Activision Blizzard business will report to Phil Spencer, CEO, Microsoft Gaming.

        “Gaming is the most dynamic and exciting category in entertainment across all platforms today and will play a key role in the development of metaverse platforms,” said Satya Nadella, chairman and CEO, Microsoft. “We’re investing deeply in world-class content, community and the cloud to usher in a new era of gaming that puts players and creators first and makes gaming safe, inclusive and accessible to all.”

        “Players everywhere love Activision Blizzard games, and we believe the creative teams have their best work in front of them,” said Phil Spencer, CEO, Microsoft Gaming. “Together we will build a future where people can play the games they want, virtually anywhere they want.”

        “For more than 30 years our incredibly talented teams have created some of the most successful games,” said Bobby Kotick, CEO, Activision Blizzard. “The combination of Activision Blizzard’s world-class talent and extraordinary franchises with Microsoft’s technology, distribution, access to talent, ambitious vision and shared commitment to gaming and inclusion will help ensure our continued success in an increasingly competitive industry.”

        Mobile is the largest segment in gaming, with nearly 95% of all players globally enjoying games on mobile. Through great teams and great technology, Microsoft and Activision Blizzard will empower players to enjoy the most-immersive franchises, like “Halo” and “Warcraft,” virtually anywhere they want. And with games like “Candy Crush,” Activision Blizzard´s mobile business represents a significant presence and opportunity for Microsoft in this fast-growing segment.

        The acquisition also bolsters Microsoft’s Game Pass portfolio with plans to launch Activision Blizzard games into Game Pass, which has reached a new milestone of over 25 million subscribers. With Activision Blizzard’s nearly 400 million monthly active players in 190 countries and three billion-dollar franchises, this acquisition will make Game Pass one of the most compelling and diverse lineups of gaming content in the industry. Upon close, Microsoft will have 30 internal game development studios, along with additional publishing and esports production capabilities.

        The transaction is subject to customary closing conditions and completion of regulatory review and Activision Blizzard’s shareholder approval. The deal is expected to close in fiscal year 2023 and will be accretive to non-GAAP earnings per share upon close. The transaction has been approved by the boards of directors of both Microsoft and Activision Blizzard.`
    }
];

export const conversationSummarizationDemoSamples: DemoSample[] = [
    {
        language: 'en',
        title: 'Setting up WiFi Connection',
        document: `Agent: Hello, you're chatting with Rene. How may I help you?
Customer: Hi, I tried to set up wifi connection for Smart Brew 300 espresso machine, but it didn't work.
Agent: I’m sorry to hear that. Let’s see what we can do to fix this issue. Could you please try the following steps for me? First, could you push the wifi connection button, hold for 3 seconds, then let me know if the power light is slowly blinking on and off every second?
Customer: Yes, I pushed the wifi connection button, and now the power light is slowly blinking.
Agent: Great. Thank you! Now, please check in your Contoso Coffee app. Does it prompt to ask you to connect with the machine?
Customer: No. Nothing happened.
Agent: I’m very sorry to hear that. Let me see if there’s another way to fix the issue. Please hold on for a minute.`
    },
    {
        language: 'en',
        title: 'Customer complaint',
        document: `Customer: Hello
Agent: Welcome, Mr. Smith, how are you today?
Customer: Welcome, I’m fine.
Agent: With you today is John from the complaints and suggestions department, how can I help you today, Mr. Smith?
Customer: I have a big problem with the product I bought from your site this week
Agent: Moments with me Mr. Smith, I will review your request now
Customer: Ok
Agent: Your request number 538879 was on Wednesday correct Mr. Smith?
Customer: Yup
Agent: Ok, Mr. Smith can you explain to me what is the problem exactly?
Customer: I didn’t like the product and I didn’t get it as the same picture on the company website, you are liars!
Agent: I apologize very much to you Mr. Smith but on our website, we put pictures of real products only, and the proof is the positive comments and evaluations from all our customers in Saudi Arabia
Customer: I don’t believe it; I believe my eyes who see the product
Agent: Sorry, Mr. Smith I’m not accusing you of lying at all, and I will solve this problem immediately, can you please explain to me in detail how the product is different from the picture?
Customer: In the picture, the size of the product is bigger than what I received
Agent: Ok, Mr. Smith if you opened our website, you would find the sizes written in detail in the product description, if you can look at it now while you are with me on the line?
Customer: Ok
Agent: Take your time, Mr. Smith
Customer: it is Written but the product in the picture is very big than the size that is written in the description?
Agent: This is because of the angle of photography Mr. Smith because we in the company must show all the details of the product to the customer, so the pictures are a little close to the product to show all the details to the customers, and we provide the product with a specific and clear description of materials and sizes and advantages and everything so that the customer knows the product that he will buy, are you still with me Mr. Smith?
Customer: yes
Agent: Are there any other differences than the size Mr. Smith?
Customer: I don’t know how to use this product; I mean the way I used it and it didn’t work
Agent: There is no problem Mr. Smith I will send you an accurate description of the method of use and will follow up with you one of the Agents in the department of inquiries to clarify all the details and information about the product
Customer: Pretty good!
Agent: Do you have any other complaints, Mr. Smith?
Customer: I wanted to complain to the shipping representative who got me this product
Agent: Moments with me, Mr. Smith now I will determine who is responsible for your shipping operation
Customer: OK I am waiting for you
Agent: I apologize for being late, moments Mr. Smith
Customer: Ok
Agent: are you still with me Mr. Smith ?
Customer: Go ahead I am with you
Agent: Sorry again for being late Mr. Smith, now we will review with you some data about the shipment, ok?
Customer: Ok
Agent: If you can check the delivery information on the bill!
Customer: One minute
Agent: Ok
Customer: ok I am with you now
Agent: All right, the shipment was 6:00 p.m. on Wednesday, is it right?
Customer: The product was supposed to be delivered to me at 6 p.m., but I was surprised that the agent was two hours late!!!
Agent: Ok Mr. Smith, can you tell me the number of the representative on the bill?
Customer: Moments… Agent number is (95520)
Agent: OK Mr. Smith, can you tell me now about the whole problem, go-ahead
Customer: The representative was two hours behind schedule, and when I received the shipment was untidy and once humiliated, and unacceptably spoke to me!
Agent: I apologize to you very much Mr. Smith, and the necessary work will be done, and strict measures will be taken with the representative immediately, and an apology from our company to you we will offer you a 50% discount on any product you want from our site and shipping the product is free
Customer: nice thank you John
Agent: I’m always at your service, Mr. Smith, do you have any complaints or other inquiries?
Customer: no
Agent: Ok, Mr. Smith a customer service representative will contact you from the department of inquiries about the method of using the product as soon as possible, I wish you a happy day
Customer: thanks a lot
Agent: I am pleased to serve you, thank you for contacting the customer service of the company`
    }
];

export const callCenterDemoSamples: CallCenterDemoSample[] = [
    {
        audioLink: '',
        sampleType: 'text',
        title: 'Refilling pharmacy prescription',
        document: 'Customer calls pharmacy to get a refill of their prescription.',
        textInput: `"Speaker 0": Hi, thank you for calling Contoso pharmacy. Who am I speaking with today?
\n"Speaker 1": Good afternoon. My name is Mary. I'm calling about a refill for my prescribed medications. I have been trying to get ahold of someone for weeks and was told that I would get a call back regarding my situation, but it's been weeks and no one's contacted me, so I thought I'd call.
\n"Speaker 0": I understand your frustration, Mary. Can you tell me what exactly you're trying to accomplish?
\n"Speaker 1": Yes, I'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol.
\n"Speaker 0": OK, uh, certainly happy to check that for you. One moment please.
\n"Speaker 0": I see here that you were on a generic form of Lipitor, is that right?
\n"Speaker 1": Uh, yes, I was taking the generic form of Lipitor.
\n"Speaker 0": OK, uh, so I see that your doctor stopped prescribing these drugs in 2021, actually.
\n"Speaker 1": Oh really? That doesn't sound right. I don't remember him cancelling my prescription.
\n"Speaker 0": OK, uh, yeah, I'd be happy to check that for you. Uh, because sometimes there's a gap in the system and it just doesn't get reported. So let me take a look here.
\n"Speaker 0": Just a moment.
\n"Speaker 0": So I'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021.
\n"Speaker 1": Oh, huh? I mean, I'm definitely supposed to be taking something else. Uh, would you check, please?
\n"Speaker 0": OK. Uh, Yeah. According to the latest records provided by doctor's office, you're now on a different drug, metformin. Would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams?
\n"Speaker 1": yeah, yeah. Thank you so much. I'm almost out, so that'd be perfect.
\n"Speaker 0": Uh, You're very welcome, Mary. Please let us know if there's anything else we can do for you today.
\n"Speaker 1": OK. Thank you.`,
        output: {
            transcription: {
                source:
                    'https://github.com/Azure-Samples/cognitive-services-speech-sdk/raw/master/scenarios/call-center/sampledata/Call3_separated_16k_pharmacy_call.wav',
                timestamp: '2022-09-12T23:31:27Z',
                durationInTicks: 1207500000,
                duration: 'PT2M0.75S',
                combinedRecognizedPhrases: [
                    {
                        channel: 0,
                        lexical:
                            "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration mary umm can you tell me what exactly you're trying to accomplish OK uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right OK uh so i see that your doctor stopped prescribing these drugs in twenty twenty one actually OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams uh you're very welcome mary please let us know if there's anything else we can do for you today",
                        itn:
                            "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration mary umm can you tell me what exactly you're trying to accomplish OK uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right OK uh so i see that your doctor stopped prescribing these drugs in 2021 actually OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021 OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams uh you're very welcome mary please let us know if there's anything else we can do for you today",
                        maskedITN:
                            "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration mary umm can you tell me what exactly you're trying to accomplish ok uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right ok uh so i see that your doctor stopped prescribing these drugs in 2021 actually ok uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021 ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams uh you're very welcome mary please let us know if there's anything else we can do for you today",
                        display:
                            "Hi, thank you for calling Contoso pharmacy. Who am I speaking with today? I understand your frustration, Mary. Umm, can you tell me what exactly you're trying to accomplish? OK, uh, certainly happy to check that for you. One moment please. I see here that you were on a generic form of Lipitor, is that right? OK, uh, so I see that your doctor stopped prescribing these drugs in 2021, actually. OK, uh, yeah, I'd be happy to check that for you. Uh, because sometimes there's a gap in the system and it just doesn't get reported. So let me take a look here. Just a moment. So I'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021. OK. Uh, yeah. According to the latest records, uh, provided by doctor's office, you're now on a different drug, metformin. Would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams? Uh, you're very welcome, Mary. Please let us know if there's anything else we can do for you today."
                    },
                    {
                        channel: 1,
                        lexical:
                            "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect OK thank you",
                        itn:
                            "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect OK thank you",
                        maskedITN:
                            "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect ok thank you",
                        display:
                            "Good afternoon. My name is Mary. I'm calling about a refill for my prescribed medications. I have been trying to get ahold of someone for weeks and was told that I would get a call back regarding my situation, but it's been weeks and no one's contacted me, so I thought I'd call. Yes, I'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol. Uh, yes, I was taking the generic form of Lipitor. Oh really? That doesn't sound right. I don't remember him cancelling my prescription. Ohh huh, I mean, I'm definitely supposed to be taking something else. Uh, would you check please? Yeah, yeah. Thank you so much. I'm almost out, so that'd be perfect. OK. Thank you."
                    }
                ],
                recognizedPhrases: [
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT0.79S',
                        duration: 'PT2.84S',
                        offsetInTicks: 7900000.0,
                        durationInTicks: 28400000.0,
                        nBest: [
                            {
                                confidence: 0.822353,
                                lexical: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                display: 'Hi, thank you for calling Contoso pharmacy. Who am I speaking with today?',
                                sentiment: { positive: 0.7, neutral: 0.29, negative: 0.01 }
                            },
                            {
                                confidence: 0.8222805,
                                lexical: 'hi thank you for calling contessa pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling contessa pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling contessa pharmacy who am i speaking with today',
                                display: 'hi thank you for calling contessa pharmacy who am i speaking with today',
                                sentiment: { positive: 0.7, neutral: 0.29, negative: 0.01 }
                            },
                            {
                                confidence: 0.7975898,
                                lexical: 'hi thank you for calling contact pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling contact pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling contact pharmacy who am i speaking with today',
                                display: 'hi thank you for calling contact pharmacy who am i speaking with today',
                                sentiment: { positive: 0.7, neutral: 0.29, negative: 0.01 }
                            },
                            {
                                confidence: 0.8106459,
                                lexical: 'hi thank you for calling answer pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling answer pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling answer pharmacy who am i speaking with today',
                                display: 'hi thank you for calling answer pharmacy who am i speaking with today',
                                sentiment: { positive: 0.7, neutral: 0.29, negative: 0.01 }
                            },
                            {
                                confidence: 0.811178,
                                lexical: 'hi thank you for calling contest pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling contest pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling contest pharmacy who am i speaking with today',
                                display: 'hi thank you for calling contest pharmacy who am i speaking with today',
                                sentiment: { positive: 0.7, neutral: 0.29, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT4.74S',
                        duration: 'PT11.8S',
                        offsetInTicks: 47400000.0,
                        durationInTicks: 118000000.0,
                        nBest: [
                            {
                                confidence: 0.88369626,
                                lexical:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call",
                                display:
                                    "Good afternoon. My name is Mary. I'm calling about a refill for my prescribed medications. I have been trying to get ahold of someone for weeks and was told that I would get a call back regarding my situation, but it's been weeks and no one's contacted me, so I thought I'd call.",
                                sentiment: { positive: 0.46, neutral: 0.15, negative: 0.39 }
                            },
                            {
                                confidence: 0.88367,
                                lexical:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                display:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                sentiment: { positive: 0.46, neutral: 0.15, negative: 0.39 }
                            },
                            {
                                confidence: 0.8825726,
                                lexical:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                display:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                sentiment: { positive: 0.46, neutral: 0.15, negative: 0.39 }
                            },
                            {
                                confidence: 0.8695216,
                                lexical:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                display:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                sentiment: { positive: 0.46, neutral: 0.15, negative: 0.39 }
                            },
                            {
                                confidence: 0.86842424,
                                lexical:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                display:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                sentiment: { positive: 0.46, neutral: 0.15, negative: 0.39 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT17.81S',
                        duration: 'PT3.93S',
                        offsetInTicks: 178100000.0,
                        durationInTicks: 39300000.0,
                        nBest: [
                            {
                                confidence: 0.8068756,
                                lexical: "i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                itn: "i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                maskedITN:
                                    "i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                display:
                                    "I understand your frustration, Mary. Umm, can you tell me what exactly you're trying to accomplish?",
                                sentiment: { positive: 0.0, neutral: 0.02, negative: 0.98 }
                            },
                            {
                                confidence: 0.8036506,
                                lexical: "i understand your frustration mary um can you tell me what exactly you're trying to accomplish",
                                itn: "i understand your frustration mary um can you tell me what exactly you're trying to accomplish",
                                maskedITN: "i understand your frustration mary um can you tell me what exactly you're trying to accomplish",
                                display: "i understand your frustration mary UM can you tell me what exactly you're trying to accomplish",
                                sentiment: { positive: 0.0, neutral: 0.02, negative: 0.98 }
                            },
                            {
                                confidence: 0.78778875,
                                lexical:
                                    "uh i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                itn: "uh i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                maskedITN:
                                    "uh i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                display:
                                    "uh i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                sentiment: { positive: 0.0, neutral: 0.02, negative: 0.98 }
                            },
                            {
                                confidence: 0.82174206,
                                lexical: "i understand your frustration mary u m can you tell me what exactly you're trying to accomplish",
                                itn: "i understand your frustration mary u m can you tell me what exactly you're trying to accomplish",
                                maskedITN:
                                    "i understand your frustration mary u m can you tell me what exactly you're trying to accomplish",
                                display: "i understand your frustration mary U M can you tell me what exactly you're trying to accomplish",
                                sentiment: { positive: 0.0, neutral: 0.02, negative: 0.98 }
                            },
                            {
                                confidence: 0.78706896,
                                lexical: "i understand your frustration marry umm can you tell me what exactly you're trying to accomplish",
                                itn: "i understand your frustration marry umm can you tell me what exactly you're trying to accomplish",
                                maskedITN:
                                    "i understand your frustration marry umm can you tell me what exactly you're trying to accomplish",
                                display: "i understand your frustration marry umm can you tell me what exactly you're trying to accomplish",
                                sentiment: { positive: 0.0, neutral: 0.02, negative: 0.98 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT22.69S',
                        duration: 'PT4.86S',
                        offsetInTicks: 226900000.0,
                        durationInTicks: 48600000.0,
                        nBest: [
                            {
                                confidence: 0.87586707,
                                lexical:
                                    "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                display:
                                    "Yes, I'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol.",
                                sentiment: { positive: 0.01, neutral: 0.93, negative: 0.05 }
                            },
                            {
                                confidence: 0.854511,
                                lexical:
                                    "yes i'm trying to get a refill of my prescription drug so the my doctor prescribed to me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill of my prescription drug so the my doctor prescribed to me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill of my prescription drug so the my doctor prescribed to me for cholesterol",
                                display:
                                    "yes i'm trying to get a refill of my prescription drug so the my doctor prescribed to me for cholesterol",
                                sentiment: { positive: 0.01, neutral: 0.93, negative: 0.05 }
                            },
                            {
                                confidence: 0.8675472,
                                lexical:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed to me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed to me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed to me for cholesterol",
                                display:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed to me for cholesterol",
                                sentiment: { positive: 0.01, neutral: 0.93, negative: 0.05 }
                            },
                            {
                                confidence: 0.8587165,
                                lexical:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed me for cholesterol",
                                display:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed me for cholesterol",
                                sentiment: { positive: 0.01, neutral: 0.93, negative: 0.05 }
                            },
                            {
                                confidence: 0.86485285,
                                lexical:
                                    "yes i'm trying to get a refill on my prescription drugs so the my doctor prescribed to me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill on my prescription drugs so the my doctor prescribed to me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill on my prescription drugs so the my doctor prescribed to me for cholesterol",
                                display:
                                    "yes i'm trying to get a refill on my prescription drugs so the my doctor prescribed to me for cholesterol",
                                sentiment: { positive: 0.01, neutral: 0.93, negative: 0.05 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT29.18S',
                        duration: 'PT3.86S',
                        offsetInTicks: 291800000.0,
                        durationInTicks: 38600000.0,
                        nBest: [
                            {
                                confidence: 0.6916329,
                                lexical: 'OK uh certainly happy to check that for you one moment please',
                                itn: 'OK uh certainly happy to check that for you one moment please',
                                maskedITN: 'ok uh certainly happy to check that for you one moment please',
                                display: 'OK, uh, certainly happy to check that for you. One moment please.',
                                sentiment: { positive: 0.97, neutral: 0.03, negative: 0.01 }
                            },
                            {
                                confidence: 0.7651293,
                                lexical: 'ok certainly happy to check that for you one moment please',
                                itn: 'ok certainly happy to check that for you one moment please',
                                maskedITN: 'ok certainly happy to check that for you one moment please',
                                display: 'OK certainly happy to check that for you one moment please',
                                sentiment: { positive: 0.97, neutral: 0.03, negative: 0.01 }
                            },
                            {
                                confidence: 0.7085876,
                                lexical: 'ok umm certainly happy to check that for you one moment please',
                                itn: 'ok umm certainly happy to check that for you one moment please',
                                maskedITN: 'ok umm certainly happy to check that for you one moment please',
                                display: 'OK umm certainly happy to check that for you one moment please',
                                sentiment: { positive: 0.97, neutral: 0.03, negative: 0.01 }
                            },
                            {
                                confidence: 0.6555899,
                                lexical: 'ok uh certainly happen to check that for you one moment please',
                                itn: 'ok uh certainly happen to check that for you one moment please',
                                maskedITN: 'ok uh certainly happen to check that for you one moment please',
                                display: 'OK uh certainly happen to check that for you one moment please',
                                sentiment: { positive: 0.97, neutral: 0.03, negative: 0.01 }
                            },
                            {
                                confidence: 0.7069122,
                                lexical: 'ok umm so certainly happy to check that for you one moment please',
                                itn: 'ok umm so certainly happy to check that for you one moment please',
                                maskedITN: 'ok umm so certainly happy to check that for you one moment please',
                                display: 'OK umm so certainly happy to check that for you one moment please',
                                sentiment: { positive: 0.97, neutral: 0.03, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT36.46S',
                        duration: 'PT3.66S',
                        offsetInTicks: 364600000.0,
                        durationInTicks: 36600000.0,
                        nBest: [
                            {
                                confidence: 0.90179586,
                                lexical: 'i see here that you were on a generic form of lipitor is that right',
                                itn: 'i see here that you were on a generic form of lipitor is that right',
                                maskedITN: 'i see here that you were on a generic form of lipitor is that right',
                                display: 'I see here that you were on a generic form of Lipitor, is that right?',
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            },
                            {
                                confidence: 0.8980128,
                                lexical: 'i see here that you are on a generic form of lipitor is that right',
                                itn: 'i see here that you are on a generic form of lipitor is that right',
                                maskedITN: 'i see here that you are on a generic form of lipitor is that right',
                                display: 'i see here that you are on a generic form of lipitor is that right',
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            },
                            {
                                confidence: 0.8700002,
                                lexical: 'i see here that you were on a generic form of lipitor or is that right',
                                itn: 'i see here that you were on a generic form of lipitor or is that right',
                                maskedITN: 'i see here that you were on a generic form of lipitor or is that right',
                                display: 'i see here that you were on a generic form of lipitor or is that right',
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            },
                            {
                                confidence: 0.8681651,
                                lexical: 'i see here that you are on a generic form of lipitor or is that right',
                                itn: 'i see here that you are on a generic form of lipitor or is that right',
                                maskedITN: 'i see here that you are on a generic form of lipitor or is that right',
                                display: 'i see here that you are on a generic form of lipitor or is that right',
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            },
                            {
                                confidence: 0.8510702,
                                lexical: 'i see here that you were on an generic form of lipitor is that right',
                                itn: 'i see here that you were on an generic form of lipitor is that right',
                                maskedITN: 'i see here that you were on an generic form of lipitor is that right',
                                display: 'i see here that you were on an generic form of lipitor is that right',
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT41.04S',
                        duration: 'PT2.46S',
                        offsetInTicks: 410400000.0,
                        durationInTicks: 24600000.0,
                        nBest: [
                            {
                                confidence: 0.8824337,
                                lexical: 'uh yes i was taking the generic form of lipitor',
                                itn: 'uh yes i was taking the generic form of lipitor',
                                maskedITN: 'uh yes i was taking the generic form of lipitor',
                                display: 'Uh, yes, I was taking the generic form of Lipitor.',
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.8705275,
                                lexical: 'ah yes i was taking the generic form of lipitor',
                                itn: 'ah yes i was taking the generic form of lipitor',
                                maskedITN: 'ah yes i was taking the generic form of lipitor',
                                display: 'ah yes i was taking the generic form of lipitor',
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.8786717,
                                lexical: 'oh yes i was taking the generic form of lipitor',
                                itn: 'oh yes i was taking the generic form of lipitor',
                                maskedITN: 'oh yes i was taking the generic form of lipitor',
                                display: 'oh yes i was taking the generic form of lipitor',
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.80698496,
                                lexical: 'yes i was taking the generic form of lipitor',
                                itn: 'yes i was taking the generic form of lipitor',
                                maskedITN: 'yes i was taking the generic form of lipitor',
                                display: 'yes i was taking the generic form of lipitor',
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.8365211,
                                lexical: 'yeah yes i was taking the generic form of lipitor',
                                itn: 'yeah yes i was taking the generic form of lipitor',
                                maskedITN: 'yeah yes i was taking the generic form of lipitor',
                                display: 'yeah yes i was taking the generic form of lipitor',
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT44.96S',
                        duration: 'PT5.85S',
                        offsetInTicks: 449600000.0,
                        durationInTicks: 58500000.0,
                        nBest: [
                            {
                                confidence: 0.85468405,
                                lexical: 'OK uh so i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                itn: 'OK uh so i see that your doctor stopped prescribing these drugs in 2021 actually',
                                maskedITN: 'ok uh so i see that your doctor stopped prescribing these drugs in 2021 actually',
                                display: 'OK, uh, so I see that your doctor stopped prescribing these drugs in 2021, actually.',
                                sentiment: { positive: 0.01, neutral: 0.94, negative: 0.05 }
                            },
                            {
                                confidence: 0.84835666,
                                lexical: 'ok uh so i see that your doctors stopped prescribing these drugs in twenty twenty one actually',
                                itn: 'ok uh so i see that your doctors stopped prescribing these drugs in twenty twenty one actually',
                                maskedITN: 'ok uh so i see that your doctors stopped prescribing these drugs in twenty twenty one actually',
                                display: 'OK uh so i see that your doctors stopped prescribing these drugs in twenty twenty one actually',
                                sentiment: { positive: 0.01, neutral: 0.94, negative: 0.05 }
                            },
                            {
                                confidence: 0.8601926,
                                lexical: 'ok uh so i see that your doctor stop prescribing these drugs in twenty twenty one actually',
                                itn: 'ok uh so i see that your doctor stop prescribing these drugs in twenty twenty one actually',
                                maskedITN: 'ok uh so i see that your doctor stop prescribing these drugs in twenty twenty one actually',
                                display: 'OK uh so i see that your doctor stop prescribing these drugs in twenty twenty one actually',
                                sentiment: { positive: 0.01, neutral: 0.94, negative: 0.05 }
                            },
                            {
                                confidence: 0.85557616,
                                lexical:
                                    "ok uh so i see that you're a doctor stopped prescribing these drugs in twenty twenty one actually",
                                itn: "ok uh so i see that you're a doctor stopped prescribing these drugs in twenty twenty one actually",
                                maskedITN:
                                    "ok uh so i see that you're a doctor stopped prescribing these drugs in twenty twenty one actually",
                                display:
                                    "OK uh so i see that you're a doctor stopped prescribing these drugs in twenty twenty one actually",
                                sentiment: { positive: 0.01, neutral: 0.94, negative: 0.05 }
                            },
                            {
                                confidence: 0.85491055,
                                lexical: 'ok uh so i i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                itn: 'ok uh so i i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                maskedITN:
                                    'ok uh so i i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                display: 'OK uh so i i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                sentiment: { positive: 0.01, neutral: 0.94, negative: 0.05 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT51.88S',
                        duration: 'PT3.62S',
                        offsetInTicks: 518800000.0,
                        durationInTicks: 36200000.0,
                        nBest: [
                            {
                                confidence: 0.7869282,
                                lexical: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                itn: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                maskedITN: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                display: "Oh really? That doesn't sound right. I don't remember him cancelling my prescription.",
                                sentiment: { positive: 0.01, neutral: 0.21, negative: 0.78 }
                            },
                            {
                                confidence: 0.7861658,
                                lexical: "oh really that doesn't sound right i don't remember him canceling my prescription",
                                itn: "oh really that doesn't sound right i don't remember him canceling my prescription",
                                maskedITN: "oh really that doesn't sound right i don't remember him canceling my prescription",
                                display: "oh really that doesn't sound right i don't remember him canceling my prescription",
                                sentiment: { positive: 0.01, neutral: 0.21, negative: 0.78 }
                            },
                            {
                                confidence: 0.7799905,
                                lexical: "ohh really that doesn't sound right i don't remember him cancelling my prescription",
                                itn: "ohh really that doesn't sound right i don't remember him cancelling my prescription",
                                maskedITN: "ohh really that doesn't sound right i don't remember him cancelling my prescription",
                                display: "ohh really that doesn't sound right i don't remember him cancelling my prescription",
                                sentiment: { positive: 0.01, neutral: 0.21, negative: 0.78 }
                            },
                            {
                                confidence: 0.77613443,
                                lexical: "ohh really that doesn't sound right i don't remember him canceling my prescription",
                                itn: "ohh really that doesn't sound right i don't remember him canceling my prescription",
                                maskedITN: "ohh really that doesn't sound right i don't remember him canceling my prescription",
                                display: "ohh really that doesn't sound right i don't remember him canceling my prescription",
                                sentiment: { positive: 0.01, neutral: 0.21, negative: 0.78 }
                            },
                            {
                                confidence: 0.7904053,
                                lexical: "oh really that doesn't sound right i i don't remember him cancelling my prescription",
                                itn: "oh really that doesn't sound right i i don't remember him cancelling my prescription",
                                maskedITN: "oh really that doesn't sound right i i don't remember him cancelling my prescription",
                                display: "oh really that doesn't sound right i i don't remember him cancelling my prescription",
                                sentiment: { positive: 0.01, neutral: 0.21, negative: 0.78 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT56.81S',
                        duration: 'PT8.6S',
                        offsetInTicks: 568100000.0,
                        durationInTicks: 86000000.0,
                        nBest: [
                            {
                                confidence: 0.8701312,
                                lexical:
                                    "OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK, uh, yeah, I'd be happy to check that for you. Uh, because sometimes there's a gap in the system and it just doesn't get reported. So let me take a look here.",
                                sentiment: { positive: 0.01, neutral: 0.26, negative: 0.73 }
                            },
                            {
                                confidence: 0.89086306,
                                lexical:
                                    "ok uh yeah i'd be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "ok uh yeah i'd be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah i'd be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK uh yeah i'd be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                sentiment: { positive: 0.01, neutral: 0.26, negative: 0.73 }
                            },
                            {
                                confidence: 0.8658213,
                                lexical:
                                    "ok uh yeah i'll be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "ok uh yeah i'll be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah i'll be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK uh yeah i'll be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                sentiment: { positive: 0.01, neutral: 0.26, negative: 0.73 }
                            },
                            {
                                confidence: 0.8684227,
                                lexical:
                                    "ok uh yeah be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "ok uh yeah be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK uh yeah be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                sentiment: { positive: 0.01, neutral: 0.26, negative: 0.73 }
                            },
                            {
                                confidence: 0.8891035,
                                lexical:
                                    "ok uh yeah be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "ok uh yeah be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK uh yeah be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                sentiment: { positive: 0.01, neutral: 0.26, negative: 0.73 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M11.44S',
                        duration: 'PT1.29S',
                        offsetInTicks: 714400000.0,
                        durationInTicks: 12900000.0,
                        nBest: [
                            {
                                confidence: 0.7124568,
                                lexical: 'just a moment',
                                itn: 'just a moment',
                                maskedITN: 'just a moment',
                                display: 'Just a moment.',
                                sentiment: { positive: 0.06, neutral: 0.91, negative: 0.03 }
                            },
                            {
                                confidence: 0.4758362,
                                lexical: 'just the moment',
                                itn: 'just the moment',
                                maskedITN: 'just the moment',
                                display: 'just the moment',
                                sentiment: { positive: 0.06, neutral: 0.91, negative: 0.03 }
                            },
                            {
                                confidence: 0.61701584,
                                lexical: 'just one moment',
                                itn: 'just one moment',
                                maskedITN: 'just one moment',
                                display: 'just one moment',
                                sentiment: { positive: 0.06, neutral: 0.91, negative: 0.03 }
                            },
                            {
                                confidence: 0.6985655,
                                lexical: 'just a moment ago',
                                itn: 'just a moment ago',
                                maskedITN: 'just a moment ago',
                                display: 'just a moment ago',
                                sentiment: { positive: 0.06, neutral: 0.91, negative: 0.03 }
                            },
                            {
                                confidence: 0.7222866,
                                lexical: 'just a moment sir',
                                itn: 'just a moment sir',
                                maskedITN: 'just a moment sir',
                                display: 'just a moment sir',
                                sentiment: { positive: 0.06, neutral: 0.91, negative: 0.03 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M17.32S',
                        duration: 'PT6.5S',
                        offsetInTicks: 773200000.0,
                        durationInTicks: 65000000.0,
                        nBest: [
                            {
                                confidence: 0.90996224,
                                lexical:
                                    "so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn: "so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021",
                                maskedITN: "so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021",
                                display: "So I'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021.",
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.8324339,
                                lexical:
                                    "uh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn:
                                    "uh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                maskedITN:
                                    "uh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                display:
                                    "uh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.91029924,
                                lexical:
                                    "so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn:
                                    "so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                maskedITN:
                                    "so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                display:
                                    "so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.8641047,
                                lexical:
                                    "oh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn:
                                    "oh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                maskedITN:
                                    "oh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                display:
                                    "oh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.8327449,
                                lexical:
                                    "uh so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn:
                                    "uh so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                maskedITN:
                                    "uh so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                display:
                                    "uh so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT1M25.73S',
                        duration: 'PT4.6S',
                        offsetInTicks: 857300000.0,
                        durationInTicks: 46000000.0,
                        nBest: [
                            {
                                confidence: 0.7253111,
                                lexical: "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display:
                                    "Ohh huh, I mean, I'm definitely supposed to be taking something else. Uh, would you check please?",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.72176623,
                                lexical: "ohhh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohhh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohhh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display: "ohhh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.7322028,
                                lexical: "ohf huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohf huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohf huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display: "ohf huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.73403776,
                                lexical: "ohg huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohg huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohg huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display: "ohg huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.7321329,
                                lexical: "ohk huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohk huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohk huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display: "ohk huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M32.79S',
                        duration: 'PT13S',
                        offsetInTicks: 927900000.0,
                        durationInTicks: 130000000.0,
                        nBest: [
                            {
                                confidence: 0.73945653,
                                lexical:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                itn:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams",
                                display:
                                    "OK. Uh, yeah. According to the latest records, uh, provided by doctor's office, you're now on a different drug, metformin. Would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams?",
                                sentiment: { positive: 0.05, neutral: 0.93, negative: 0.02 }
                            },
                            {
                                confidence: 0.7284082,
                                lexical:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                itn:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                display:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                sentiment: { positive: 0.05, neutral: 0.93, negative: 0.02 }
                            },
                            {
                                confidence: 0.7372437,
                                lexical:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of warm in five hundred milligrams",
                                itn:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of warm in five hundred milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of warm in five hundred milligrams",
                                display:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of warm in five hundred milligrams",
                                sentiment: { positive: 0.05, neutral: 0.93, negative: 0.02 }
                            },
                            {
                                confidence: 0.73961604,
                                lexical:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                itn:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                display:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                sentiment: { positive: 0.05, neutral: 0.93, negative: 0.02 }
                            },
                            {
                                confidence: 0.7285678,
                                lexical:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                itn:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                display:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                sentiment: { positive: 0.05, neutral: 0.93, negative: 0.02 }
                            }
                        ]
                    },
                    {
                        channel: 1,
                        duration: 'PT3.23S',
                        offset: 'PT1M47.68S',
                        offsetInTicks: 1076800000.0,
                        durationInTicks: 32300000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.8699608,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                lexical: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                maskedITN: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                display: "Yeah, yeah. Thank you so much. I'm almost out, so that'd be perfect."
                            },
                            {
                                confidence: 0.74270284,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "uh yeah yeah thank you so much i'm almost out so that'd be perfect",
                                lexical: "uh yeah yeah thank you so much i'm almost out so that'd be perfect",
                                display: "uh yeah yeah thank you so much i'm almost out so that'd be perfect",
                                maskedITN: "uh yeah yeah thank you so much i'm almost out so that'd be perfect"
                            },
                            {
                                confidence: 0.8404908,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "yeah yeah thank you so much i'm almost out that'd be perfect",
                                display: "yeah yeah thank you so much i'm almost out that'd be perfect",
                                lexical: "yeah yeah thank you so much i'm almost out that'd be perfect",
                                maskedITN: "yeah yeah thank you so much i'm almost out that'd be perfect"
                            },
                            {
                                confidence: 0.8577216,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "yeah yeah thank you so much i'm almost out so that'll be perfect",
                                display: "yeah yeah thank you so much i'm almost out so that'll be perfect",
                                lexical: "yeah yeah thank you so much i'm almost out so that'll be perfect",
                                maskedITN: "yeah yeah thank you so much i'm almost out so that'll be perfect"
                            },
                            {
                                confidence: 0.73420143,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "uh yeah yeah thank you so much i'm almost out so that'll be perfect",
                                lexical: "uh yeah yeah thank you so much i'm almost out so that'll be perfect",
                                display: "uh yeah yeah thank you so much i'm almost out so that'll be perfect",
                                maskedITN: "uh yeah yeah thank you so much i'm almost out so that'll be perfect"
                            }
                        ]
                    },
                    {
                        channel: 0,
                        duration: 'PT3.9S',
                        offset: 'PT1M52.14S',
                        offsetInTicks: 1121400000.0,
                        durationInTicks: 39000000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.7017551,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                lexical: "uh you're very welcome mary please let us know if there's anything else we can do for you today",
                                itn: "uh you're very welcome mary please let us know if there's anything else we can do for you today",
                                maskedITN:
                                    "uh you're very welcome mary please let us know if there's anything else we can do for you today",
                                display:
                                    "Uh, you're very welcome, Mary. Please let us know if there's anything else we can do for you today."
                            },
                            {
                                confidence: 0.7726552,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                lexical: "you're very welcome mary please let us know if there's anything else we can do for you today",
                                itn: "you're very welcome mary please let us know if there's anything else we can do for you today",
                                maskedITN: "you're very welcome mary please let us know if there's anything else we can do for you today",
                                display: "you're very welcome mary please let us know if there's anything else we can do for you today"
                            },
                            {
                                confidence: 0.69183564,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                itn: "uh you're very welcome mary and please let us know if there's anything else we can do for you today",
                                lexical:
                                    "uh you're very welcome mary and please let us know if there's anything else we can do for you today",
                                maskedITN:
                                    "uh you're very welcome mary and please let us know if there's anything else we can do for you today",
                                display:
                                    "uh you're very welcome mary and please let us know if there's anything else we can do for you today"
                            },
                            {
                                confidence: 0.81419426,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                display: "oh you're very welcome mary please let us know if there's anything else we can do for you today",
                                lexical: "oh you're very welcome mary please let us know if there's anything else we can do for you today",
                                itn: "oh you're very welcome mary please let us know if there's anything else we can do for you today",
                                maskedITN: "oh you're very welcome mary please let us know if there's anything else we can do for you today"
                            },
                            {
                                confidence: 0.6981891,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                itn: "uh you're very welcome mary please let us know if there's anything else i can do for you today",
                                lexical: "uh you're very welcome mary please let us know if there's anything else i can do for you today",
                                display: "uh you're very welcome mary please let us know if there's anything else i can do for you today",
                                maskedITN: "uh you're very welcome mary please let us know if there's anything else i can do for you today"
                            }
                        ]
                    },
                    {
                        channel: 1,
                        duration: 'PT1.13S',
                        offset: 'PT1M57.19S',
                        offsetInTicks: 1171900000.0,
                        durationInTicks: 11300000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                itn: 'OK thank you',
                                confidence: 0.8857235,
                                lexical: 'OK thank you',
                                maskedITN: 'ok thank you',
                                display: 'OK. Thank you.',
                                sentiment: { positive: 0.38, neutral: 0.6, negative: 0.03 }
                            },
                            {
                                confidence: 0.84280515,
                                itn: 'ok so thank you',
                                display: 'OK so thank you',
                                lexical: 'ok so thank you',
                                maskedITN: 'ok so thank you',
                                sentiment: { positive: 0.38, neutral: 0.6, negative: 0.03 }
                            },
                            {
                                confidence: 0.91839796,
                                itn: 'ok thank you bye',
                                lexical: 'ok thank you bye',
                                display: 'OK thank you bye',
                                maskedITN: 'ok thank you bye',
                                sentiment: { positive: 0.38, neutral: 0.6, negative: 0.03 }
                            },
                            {
                                confidence: 0.74131435,
                                itn: 'ok and thank you',
                                lexical: 'ok and thank you',
                                display: 'OK and thank you',
                                maskedITN: 'ok and thank you',
                                sentiment: { positive: 0.38, neutral: 0.6, negative: 0.03 }
                            },
                            {
                                confidence: 0.7107127,
                                itn: 'ok ok thank you',
                                lexical: 'ok ok thank you',
                                display: 'OK OK thank you',
                                maskedITN: 'ok ok thank you',
                                sentiment: { neutral: 0.6, positive: 0.38, negative: 0.03 }
                            }
                        ]
                    }
                ]
            },
            conversationAnalyticsResults: {
                conversationSummaryResults: {
                    conversations: [
                        {
                            id: 'conversation1',
                            summaries: [
                                { aspect: 'issue', text: 'Customer called about a refill for the prescribed medications' },
                                {
                                    aspect: 'resolution',
                                    text: `Helped customer to fill the prescription\nInformed customer that the prepaid prescription was cancelled`
                                }
                            ]
                        }
                    ]
                },
                conversationPiiResults: {
                    combinedRedactedContent: [
                        {
                            channel: '0',
                            display:
                                "Hi, thank you for calling Contoso pharmacy. Who am I speaking with today? I understand your frustration, ****. Umm, can you tell me what exactly you're trying to accomplish? OK, uh, certainly happy to check that for you. One moment please. I see here that you were on a generic form of Lipitor, is that right? OK, uh, so I see that your doctor stopped prescribing these drugs in 2021, actually. OK, uh, yeah, I'd be happy to check that for you. Uh, because sometimes there's a gap in the system and it just doesn't get reported. So let me take a look here. Just a moment. So I'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021. OK. Uh, yeah. According to the latest records, uh, provided by doctor's office, you're now on a different drug, metformin. Would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams? Uh, you're very welcome, ****. Please let us know if there's anything else we can do for you today. ",
                            itn:
                                "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration **** umm can you tell me what exactly you're trying to accomplish OK uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right OK uh so i see that your doctor stopped prescribing these drugs in 2021 actually OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021 OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams uh you're very welcome **** please let us know if there's anything else we can do for you today ",
                            lexical:
                                "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration **** umm can you tell me what exactly you're trying to accomplish OK uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right OK uh so i see that your doctor stopped prescribing these drugs in twenty twenty one actually OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams uh you're very welcome **** please let us know if there's anything else we can do for you today "
                        },
                        {
                            channel: '1',
                            display:
                                "Good afternoon. My name is ****. I'm calling about a refill for my prescribed medications. I have been trying to get ahold of someone for weeks and was told that I would get a call back regarding my situation, but it's been weeks and no one's contacted me, so I thought I'd call. Yes, I'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol. Uh, yes, I was taking the generic form of Lipitor. Oh really? That doesn't sound right. I don't remember him cancelling my prescription. Ohh huh, I mean, I'm definitely supposed to be taking something else. Uh, would you check please? Yeah, yeah. Thank you so much. I'm almost out, so that'd be perfect. OK. Thank you. ",
                            itn:
                                "good afternoon my name is **** i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect OK thank you ",
                            lexical:
                                "good afternoon my name is **** i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect OK thank you "
                        }
                    ],
                    conversations: [
                        {
                            id: 'conversation1',
                            conversationItems: [
                                {
                                    id: '0',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT0.79S',
                                    redactedContent: {
                                        itn: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                        lexical: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                        text: 'Hi, thank you for calling Contoso pharmacy. Who am I speaking with today?'
                                    }
                                },
                                {
                                    id: '1',
                                    channel: '1',
                                    offset: 'PT4.74S',
                                    entities: [{ length: 4, offset: 27, text: 'mary', category: 'Name', confidenceScore: 0.96 }],
                                    redactedContent: {
                                        itn:
                                            "good afternoon my name is **** i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call",
                                        lexical:
                                            "good afternoon my name is **** i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                        text:
                                            "Good afternoon. My name is ****. I'm calling about a refill for my prescribed medications. I have been trying to get ahold of someone for weeks and was told that I would get a call back regarding my situation, but it's been weeks and no one's contacted me, so I thought I'd call."
                                    }
                                },
                                {
                                    id: '2',
                                    channel: '0',
                                    offset: 'PT17.81S',
                                    entities: [{ length: 4, offset: 31, text: 'mary', category: 'Name', confidenceScore: 0.94 }],
                                    redactedContent: {
                                        itn:
                                            "i understand your frustration **** umm can you tell me what exactly you're trying to accomplish",
                                        lexical:
                                            "i understand your frustration **** umm can you tell me what exactly you're trying to accomplish",
                                        text:
                                            "I understand your frustration, ****. Umm, can you tell me what exactly you're trying to accomplish?"
                                    }
                                },
                                {
                                    id: '3',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT22.69S',
                                    redactedContent: {
                                        itn:
                                            "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                        lexical:
                                            "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                        text:
                                            "Yes, I'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol."
                                    }
                                },
                                {
                                    id: '4',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT29.18S',
                                    redactedContent: {
                                        itn: 'OK uh certainly happy to check that for you one moment please',
                                        lexical: 'OK uh certainly happy to check that for you one moment please',
                                        text: 'OK, uh, certainly happy to check that for you. One moment please.'
                                    }
                                },
                                {
                                    id: '5',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT36.46S',
                                    redactedContent: {
                                        itn: 'i see here that you were on a generic form of lipitor is that right',
                                        lexical: 'i see here that you were on a generic form of lipitor is that right',
                                        text: 'I see here that you were on a generic form of Lipitor, is that right?'
                                    }
                                },
                                {
                                    id: '6',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT41.04S',
                                    redactedContent: {
                                        itn: 'uh yes i was taking the generic form of lipitor',
                                        lexical: 'uh yes i was taking the generic form of lipitor',
                                        text: 'Uh, yes, I was taking the generic form of Lipitor.'
                                    }
                                },
                                {
                                    id: '7',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT44.96S',
                                    redactedContent: {
                                        itn: 'OK uh so i see that your doctor stopped prescribing these drugs in 2021 actually',
                                        lexical:
                                            'OK uh so i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                        text: 'OK, uh, so I see that your doctor stopped prescribing these drugs in 2021, actually.'
                                    }
                                },
                                {
                                    id: '8',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT51.88S',
                                    redactedContent: {
                                        itn: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                        lexical: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                        text: "Oh really? That doesn't sound right. I don't remember him cancelling my prescription."
                                    }
                                },
                                {
                                    id: '9',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT56.81S',
                                    redactedContent: {
                                        itn:
                                            "OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                        lexical:
                                            "OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                        text:
                                            "OK, uh, yeah, I'd be happy to check that for you. Uh, because sometimes there's a gap in the system and it just doesn't get reported. So let me take a look here."
                                    }
                                },
                                {
                                    id: '10',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M11.44S',
                                    redactedContent: {
                                        itn: 'just a moment',
                                        lexical: 'just a moment',
                                        text: 'Just a moment.'
                                    }
                                },
                                {
                                    id: '11',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M17.32S',
                                    redactedContent: {
                                        itn: "so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021",
                                        lexical:
                                            "so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                        text:
                                            "So I'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021."
                                    }
                                },
                                {
                                    id: '12',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M25.73S',
                                    redactedContent: {
                                        itn: "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                        lexical:
                                            "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                        text:
                                            "Ohh huh, I mean, I'm definitely supposed to be taking something else. Uh, would you check please?"
                                    }
                                },
                                {
                                    id: '13',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M32.79S',
                                    redactedContent: {
                                        itn:
                                            "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams",
                                        lexical:
                                            "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                        text:
                                            "OK. Uh, yeah. According to the latest records, uh, provided by doctor's office, you're now on a different drug, metformin. Would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams?"
                                    }
                                },
                                {
                                    id: '14',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M47.68S',
                                    redactedContent: {
                                        itn: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                        lexical: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                        text: "Yeah, yeah. Thank you so much. I'm almost out, so that'd be perfect."
                                    }
                                },
                                {
                                    id: '15',
                                    channel: '0',
                                    offset: 'PT1M52.14S',
                                    entities: [{ text: 'mary', category: 'Name', offset: 25, length: 4, confidenceScore: 0.97 }],
                                    redactedContent: {
                                        itn:
                                            "uh you're very welcome **** please let us know if there's anything else we can do for you today",
                                        lexical:
                                            "uh you're very welcome **** please let us know if there's anything else we can do for you today",
                                        text:
                                            "Uh, you're very welcome, ****. Please let us know if there's anything else we can do for you today."
                                    }
                                },
                                {
                                    id: '16',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M57.19S',
                                    redactedContent: { itn: 'OK thank you', lexical: 'OK thank you', text: 'OK. Thank you.' }
                                }
                            ]
                        }
                    ]
                }
            }
        }
    },
    {
        textInput: '',
        sampleType: 'audio',
        isSingleConversation: false,
        title: 'Refilling pharmacy prescription',
        document: 'Customer calls pharmacy to get a refill of their prescription.',
        audioLink: getAssetsUrl('assets/images/audio/Call3_separated_16k_pharmacy_call.wav'),
        output: {
            transcription: {
                source:
                    'https://github.com/Azure-Samples/cognitive-services-speech-sdk/raw/master/scenarios/call-center/sampledata/Call3_separated_16k_pharmacy_call.wav',
                timestamp: '2022-09-12T23:31:27Z',
                durationInTicks: 1207500000,
                duration: 'PT2M0.75S',
                combinedRecognizedPhrases: [
                    {
                        channel: 0,
                        lexical:
                            "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration mary umm can you tell me what exactly you're trying to accomplish OK uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right OK uh so i see that your doctor stopped prescribing these drugs in twenty twenty one actually OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams uh you're very welcome mary please let us know if there's anything else we can do for you today",
                        itn:
                            "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration mary umm can you tell me what exactly you're trying to accomplish OK uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right OK uh so i see that your doctor stopped prescribing these drugs in 2021 actually OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021 OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams uh you're very welcome mary please let us know if there's anything else we can do for you today",
                        maskedITN:
                            "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration mary umm can you tell me what exactly you're trying to accomplish ok uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right ok uh so i see that your doctor stopped prescribing these drugs in 2021 actually ok uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021 ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams uh you're very welcome mary please let us know if there's anything else we can do for you today",
                        display:
                            "Hi, thank you for calling Contoso pharmacy. Who am I speaking with today? I understand your frustration, Mary. Umm, can you tell me what exactly you're trying to accomplish? OK, uh, certainly happy to check that for you. One moment please. I see here that you were on a generic form of Lipitor, is that right? OK, uh, so I see that your doctor stopped prescribing these drugs in 2021, actually. OK, uh, yeah, I'd be happy to check that for you. Uh, because sometimes there's a gap in the system and it just doesn't get reported. So let me take a look here. Just a moment. So I'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021. OK. Uh, yeah. According to the latest records, uh, provided by doctor's office, you're now on a different drug, metformin. Would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams? Uh, you're very welcome, Mary. Please let us know if there's anything else we can do for you today."
                    },
                    {
                        channel: 1,
                        lexical:
                            "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect OK thank you",
                        itn:
                            "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect OK thank you",
                        maskedITN:
                            "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect ok thank you",
                        display:
                            "Good afternoon. My name is Mary. I'm calling about a refill for my prescribed medications. I have been trying to get ahold of someone for weeks and was told that I would get a call back regarding my situation, but it's been weeks and no one's contacted me, so I thought I'd call. Yes, I'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol. Uh, yes, I was taking the generic form of Lipitor. Oh really? That doesn't sound right. I don't remember him cancelling my prescription. Ohh huh, I mean, I'm definitely supposed to be taking something else. Uh, would you check please? Yeah, yeah. Thank you so much. I'm almost out, so that'd be perfect. OK. Thank you."
                    }
                ],
                recognizedPhrases: [
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT0.79S',
                        duration: 'PT2.84S',
                        offsetInTicks: 7900000.0,
                        durationInTicks: 28400000.0,
                        nBest: [
                            {
                                confidence: 0.822353,
                                lexical: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                display: 'Hi, thank you for calling Contoso pharmacy. Who am I speaking with today?',
                                sentiment: {
                                    positive: 0.7,
                                    neutral: 0.29,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.8222805,
                                lexical: 'hi thank you for calling contessa pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling contessa pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling contessa pharmacy who am i speaking with today',
                                display: 'hi thank you for calling contessa pharmacy who am i speaking with today',
                                sentiment: {
                                    positive: 0.7,
                                    neutral: 0.29,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.7975898,
                                lexical: 'hi thank you for calling contact pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling contact pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling contact pharmacy who am i speaking with today',
                                display: 'hi thank you for calling contact pharmacy who am i speaking with today',
                                sentiment: {
                                    positive: 0.7,
                                    neutral: 0.29,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.8106459,
                                lexical: 'hi thank you for calling answer pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling answer pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling answer pharmacy who am i speaking with today',
                                display: 'hi thank you for calling answer pharmacy who am i speaking with today',
                                sentiment: {
                                    positive: 0.7,
                                    neutral: 0.29,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.811178,
                                lexical: 'hi thank you for calling contest pharmacy who am i speaking with today',
                                itn: 'hi thank you for calling contest pharmacy who am i speaking with today',
                                maskedITN: 'hi thank you for calling contest pharmacy who am i speaking with today',
                                display: 'hi thank you for calling contest pharmacy who am i speaking with today',
                                sentiment: {
                                    positive: 0.7,
                                    neutral: 0.29,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT4.74S',
                        duration: 'PT11.8S',
                        offsetInTicks: 47400000.0,
                        durationInTicks: 118000000.0,
                        nBest: [
                            {
                                confidence: 0.88369626,
                                lexical:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call",
                                display:
                                    "Good afternoon. My name is Mary. I'm calling about a refill for my prescribed medications. I have been trying to get ahold of someone for weeks and was told that I would get a call back regarding my situation, but it's been weeks and no one's contacted me, so I thought I'd call.",
                                sentiment: {
                                    positive: 0.46,
                                    neutral: 0.15,
                                    negative: 0.39
                                }
                            },
                            {
                                confidence: 0.88367,
                                lexical:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                display:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                sentiment: {
                                    positive: 0.46,
                                    neutral: 0.15,
                                    negative: 0.39
                                }
                            },
                            {
                                confidence: 0.8825726,
                                lexical:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                display:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                sentiment: {
                                    positive: 0.46,
                                    neutral: 0.15,
                                    negative: 0.39
                                }
                            },
                            {
                                confidence: 0.8695216,
                                lexical:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                display:
                                    "good afternoon my name is mary i'm calling about a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                sentiment: {
                                    positive: 0.46,
                                    neutral: 0.15,
                                    negative: 0.39
                                }
                            },
                            {
                                confidence: 0.86842424,
                                lexical:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                itn:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                maskedITN:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                display:
                                    "good afternoon my name is mary i'm calling for a refill for my prescribed medications i have been trying to get a hold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one contacted me so i thought i'd call",
                                sentiment: {
                                    positive: 0.46,
                                    neutral: 0.15,
                                    negative: 0.39
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT17.81S',
                        duration: 'PT3.93S',
                        offsetInTicks: 178100000.0,
                        durationInTicks: 39300000.0,
                        nBest: [
                            {
                                confidence: 0.8068756,
                                lexical: "i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                itn: "i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                maskedITN:
                                    "i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                display:
                                    "I understand your frustration, Mary. Umm, can you tell me what exactly you're trying to accomplish?",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.02,
                                    negative: 0.98
                                }
                            },
                            {
                                confidence: 0.8036506,
                                lexical: "i understand your frustration mary um can you tell me what exactly you're trying to accomplish",
                                itn: "i understand your frustration mary um can you tell me what exactly you're trying to accomplish",
                                maskedITN: "i understand your frustration mary um can you tell me what exactly you're trying to accomplish",
                                display: "i understand your frustration mary UM can you tell me what exactly you're trying to accomplish",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.02,
                                    negative: 0.98
                                }
                            },
                            {
                                confidence: 0.78778875,
                                lexical:
                                    "uh i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                itn: "uh i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                maskedITN:
                                    "uh i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                display:
                                    "uh i understand your frustration mary umm can you tell me what exactly you're trying to accomplish",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.02,
                                    negative: 0.98
                                }
                            },
                            {
                                confidence: 0.82174206,
                                lexical: "i understand your frustration mary u m can you tell me what exactly you're trying to accomplish",
                                itn: "i understand your frustration mary u m can you tell me what exactly you're trying to accomplish",
                                maskedITN:
                                    "i understand your frustration mary u m can you tell me what exactly you're trying to accomplish",
                                display: "i understand your frustration mary U M can you tell me what exactly you're trying to accomplish",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.02,
                                    negative: 0.98
                                }
                            },
                            {
                                confidence: 0.78706896,
                                lexical: "i understand your frustration marry umm can you tell me what exactly you're trying to accomplish",
                                itn: "i understand your frustration marry umm can you tell me what exactly you're trying to accomplish",
                                maskedITN:
                                    "i understand your frustration marry umm can you tell me what exactly you're trying to accomplish",
                                display: "i understand your frustration marry umm can you tell me what exactly you're trying to accomplish",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.02,
                                    negative: 0.98
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT22.69S',
                        duration: 'PT4.86S',
                        offsetInTicks: 226900000.0,
                        durationInTicks: 48600000.0,
                        nBest: [
                            {
                                confidence: 0.87586707,
                                lexical:
                                    "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                display:
                                    "Yes, I'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol.",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.93,
                                    negative: 0.05
                                }
                            },
                            {
                                confidence: 0.854511,
                                lexical:
                                    "yes i'm trying to get a refill of my prescription drug so the my doctor prescribed to me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill of my prescription drug so the my doctor prescribed to me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill of my prescription drug so the my doctor prescribed to me for cholesterol",
                                display:
                                    "yes i'm trying to get a refill of my prescription drug so the my doctor prescribed to me for cholesterol",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.93,
                                    negative: 0.05
                                }
                            },
                            {
                                confidence: 0.8675472,
                                lexical:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed to me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed to me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed to me for cholesterol",
                                display:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed to me for cholesterol",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.93,
                                    negative: 0.05
                                }
                            },
                            {
                                confidence: 0.8587165,
                                lexical:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed me for cholesterol",
                                display:
                                    "yes i'm trying to get a refill of my prescription drugs so the my doctor prescribed me for cholesterol",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.93,
                                    negative: 0.05
                                }
                            },
                            {
                                confidence: 0.86485285,
                                lexical:
                                    "yes i'm trying to get a refill on my prescription drugs so the my doctor prescribed to me for cholesterol",
                                itn:
                                    "yes i'm trying to get a refill on my prescription drugs so the my doctor prescribed to me for cholesterol",
                                maskedITN:
                                    "yes i'm trying to get a refill on my prescription drugs so the my doctor prescribed to me for cholesterol",
                                display:
                                    "yes i'm trying to get a refill on my prescription drugs so the my doctor prescribed to me for cholesterol",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.93,
                                    negative: 0.05
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT29.18S',
                        duration: 'PT3.86S',
                        offsetInTicks: 291800000.0,
                        durationInTicks: 38600000.0,
                        nBest: [
                            {
                                confidence: 0.6916329,
                                lexical: 'OK uh certainly happy to check that for you one moment please',
                                itn: 'OK uh certainly happy to check that for you one moment please',
                                maskedITN: 'ok uh certainly happy to check that for you one moment please',
                                display: 'OK, uh, certainly happy to check that for you. One moment please.',
                                sentiment: {
                                    positive: 0.97,
                                    neutral: 0.03,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.7651293,
                                lexical: 'ok certainly happy to check that for you one moment please',
                                itn: 'ok certainly happy to check that for you one moment please',
                                maskedITN: 'ok certainly happy to check that for you one moment please',
                                display: 'OK certainly happy to check that for you one moment please',
                                sentiment: {
                                    positive: 0.97,
                                    neutral: 0.03,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.7085876,
                                lexical: 'ok umm certainly happy to check that for you one moment please',
                                itn: 'ok umm certainly happy to check that for you one moment please',
                                maskedITN: 'ok umm certainly happy to check that for you one moment please',
                                display: 'OK umm certainly happy to check that for you one moment please',
                                sentiment: {
                                    positive: 0.97,
                                    neutral: 0.03,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.6555899,
                                lexical: 'ok uh certainly happen to check that for you one moment please',
                                itn: 'ok uh certainly happen to check that for you one moment please',
                                maskedITN: 'ok uh certainly happen to check that for you one moment please',
                                display: 'OK uh certainly happen to check that for you one moment please',
                                sentiment: {
                                    positive: 0.97,
                                    neutral: 0.03,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.7069122,
                                lexical: 'ok umm so certainly happy to check that for you one moment please',
                                itn: 'ok umm so certainly happy to check that for you one moment please',
                                maskedITN: 'ok umm so certainly happy to check that for you one moment please',
                                display: 'OK umm so certainly happy to check that for you one moment please',
                                sentiment: {
                                    positive: 0.97,
                                    neutral: 0.03,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT36.46S',
                        duration: 'PT3.66S',
                        offsetInTicks: 364600000.0,
                        durationInTicks: 36600000.0,
                        nBest: [
                            {
                                confidence: 0.90179586,
                                lexical: 'i see here that you were on a generic form of lipitor is that right',
                                itn: 'i see here that you were on a generic form of lipitor is that right',
                                maskedITN: 'i see here that you were on a generic form of lipitor is that right',
                                display: 'I see here that you were on a generic form of Lipitor, is that right?',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.96,
                                    negative: 0.03
                                }
                            },
                            {
                                confidence: 0.8980128,
                                lexical: 'i see here that you are on a generic form of lipitor is that right',
                                itn: 'i see here that you are on a generic form of lipitor is that right',
                                maskedITN: 'i see here that you are on a generic form of lipitor is that right',
                                display: 'i see here that you are on a generic form of lipitor is that right',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.96,
                                    negative: 0.03
                                }
                            },
                            {
                                confidence: 0.8700002,
                                lexical: 'i see here that you were on a generic form of lipitor or is that right',
                                itn: 'i see here that you were on a generic form of lipitor or is that right',
                                maskedITN: 'i see here that you were on a generic form of lipitor or is that right',
                                display: 'i see here that you were on a generic form of lipitor or is that right',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.96,
                                    negative: 0.03
                                }
                            },
                            {
                                confidence: 0.8681651,
                                lexical: 'i see here that you are on a generic form of lipitor or is that right',
                                itn: 'i see here that you are on a generic form of lipitor or is that right',
                                maskedITN: 'i see here that you are on a generic form of lipitor or is that right',
                                display: 'i see here that you are on a generic form of lipitor or is that right',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.96,
                                    negative: 0.03
                                }
                            },
                            {
                                confidence: 0.8510702,
                                lexical: 'i see here that you were on an generic form of lipitor is that right',
                                itn: 'i see here that you were on an generic form of lipitor is that right',
                                maskedITN: 'i see here that you were on an generic form of lipitor is that right',
                                display: 'i see here that you were on an generic form of lipitor is that right',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.96,
                                    negative: 0.03
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT41.04S',
                        duration: 'PT2.46S',
                        offsetInTicks: 410400000.0,
                        durationInTicks: 24600000.0,
                        nBest: [
                            {
                                confidence: 0.8824337,
                                lexical: 'uh yes i was taking the generic form of lipitor',
                                itn: 'uh yes i was taking the generic form of lipitor',
                                maskedITN: 'uh yes i was taking the generic form of lipitor',
                                display: 'Uh, yes, I was taking the generic form of Lipitor.',
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.8705275,
                                lexical: 'ah yes i was taking the generic form of lipitor',
                                itn: 'ah yes i was taking the generic form of lipitor',
                                maskedITN: 'ah yes i was taking the generic form of lipitor',
                                display: 'ah yes i was taking the generic form of lipitor',
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.8786717,
                                lexical: 'oh yes i was taking the generic form of lipitor',
                                itn: 'oh yes i was taking the generic form of lipitor',
                                maskedITN: 'oh yes i was taking the generic form of lipitor',
                                display: 'oh yes i was taking the generic form of lipitor',
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.80698496,
                                lexical: 'yes i was taking the generic form of lipitor',
                                itn: 'yes i was taking the generic form of lipitor',
                                maskedITN: 'yes i was taking the generic form of lipitor',
                                display: 'yes i was taking the generic form of lipitor',
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.8365211,
                                lexical: 'yeah yes i was taking the generic form of lipitor',
                                itn: 'yeah yes i was taking the generic form of lipitor',
                                maskedITN: 'yeah yes i was taking the generic form of lipitor',
                                display: 'yeah yes i was taking the generic form of lipitor',
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT44.96S',
                        duration: 'PT5.85S',
                        offsetInTicks: 449600000.0,
                        durationInTicks: 58500000.0,
                        nBest: [
                            {
                                confidence: 0.85468405,
                                lexical: 'OK uh so i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                itn: 'OK uh so i see that your doctor stopped prescribing these drugs in 2021 actually',
                                maskedITN: 'ok uh so i see that your doctor stopped prescribing these drugs in 2021 actually',
                                display: 'OK, uh, so I see that your doctor stopped prescribing these drugs in 2021, actually.',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.94,
                                    negative: 0.05
                                }
                            },
                            {
                                confidence: 0.84835666,
                                lexical: 'ok uh so i see that your doctors stopped prescribing these drugs in twenty twenty one actually',
                                itn: 'ok uh so i see that your doctors stopped prescribing these drugs in twenty twenty one actually',
                                maskedITN: 'ok uh so i see that your doctors stopped prescribing these drugs in twenty twenty one actually',
                                display: 'OK uh so i see that your doctors stopped prescribing these drugs in twenty twenty one actually',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.94,
                                    negative: 0.05
                                }
                            },
                            {
                                confidence: 0.8601926,
                                lexical: 'ok uh so i see that your doctor stop prescribing these drugs in twenty twenty one actually',
                                itn: 'ok uh so i see that your doctor stop prescribing these drugs in twenty twenty one actually',
                                maskedITN: 'ok uh so i see that your doctor stop prescribing these drugs in twenty twenty one actually',
                                display: 'OK uh so i see that your doctor stop prescribing these drugs in twenty twenty one actually',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.94,
                                    negative: 0.05
                                }
                            },
                            {
                                confidence: 0.85557616,
                                lexical:
                                    "ok uh so i see that you're a doctor stopped prescribing these drugs in twenty twenty one actually",
                                itn: "ok uh so i see that you're a doctor stopped prescribing these drugs in twenty twenty one actually",
                                maskedITN:
                                    "ok uh so i see that you're a doctor stopped prescribing these drugs in twenty twenty one actually",
                                display:
                                    "OK uh so i see that you're a doctor stopped prescribing these drugs in twenty twenty one actually",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.94,
                                    negative: 0.05
                                }
                            },
                            {
                                confidence: 0.85491055,
                                lexical: 'ok uh so i i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                itn: 'ok uh so i i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                maskedITN:
                                    'ok uh so i i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                display: 'OK uh so i i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.94,
                                    negative: 0.05
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT51.88S',
                        duration: 'PT3.62S',
                        offsetInTicks: 518800000.0,
                        durationInTicks: 36200000.0,
                        nBest: [
                            {
                                confidence: 0.7869282,
                                lexical: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                itn: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                maskedITN: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                display: "Oh really? That doesn't sound right. I don't remember him cancelling my prescription.",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.21,
                                    negative: 0.78
                                }
                            },
                            {
                                confidence: 0.7861658,
                                lexical: "oh really that doesn't sound right i don't remember him canceling my prescription",
                                itn: "oh really that doesn't sound right i don't remember him canceling my prescription",
                                maskedITN: "oh really that doesn't sound right i don't remember him canceling my prescription",
                                display: "oh really that doesn't sound right i don't remember him canceling my prescription",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.21,
                                    negative: 0.78
                                }
                            },
                            {
                                confidence: 0.7799905,
                                lexical: "ohh really that doesn't sound right i don't remember him cancelling my prescription",
                                itn: "ohh really that doesn't sound right i don't remember him cancelling my prescription",
                                maskedITN: "ohh really that doesn't sound right i don't remember him cancelling my prescription",
                                display: "ohh really that doesn't sound right i don't remember him cancelling my prescription",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.21,
                                    negative: 0.78
                                }
                            },
                            {
                                confidence: 0.77613443,
                                lexical: "ohh really that doesn't sound right i don't remember him canceling my prescription",
                                itn: "ohh really that doesn't sound right i don't remember him canceling my prescription",
                                maskedITN: "ohh really that doesn't sound right i don't remember him canceling my prescription",
                                display: "ohh really that doesn't sound right i don't remember him canceling my prescription",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.21,
                                    negative: 0.78
                                }
                            },
                            {
                                confidence: 0.7904053,
                                lexical: "oh really that doesn't sound right i i don't remember him cancelling my prescription",
                                itn: "oh really that doesn't sound right i i don't remember him cancelling my prescription",
                                maskedITN: "oh really that doesn't sound right i i don't remember him cancelling my prescription",
                                display: "oh really that doesn't sound right i i don't remember him cancelling my prescription",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.21,
                                    negative: 0.78
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT56.81S',
                        duration: 'PT8.6S',
                        offsetInTicks: 568100000.0,
                        durationInTicks: 86000000.0,
                        nBest: [
                            {
                                confidence: 0.8701312,
                                lexical:
                                    "OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK, uh, yeah, I'd be happy to check that for you. Uh, because sometimes there's a gap in the system and it just doesn't get reported. So let me take a look here.",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.26,
                                    negative: 0.73
                                }
                            },
                            {
                                confidence: 0.89086306,
                                lexical:
                                    "ok uh yeah i'd be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "ok uh yeah i'd be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah i'd be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK uh yeah i'd be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.26,
                                    negative: 0.73
                                }
                            },
                            {
                                confidence: 0.8658213,
                                lexical:
                                    "ok uh yeah i'll be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "ok uh yeah i'll be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah i'll be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK uh yeah i'll be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.26,
                                    negative: 0.73
                                }
                            },
                            {
                                confidence: 0.8684227,
                                lexical:
                                    "ok uh yeah be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "ok uh yeah be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK uh yeah be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.26,
                                    negative: 0.73
                                }
                            },
                            {
                                confidence: 0.8891035,
                                lexical:
                                    "ok uh yeah be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                itn:
                                    "ok uh yeah be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                maskedITN:
                                    "ok uh yeah be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                display:
                                    "OK uh yeah be happy to check that for you because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.26,
                                    negative: 0.73
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M11.44S',
                        duration: 'PT1.29S',
                        offsetInTicks: 714400000.0,
                        durationInTicks: 12900000.0,
                        nBest: [
                            {
                                confidence: 0.7124568,
                                lexical: 'just a moment',
                                itn: 'just a moment',
                                maskedITN: 'just a moment',
                                display: 'Just a moment.',
                                sentiment: {
                                    positive: 0.06,
                                    neutral: 0.91,
                                    negative: 0.03
                                }
                            },
                            {
                                confidence: 0.4758362,
                                lexical: 'just the moment',
                                itn: 'just the moment',
                                maskedITN: 'just the moment',
                                display: 'just the moment',
                                sentiment: {
                                    positive: 0.06,
                                    neutral: 0.91,
                                    negative: 0.03
                                }
                            },
                            {
                                confidence: 0.61701584,
                                lexical: 'just one moment',
                                itn: 'just one moment',
                                maskedITN: 'just one moment',
                                display: 'just one moment',
                                sentiment: {
                                    positive: 0.06,
                                    neutral: 0.91,
                                    negative: 0.03
                                }
                            },
                            {
                                confidence: 0.6985655,
                                lexical: 'just a moment ago',
                                itn: 'just a moment ago',
                                maskedITN: 'just a moment ago',
                                display: 'just a moment ago',
                                sentiment: {
                                    positive: 0.06,
                                    neutral: 0.91,
                                    negative: 0.03
                                }
                            },
                            {
                                confidence: 0.7222866,
                                lexical: 'just a moment sir',
                                itn: 'just a moment sir',
                                maskedITN: 'just a moment sir',
                                display: 'just a moment sir',
                                sentiment: {
                                    positive: 0.06,
                                    neutral: 0.91,
                                    negative: 0.03
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M17.32S',
                        duration: 'PT6.5S',
                        offsetInTicks: 773200000.0,
                        durationInTicks: 65000000.0,
                        nBest: [
                            {
                                confidence: 0.90996224,
                                lexical:
                                    "so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn: "so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021",
                                maskedITN: "so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021",
                                display: "So I'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021.",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.8324339,
                                lexical:
                                    "uh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn:
                                    "uh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                maskedITN:
                                    "uh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                display:
                                    "uh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.91029924,
                                lexical:
                                    "so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn:
                                    "so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                maskedITN:
                                    "so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                display:
                                    "so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.8641047,
                                lexical:
                                    "oh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn:
                                    "oh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                maskedITN:
                                    "oh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                display:
                                    "oh so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.8327449,
                                lexical:
                                    "uh so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                itn:
                                    "uh so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                maskedITN:
                                    "uh so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                display:
                                    "uh so i'm seeing here that you're a doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.99,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT1M25.73S',
                        duration: 'PT4.6S',
                        offsetInTicks: 857300000.0,
                        durationInTicks: 46000000.0,
                        nBest: [
                            {
                                confidence: 0.7253111,
                                lexical: "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display:
                                    "Ohh huh, I mean, I'm definitely supposed to be taking something else. Uh, would you check please?",
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.96,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.72176623,
                                lexical: "ohhh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohhh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohhh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display: "ohhh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.96,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.7322028,
                                lexical: "ohf huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohf huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohf huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display: "ohf huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.96,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.73403776,
                                lexical: "ohg huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohg huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohg huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display: "ohg huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.96,
                                    negative: 0.01
                                }
                            },
                            {
                                confidence: 0.7321329,
                                lexical: "ohk huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                itn: "ohk huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                maskedITN: "ohk huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                display: "ohk huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.96,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M32.79S',
                        duration: 'PT13S',
                        offsetInTicks: 927900000.0,
                        durationInTicks: 130000000.0,
                        nBest: [
                            {
                                confidence: 0.73945653,
                                lexical:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                itn:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams",
                                display:
                                    "OK. Uh, yeah. According to the latest records, uh, provided by doctor's office, you're now on a different drug, metformin. Would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams?",
                                sentiment: {
                                    positive: 0.05,
                                    neutral: 0.93,
                                    negative: 0.02
                                }
                            },
                            {
                                confidence: 0.7284082,
                                lexical:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                itn:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                display:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                sentiment: {
                                    positive: 0.05,
                                    neutral: 0.93,
                                    negative: 0.02
                                }
                            },
                            {
                                confidence: 0.7372437,
                                lexical:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of warm in five hundred milligrams",
                                itn:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of warm in five hundred milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of warm in five hundred milligrams",
                                display:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of warm in five hundred milligrams",
                                sentiment: {
                                    positive: 0.05,
                                    neutral: 0.93,
                                    negative: 0.02
                                }
                            },
                            {
                                confidence: 0.73961604,
                                lexical:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                itn:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                display:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                sentiment: {
                                    positive: 0.05,
                                    neutral: 0.93,
                                    negative: 0.02
                                }
                            },
                            {
                                confidence: 0.7285678,
                                lexical:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                itn:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                maskedITN:
                                    "ok uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                display:
                                    "OK uh yeah according to the latest records uh provided by doctor's office you're now in a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of orman five hundred milligrams",
                                sentiment: {
                                    positive: 0.05,
                                    neutral: 0.93,
                                    negative: 0.02
                                }
                            }
                        ]
                    },
                    {
                        channel: 1,
                        duration: 'PT3.23S',
                        offset: 'PT1M47.68S',
                        offsetInTicks: 1076800000.0,
                        durationInTicks: 32300000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.8699608,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                lexical: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                maskedITN: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                display: "Yeah, yeah. Thank you so much. I'm almost out, so that'd be perfect."
                            },
                            {
                                confidence: 0.74270284,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "uh yeah yeah thank you so much i'm almost out so that'd be perfect",
                                lexical: "uh yeah yeah thank you so much i'm almost out so that'd be perfect",
                                display: "uh yeah yeah thank you so much i'm almost out so that'd be perfect",
                                maskedITN: "uh yeah yeah thank you so much i'm almost out so that'd be perfect"
                            },
                            {
                                confidence: 0.8404908,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "yeah yeah thank you so much i'm almost out that'd be perfect",
                                display: "yeah yeah thank you so much i'm almost out that'd be perfect",
                                lexical: "yeah yeah thank you so much i'm almost out that'd be perfect",
                                maskedITN: "yeah yeah thank you so much i'm almost out that'd be perfect"
                            },
                            {
                                confidence: 0.8577216,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "yeah yeah thank you so much i'm almost out so that'll be perfect",
                                display: "yeah yeah thank you so much i'm almost out so that'll be perfect",
                                lexical: "yeah yeah thank you so much i'm almost out so that'll be perfect",
                                maskedITN: "yeah yeah thank you so much i'm almost out so that'll be perfect"
                            },
                            {
                                confidence: 0.73420143,
                                sentiment: { positive: 0.93, neutral: 0.06, negative: 0.01 },
                                itn: "uh yeah yeah thank you so much i'm almost out so that'll be perfect",
                                lexical: "uh yeah yeah thank you so much i'm almost out so that'll be perfect",
                                display: "uh yeah yeah thank you so much i'm almost out so that'll be perfect",
                                maskedITN: "uh yeah yeah thank you so much i'm almost out so that'll be perfect"
                            }
                        ]
                    },
                    {
                        channel: 0,
                        duration: 'PT3.9S',
                        offset: 'PT1M52.14S',
                        offsetInTicks: 1121400000.0,
                        durationInTicks: 39000000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.7017551,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                lexical: "uh you're very welcome mary please let us know if there's anything else we can do for you today",
                                itn: "uh you're very welcome mary please let us know if there's anything else we can do for you today",
                                maskedITN:
                                    "uh you're very welcome mary please let us know if there's anything else we can do for you today",
                                display:
                                    "Uh, you're very welcome, Mary. Please let us know if there's anything else we can do for you today."
                            },
                            {
                                confidence: 0.7726552,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                lexical: "you're very welcome mary please let us know if there's anything else we can do for you today",
                                itn: "you're very welcome mary please let us know if there's anything else we can do for you today",
                                maskedITN: "you're very welcome mary please let us know if there's anything else we can do for you today",
                                display: "you're very welcome mary please let us know if there's anything else we can do for you today"
                            },
                            {
                                confidence: 0.69183564,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                itn: "uh you're very welcome mary and please let us know if there's anything else we can do for you today",
                                lexical:
                                    "uh you're very welcome mary and please let us know if there's anything else we can do for you today",
                                maskedITN:
                                    "uh you're very welcome mary and please let us know if there's anything else we can do for you today",
                                display:
                                    "uh you're very welcome mary and please let us know if there's anything else we can do for you today"
                            },
                            {
                                confidence: 0.81419426,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                display: "oh you're very welcome mary please let us know if there's anything else we can do for you today",
                                lexical: "oh you're very welcome mary please let us know if there's anything else we can do for you today",
                                itn: "oh you're very welcome mary please let us know if there's anything else we can do for you today",
                                maskedITN: "oh you're very welcome mary please let us know if there's anything else we can do for you today"
                            },
                            {
                                confidence: 0.6981891,
                                sentiment: { positive: 0.26, neutral: 0.73, negative: 0.01 },
                                itn: "uh you're very welcome mary please let us know if there's anything else i can do for you today",
                                lexical: "uh you're very welcome mary please let us know if there's anything else i can do for you today",
                                display: "uh you're very welcome mary please let us know if there's anything else i can do for you today",
                                maskedITN: "uh you're very welcome mary please let us know if there's anything else i can do for you today"
                            }
                        ]
                    },
                    {
                        channel: 1,
                        duration: 'PT1.13S',
                        offset: 'PT1M57.19S',
                        offsetInTicks: 1171900000.0,
                        durationInTicks: 11300000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                itn: 'OK thank you',
                                confidence: 0.8857235,
                                lexical: 'OK thank you',
                                maskedITN: 'ok thank you',
                                display: 'OK. Thank you.',
                                sentiment: { positive: 0.38, neutral: 0.6, negative: 0.03 }
                            },
                            {
                                confidence: 0.84280515,
                                itn: 'ok so thank you',
                                display: 'OK so thank you',
                                lexical: 'ok so thank you',
                                maskedITN: 'ok so thank you',
                                sentiment: { positive: 0.38, neutral: 0.6, negative: 0.03 }
                            },
                            {
                                confidence: 0.91839796,
                                itn: 'ok thank you bye',
                                lexical: 'ok thank you bye',
                                display: 'OK thank you bye',
                                maskedITN: 'ok thank you bye',
                                sentiment: { positive: 0.38, neutral: 0.6, negative: 0.03 }
                            },
                            {
                                confidence: 0.74131435,
                                itn: 'ok and thank you',
                                lexical: 'ok and thank you',
                                display: 'OK and thank you',
                                maskedITN: 'ok and thank you',
                                sentiment: { positive: 0.38, neutral: 0.6, negative: 0.03 }
                            },
                            {
                                confidence: 0.7107127,
                                itn: 'ok ok thank you',
                                lexical: 'ok ok thank you',
                                display: 'OK OK thank you',
                                maskedITN: 'ok ok thank you',
                                sentiment: { neutral: 0.6, positive: 0.38, negative: 0.03 }
                            }
                        ]
                    }
                ]
            },
            conversationAnalyticsResults: {
                conversationSummaryResults: {
                    conversations: [
                        {
                            id: 'conversation1',
                            summaries: [
                                { aspect: 'issue', text: 'Customer called about a refill for the prescribed medications' },
                                {
                                    aspect: 'resolution',
                                    text: `Helped customer to fill the prescription\nInformed customer that the prepaid prescription was cancelled`
                                }
                            ]
                        }
                    ]
                },
                conversationPiiResults: {
                    combinedRedactedContent: [
                        {
                            channel: '0',
                            display:
                                "Hi, thank you for calling Contoso pharmacy. Who am I speaking with today? I understand your frustration, ****. Umm, can you tell me what exactly you're trying to accomplish? OK, uh, certainly happy to check that for you. One moment please. I see here that you were on a generic form of Lipitor, is that right? OK, uh, so I see that your doctor stopped prescribing these drugs in 2021, actually. OK, uh, yeah, I'd be happy to check that for you. Uh, because sometimes there's a gap in the system and it just doesn't get reported. So let me take a look here. Just a moment. So I'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021. OK. Uh, yeah. According to the latest records, uh, provided by doctor's office, you're now on a different drug, metformin. Would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams? Uh, you're very welcome, ****. Please let us know if there's anything else we can do for you today. ",
                            itn:
                                "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration **** umm can you tell me what exactly you're trying to accomplish OK uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right OK uh so i see that your doctor stopped prescribing these drugs in 2021 actually OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021 OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams uh you're very welcome **** please let us know if there's anything else we can do for you today ",
                            lexical:
                                "hi thank you for calling contoso pharmacy who am i speaking with today i understand your frustration **** umm can you tell me what exactly you're trying to accomplish OK uh certainly happy to check that for you one moment please i see here that you were on a generic form of lipitor is that right OK uh so i see that your doctor stopped prescribing these drugs in twenty twenty one actually OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here just a moment so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams uh you're very welcome **** please let us know if there's anything else we can do for you today "
                        },
                        {
                            channel: '1',
                            display:
                                "Good afternoon. My name is ****. I'm calling about a refill for my prescribed medications. I have been trying to get ahold of someone for weeks and was told that I would get a call back regarding my situation, but it's been weeks and no one's contacted me, so I thought I'd call. Yes, I'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol. Uh, yes, I was taking the generic form of Lipitor. Oh really? That doesn't sound right. I don't remember him cancelling my prescription. Ohh huh, I mean, I'm definitely supposed to be taking something else. Uh, would you check please? Yeah, yeah. Thank you so much. I'm almost out, so that'd be perfect. OK. Thank you. ",
                            itn:
                                "good afternoon my name is **** i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect OK thank you ",
                            lexical:
                                "good afternoon my name is **** i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol uh yes i was taking the generic form of lipitor oh really that doesn't sound right i don't remember him cancelling my prescription ohh huh i mean i'm definitely supposed to be taking something else uh would you check please yeah yeah thank you so much i'm almost out so that'd be perfect OK thank you "
                        }
                    ],
                    conversations: [
                        {
                            id: 'conversation1',
                            conversationItems: [
                                {
                                    id: '0',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT0.79S',
                                    redactedContent: {
                                        itn: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                        lexical: 'hi thank you for calling contoso pharmacy who am i speaking with today',
                                        text: 'Hi, thank you for calling Contoso pharmacy. Who am I speaking with today?'
                                    }
                                },
                                {
                                    id: '1',
                                    channel: '1',
                                    offset: 'PT4.74S',
                                    entities: [{ length: 4, offset: 27, text: 'mary', category: 'Name', confidenceScore: 0.96 }],
                                    redactedContent: {
                                        itn:
                                            "good afternoon my name is **** i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one's contacted me so i thought i'd call",
                                        lexical:
                                            "good afternoon my name is **** i'm calling about a refill for my prescribed medications i have been trying to get ahold of someone for weeks and was told that i would get a call back regarding my situation but it's been weeks and no one 's contacted me so i thought i'd call",
                                        text:
                                            "Good afternoon. My name is ****. I'm calling about a refill for my prescribed medications. I have been trying to get ahold of someone for weeks and was told that I would get a call back regarding my situation, but it's been weeks and no one's contacted me, so I thought I'd call."
                                    }
                                },
                                {
                                    id: '2',
                                    channel: '0',
                                    offset: 'PT17.81S',
                                    entities: [{ length: 4, offset: 31, text: 'mary', category: 'Name', confidenceScore: 0.94 }],
                                    redactedContent: {
                                        itn:
                                            "i understand your frustration **** umm can you tell me what exactly you're trying to accomplish",
                                        lexical:
                                            "i understand your frustration **** umm can you tell me what exactly you're trying to accomplish",
                                        text:
                                            "I understand your frustration, ****. Umm, can you tell me what exactly you're trying to accomplish?"
                                    }
                                },
                                {
                                    id: '3',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT22.69S',
                                    redactedContent: {
                                        itn:
                                            "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                        lexical:
                                            "yes i'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol",
                                        text:
                                            "Yes, I'm trying to get a refill of my prescription drugs that the my doctor prescribed to me for cholesterol."
                                    }
                                },
                                {
                                    id: '4',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT29.18S',
                                    redactedContent: {
                                        itn: 'OK uh certainly happy to check that for you one moment please',
                                        lexical: 'OK uh certainly happy to check that for you one moment please',
                                        text: 'OK, uh, certainly happy to check that for you. One moment please.'
                                    }
                                },
                                {
                                    id: '5',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT36.46S',
                                    redactedContent: {
                                        itn: 'i see here that you were on a generic form of lipitor is that right',
                                        lexical: 'i see here that you were on a generic form of lipitor is that right',
                                        text: 'I see here that you were on a generic form of Lipitor, is that right?'
                                    }
                                },
                                {
                                    id: '6',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT41.04S',
                                    redactedContent: {
                                        itn: 'uh yes i was taking the generic form of lipitor',
                                        lexical: 'uh yes i was taking the generic form of lipitor',
                                        text: 'Uh, yes, I was taking the generic form of Lipitor.'
                                    }
                                },
                                {
                                    id: '7',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT44.96S',
                                    redactedContent: {
                                        itn: 'OK uh so i see that your doctor stopped prescribing these drugs in 2021 actually',
                                        lexical:
                                            'OK uh so i see that your doctor stopped prescribing these drugs in twenty twenty one actually',
                                        text: 'OK, uh, so I see that your doctor stopped prescribing these drugs in 2021, actually.'
                                    }
                                },
                                {
                                    id: '8',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT51.88S',
                                    redactedContent: {
                                        itn: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                        lexical: "oh really that doesn't sound right i don't remember him cancelling my prescription",
                                        text: "Oh really? That doesn't sound right. I don't remember him cancelling my prescription."
                                    }
                                },
                                {
                                    id: '9',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT56.81S',
                                    redactedContent: {
                                        itn:
                                            "OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                        lexical:
                                            "OK uh yeah i'd be happy to check that for you uh because sometimes there's a gap in the system and it just doesn't get reported so let me take a look here",
                                        text:
                                            "OK, uh, yeah, I'd be happy to check that for you. Uh, because sometimes there's a gap in the system and it just doesn't get reported. So let me take a look here."
                                    }
                                },
                                {
                                    id: '10',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M11.44S',
                                    redactedContent: {
                                        itn: 'just a moment',
                                        lexical: 'just a moment',
                                        text: 'Just a moment.'
                                    }
                                },
                                {
                                    id: '11',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M17.32S',
                                    redactedContent: {
                                        itn: "so i'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021",
                                        lexical:
                                            "so i'm seeing here that your doctor had these drugs prescribed to you from twenty twelve through twenty twenty one",
                                        text:
                                            "So I'm seeing here that your doctor had these drugs prescribed to you from 2012 through 2021."
                                    }
                                },
                                {
                                    id: '12',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M25.73S',
                                    redactedContent: {
                                        itn: "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                        lexical:
                                            "ohh huh i mean i'm definitely supposed to be taking something else uh would you check please",
                                        text:
                                            "Ohh huh, I mean, I'm definitely supposed to be taking something else. Uh, would you check please?"
                                    }
                                },
                                {
                                    id: '13',
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M32.79S',
                                    redactedContent: {
                                        itn:
                                            "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams",
                                        lexical:
                                            "OK uh yeah according to the latest records uh provided by doctor's office you're now on a different drug metformin would you like us to go ahead and fill that prescription for you for pick up a bit of form in five hundred milligrams",
                                        text:
                                            "OK. Uh, yeah. According to the latest records, uh, provided by doctor's office, you're now on a different drug, metformin. Would you like us to go ahead and fill that prescription for you for pick up a bit of form in 500 milligrams?"
                                    }
                                },
                                {
                                    id: '14',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M47.68S',
                                    redactedContent: {
                                        itn: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                        lexical: "yeah yeah thank you so much i'm almost out so that'd be perfect",
                                        text: "Yeah, yeah. Thank you so much. I'm almost out, so that'd be perfect."
                                    }
                                },
                                {
                                    id: '15',
                                    channel: '0',
                                    offset: 'PT1M52.14S',
                                    entities: [{ text: 'mary', category: 'Name', offset: 25, length: 4, confidenceScore: 0.97 }],
                                    redactedContent: {
                                        itn:
                                            "uh you're very welcome **** please let us know if there's anything else we can do for you today",
                                        lexical:
                                            "uh you're very welcome **** please let us know if there's anything else we can do for you today",
                                        text:
                                            "Uh, you're very welcome, ****. Please let us know if there's anything else we can do for you today."
                                    }
                                },
                                {
                                    id: '16',
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M57.19S',
                                    redactedContent: { itn: 'OK thank you', lexical: 'OK thank you', text: 'OK. Thank you.' }
                                }
                            ]
                        }
                    ]
                }
            }
        }
    },
    {
        textInput: '',
        sampleType: 'audio',
        isSingleConversation: false,
        title: 'Signing up for Insurance',
        document: 'An insurance agent helps a customer sign up for health insurance.',
        audioLink: getAssetsUrl('assets/images/audio/Call1_separated_16k_health_insurance.wav'),
        output: {
            transcription: {
                source: '',
                timestamp: '2022-09-29T03:17:06Z',
                durationInTicks: 1850800000,
                duration: 'PT3M5.08S',
                combinedRecognizedPhrases: [
                    {
                        channel: 0,
                        lexical:
                            "hello thank you for calling contoso who am i speaking with today hi mary uh are you calling because you need health insurance great if you can answer a few questions uh we can get you signed up in the jiffy so what's your full name got it and what's the best callback number in case we get disconnected yeah that'll be fine got it so to confirm it's two three four five five four nine three one two excellent uh let's get some additional information from your app for your application uh do you have a job OK so then you have a social security number as well OK and what is your social security number please sorry what was that a two five or a two two five you cut out for a bit thank you so much and could i have your email address please great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application absolutely um if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso uh yes of course so the default is a digital membership card but we can send you a physical card if you prefer uh yeah absolutely i've made a note on your file you're very welcome thank you for calling contoso and have a great day",
                        itn:
                            "hello thank you for calling contoso who am i speaking with today hi mary uh are you calling because you need health insurance great if you can answer a few questions uh we can get you signed up in the jiffy so what's your full name got it and what's the best callback number in case we get disconnected yeah that'll be fine got it so to confirm it's 2345549312 excellent uh let's get some additional information from your app for your application uh do you have a job OK so then you have a social security number as well OK and what is your social security number please sorry what was that A-25 or A225 you cut out for a bit thank you so much and could i have your email address please great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about 24 hours or so to confirm your application absolutely um if you need anything else please give us a call at 1-800-555-5564 ext 123 thank you very much for calling contoso uh yes of course so the default is a digital membership card but we can send you a physical card if you prefer uh yeah absolutely i've made a note on your file you're very welcome thank you for calling contoso and have a great day",
                        maskedITN:
                            "hello thank you for calling contoso who am i speaking with today hi mary uh are you calling because you need health insurance great if you can answer a few questions uh we can get you signed up in the jiffy so what's your full name got it and what's the best callback number in case we get disconnected yeah that'll be fine got it so to confirm it's 2345549312 excellent uh let's get some additional information from your app for your application uh do you have a job ok so then you have a social security number as well ok and what is your social security number please sorry what was that a-25 or a225 you cut out for a bit thank you so much and could i have your e-mail address please great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about 24 hours or so to confirm your application absolutely um if you need anything else please give us a call at 1-800-555-5564 ext 123 thank you very much for calling contoso uh yes of course so the default is a digital membership card but we can send you a physical card if you prefer uh yeah absolutely i've made a note on your file you're very welcome thank you for calling contoso and have a great day",
                        display:
                            "Hello, thank you for calling Contoso. Who am I speaking with today? Hi, Mary. Uh, are you calling because you need health insurance? Great. If you can answer a few questions, uh, we can get you signed up in the Jiffy. So what's your full name? Got it. And what's the best callback number in case we get disconnected? Yeah, that'll be fine. Got it. So to confirm, it's 2345549312. Excellent. Uh, let's get some additional information from your app. For your application, uh, do you have a job? OK, so then you have a Social Security number as well? OK. And what is your Social Security number please? Sorry, what was that A-25 or A225 you cut out for a bit? Thank you so much and could I have your e-mail address please? Great. Uh, that is the last question. So let me take your information and I'll be able to get you signed up right away. Thank you for calling Contoso, and I'll be able to get you signed up immediately. One of our agents will call you back in about 24 hours or so to confirm your application. Absolutely. Um, if you need anything else, please give us a call at 1-800-555-5564, ext 123. Thank you very much for calling Contoso. Uh, yes, of course. So the default is a digital membership card, but we can send you a physical card if you prefer. Uh, yeah. Absolutely. I've made a note on your file. You're very welcome. Thank you for calling Contoso and have a great day."
                    },
                    {
                        channel: 1,
                        lexical:
                            "hi my name is mary rondo i'm trying to enroll myself with contoso yes yeah i'm calling to sign up for insurance OK so mary beth rondo last name is R like romeo O like ocean and like nancy D D like dog and O like ocean again rondo i only have a cell phone so i can give you that sure so it's two three four five five four and then nine three one two yep that's right uh yes i am self employed i guess i do uh sure so it's four one two uh two five six seven eight nine uh it's double two so four one two then another two then five yeah it's mary rondo at gmail dot com so my first and last name at gmail dot com no periods no dashes that's great thank you actually uh i have one more question i'm curious what i'd be getting a physical card as proof of coverage uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address uh so it's two six six zero unit A on maple avenue southeast lansing and then zip code is four eight eight two three awesome thanks so much",
                        itn:
                            "hi my name is mary rondo i'm trying to enroll myself with contoso yes yeah i'm calling to sign up for insurance OK so mary beth rondo last name is R like romeo O like ocean and like nancy DD like dog and O like ocean again rondo i only have a cell phone so i can give you that sure so it's 234554 and then 9312 yep that's right uh yes i am self employed i guess i do uh sure so it's 412 uh 256789 uh it's 22 so 412 then another two then five yeah it's mary rondo@gmail.com so myfirstandlastname@gmail.com no periods no dashes that's great thank you actually uh i have one more question i'm curious what i'd be getting a physical card as proof of coverage uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address uh so it's 2660 unit A on maple ave SE lansing and then zip code is 48823 awesome thanks so much",
                        maskedITN:
                            "hi my name is mary rondo i'm trying to enroll myself with contoso yes yeah i'm calling to sign up for insurance ok so mary beth rondo last name is r like romeo o like ocean and like nancy dd like dog and o like ocean again rondo i only have a cell phone so i can give you that sure so it's 234554 and then 9312 yep that's right uh yes i am self-employed i guess i do uh sure so it's 412 uh 256789 uh it's 22 so 412 then another two then five yeah it's mary rondo@gmail.com so myfirstandlastname@gmail.com no periods no dashes that's great thank you actually uh i have one more question i'm curious what i'd be getting a physical card as proof of coverage uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address uh so it's 2660 unit a on maple ave se lansing and then zip code is 48823 awesome thanks so much",
                        display:
                            "Hi, my name is Mary Rondo. I'm trying to enroll myself with Contoso. Yes. Yeah, I'm calling to sign up for insurance. OK. So Mary Beth Rondo last name is R like Romeo, O like ocean and like Nancy, DD like dog and O like ocean again. Rondo. I only have a cell phone, so I can give you that. Sure. So it's 234554 and then 9312. Yep, that's right. Uh, yes, I am self-employed. I guess I do. Uh, sure. So it's 412, uh 256789. Uh, it's 22, so. 412, then another two, then five. Yeah, it's Mary rondo@gmail.com, so myfirstandlastname@gmail.com. No periods, no dashes. That's great. Thank you. Actually, uh, I have one more question. I'm curious what I'd be getting a physical card as proof of coverage. Uh, yes. Could you please mail it to me when it's ready? I'd like to have it shipped to you for my address. Uh, so it's 2660, unit A on Maple Ave, SE Lansing and then ZIP code is 48823. Awesome. Thanks so much."
                    }
                ],
                recognizedPhrases: [
                    {
                        channel: 0,
                        offset: 'PT0.77S',
                        duration: 'PT2.77S',
                        offsetInTicks: 7700000.0,
                        durationInTicks: 27700000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.76984274,
                                sentiment: { positive: 0.78, neutral: 0.21, negative: 0.01 },
                                itn: 'hello thank you for calling contoso who am i speaking with today',
                                lexical: 'hello thank you for calling contoso who am i speaking with today',
                                maskedITN: 'hello thank you for calling contoso who am i speaking with today',
                                display: 'Hello, thank you for calling Contoso. Who am I speaking with today?'
                            },
                            {
                                confidence: 0.7656101,
                                sentiment: { positive: 0.78, neutral: 0.21, negative: 0.01 },
                                itn: 'hello thank you for calling contessa who am i speaking with today',
                                lexical: 'hello thank you for calling contessa who am i speaking with today',
                                display: 'hello thank you for calling contessa who am i speaking with today',
                                maskedITN: 'hello thank you for calling contessa who am i speaking with today'
                            },
                            {
                                confidence: 0.7601531,
                                sentiment: { positive: 0.78, neutral: 0.21, negative: 0.01 },
                                itn: 'hello thank you for calling contoso a who am i speaking with today',
                                display: 'hello thank you for calling contoso a who am i speaking with today',
                                lexical: 'hello thank you for calling contoso a who am i speaking with today',
                                maskedITN: 'hello thank you for calling contoso a who am i speaking with today'
                            },
                            {
                                confidence: 0.7523945,
                                sentiment: { positive: 0.78, neutral: 0.21, negative: 0.01 },
                                itn: 'hello hello thank you for calling contoso who am i speaking with today',
                                display: 'hello hello thank you for calling contoso who am i speaking with today',
                                lexical: 'hello hello thank you for calling contoso who am i speaking with today',
                                maskedITN: 'hello hello thank you for calling contoso who am i speaking with today'
                            },
                            {
                                confidence: 0.76494396,
                                sentiment: { positive: 0.78, neutral: 0.21, negative: 0.01 },
                                itn: 'hello thank you for calling contoso or who am i speaking with today',
                                display: 'hello thank you for calling contoso or who am i speaking with today',
                                lexical: 'hello thank you for calling contoso or who am i speaking with today',
                                maskedITN: 'hello thank you for calling contoso or who am i speaking with today'
                            }
                        ]
                    },
                    {
                        channel: 1,
                        offset: 'PT4.55S',
                        duration: 'PT3.47S',
                        offsetInTicks: 45500000.0,
                        durationInTicks: 34700000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.6813098,
                                sentiment: { positive: 0.03, neutral: 0.95, negative: 0.02 },
                                itn: "hi my name is mary rondo i'm trying to enroll myself with contoso",
                                lexical: "hi my name is mary rondo i'm trying to enroll myself with contoso",
                                maskedITN: "hi my name is mary rondo i'm trying to enroll myself with contoso",
                                display: "Hi, my name is Mary Rondo. I'm trying to enroll myself with Contoso."
                            },
                            {
                                confidence: 0.7516502,
                                sentiment: { positive: 0.03, neutral: 0.95, negative: 0.02 },
                                itn: "hi my name is mary rondo i'm trying to enroll myself with conscious so",
                                lexical: "hi my name is mary rondo i'm trying to enroll myself with conscious so",
                                display: "hi my name is mary rondo i'm trying to enroll myself with conscious so",
                                maskedITN: "hi my name is mary rondo i'm trying to enroll myself with conscious so"
                            },
                            {
                                confidence: 0.7510633,
                                sentiment: { positive: 0.03, neutral: 0.95, negative: 0.02 },
                                itn: "hi my name is mary rondo i'm trying to enroll myself with conscious though",
                                display: "hi my name is mary rondo i'm trying to enroll myself with conscious though",
                                lexical: "hi my name is mary rondo i'm trying to enroll myself with conscious though",
                                maskedITN: "hi my name is mary rondo i'm trying to enroll myself with conscious though"
                            },
                            {
                                confidence: 0.73764974,
                                sentiment: { positive: 0.03, neutral: 0.95, negative: 0.02 },
                                itn: "hi my name is mary rondo i'm trying to enroll myself with conscious thought",
                                lexical: "hi my name is mary rondo i'm trying to enroll myself with conscious thought",
                                display: "hi my name is mary rondo i'm trying to enroll myself with conscious thought",
                                maskedITN: "hi my name is mary rondo i'm trying to enroll myself with conscious thought"
                            },
                            {
                                confidence: 0.8002269,
                                sentiment: { positive: 0.03, neutral: 0.95, negative: 0.02 },
                                itn: "hi my name is mary rondo i'm trying to enroll myself with conscious",
                                display: "hi my name is mary rondo i'm trying to enroll myself with conscious",
                                lexical: "hi my name is mary rondo i'm trying to enroll myself with conscious",
                                maskedITN: "hi my name is mary rondo i'm trying to enroll myself with conscious"
                            }
                        ]
                    },
                    {
                        channel: 0,
                        offset: 'PT9.53S',
                        duration: 'PT2.64S',
                        offsetInTicks: 95300000.0,
                        durationInTicks: 26400000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.7773647,
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 },
                                itn: 'hi mary uh are you calling because you need health insurance',
                                lexical: 'hi mary uh are you calling because you need health insurance',
                                maskedITN: 'hi mary uh are you calling because you need health insurance',
                                display: 'Hi, Mary. Uh, are you calling because you need health insurance?'
                            },
                            {
                                confidence: 0.7510856,
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 },
                                itn: 'hi mary uh are you calling because we need health insurance',
                                display: 'hi mary uh are you calling because we need health insurance',
                                lexical: 'hi mary uh are you calling because we need health insurance',
                                maskedITN: 'hi mary uh are you calling because we need health insurance'
                            },
                            {
                                confidence: 0.76939183,
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 },
                                itn: 'hi mary are you calling because you need health insurance',
                                lexical: 'hi mary are you calling because you need health insurance',
                                display: 'hi mary are you calling because you need health insurance',
                                maskedITN: 'hi mary are you calling because you need health insurance'
                            },
                            {
                                confidence: 0.83506125,
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 },
                                itn: "hi mary 's uh are you calling because you need health insurance",
                                lexical: "hi mary 's uh are you calling because you need health insurance",
                                display: "hi mary 's uh are you calling because you need health insurance",
                                maskedITN: "hi mary 's uh are you calling because you need health insurance"
                            },
                            {
                                confidence: 0.8188598,
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 },
                                itn: 'hi mary i uh are you calling because you need health insurance',
                                lexical: 'hi mary i uh are you calling because you need health insurance',
                                display: 'hi mary i uh are you calling because you need health insurance',
                                maskedITN: 'hi mary i uh are you calling because you need health insurance'
                            }
                        ]
                    },
                    {
                        channel: 1,
                        offset: 'PT13.09S',
                        duration: 'PT2.12S',
                        offsetInTicks: 130900000.0,
                        durationInTicks: 21200000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.75269204,
                                itn: "yes yeah i'm calling to sign up for insurance",
                                lexical: "yes yeah i'm calling to sign up for insurance",
                                maskedITN: "yes yeah i'm calling to sign up for insurance",
                                display: "Yes. Yeah, I'm calling to sign up for insurance.",
                                sentiment: { positive: 0.04, neutral: 0.93, negative: 0.02 }
                            },
                            {
                                confidence: 0.7903627,
                                itn: "yeah yeah i'm calling to sign up for insurance",
                                lexical: "yeah yeah i'm calling to sign up for insurance",
                                display: "yeah yeah i'm calling to sign up for insurance",
                                maskedITN: "yeah yeah i'm calling to sign up for insurance",
                                sentiment: { positive: 0.04, neutral: 0.93, negative: 0.02 }
                            },
                            {
                                confidence: 0.78854865,
                                itn: "yes yes yeah i'm calling to sign up for insurance",
                                lexical: "yes yes yeah i'm calling to sign up for insurance",
                                display: "yes yes yeah i'm calling to sign up for insurance",
                                sentiment: { positive: 0.04, neutral: 0.93, negative: 0.02 },
                                maskedITN: "yes yes yeah i'm calling to sign up for insurance"
                            },
                            {
                                confidence: 0.7006665,
                                itn: "yes yes i'm calling to sign up for insurance",
                                lexical: "yes yes i'm calling to sign up for insurance",
                                display: "yes yes i'm calling to sign up for insurance",
                                maskedITN: "yes yes i'm calling to sign up for insurance",
                                sentiment: { positive: 0.04, neutral: 0.93, negative: 0.02 }
                            },
                            {
                                confidence: 0.739863,
                                itn: "yes yeah i i'm calling to sign up for insurance",
                                lexical: "yes yeah i i'm calling to sign up for insurance",
                                display: "yes yeah i i'm calling to sign up for insurance",
                                maskedITN: "yes yeah i i'm calling to sign up for insurance",
                                sentiment: { positive: 0.04, neutral: 0.93, negative: 0.02 }
                            }
                        ]
                    },
                    {
                        channel: 0,
                        offset: 'PT15.99S',
                        duration: 'PT3.3S',
                        offsetInTicks: 159900000.0,
                        durationInTicks: 33000000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.746321,
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 },
                                itn: 'great if you can answer a few questions uh we can get you signed up in the jiffy',
                                lexical: 'great if you can answer a few questions uh we can get you signed up in the jiffy',
                                maskedITN: 'great if you can answer a few questions uh we can get you signed up in the jiffy',
                                display: 'Great. If you can answer a few questions, uh, we can get you signed up in the Jiffy.'
                            },
                            {
                                confidence: 0.7340917,
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 },
                                itn: 'great if you can answer a few questions that we can get you signed up in the jiffy',
                                lexical: 'great if you can answer a few questions that we can get you signed up in the jiffy',
                                display: 'great if you can answer a few questions that we can get you signed up in the jiffy',
                                maskedITN: 'great if you can answer a few questions that we can get you signed up in the jiffy'
                            },
                            {
                                confidence: 0.7646631,
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 },
                                itn: 'great if you can answer a few questions uh we can get you signed up in the jeffy',
                                lexical: 'great if you can answer a few questions uh we can get you signed up in the jeffy',
                                display: 'great if you can answer a few questions uh we can get you signed up in the jeffy',
                                maskedITN: 'great if you can answer a few questions uh we can get you signed up in the jeffy'
                            },
                            {
                                confidence: 0.75243384,
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 },
                                itn: 'great if you can answer a few questions that we can get you signed up in the jeffy',
                                lexical: 'great if you can answer a few questions that we can get you signed up in the jeffy',
                                display: 'great if you can answer a few questions that we can get you signed up in the jeffy',
                                maskedITN: 'great if you can answer a few questions that we can get you signed up in the jeffy'
                            },
                            {
                                confidence: 0.82345766,
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 },
                                itn: 'great uh if you can answer a few questions that we can get you signed up in the jeffy',
                                lexical: 'great uh if you can answer a few questions that we can get you signed up in the jeffy',
                                display: 'great uh if you can answer a few questions that we can get you signed up in the jeffy',
                                maskedITN: 'great uh if you can answer a few questions that we can get you signed up in the jeffy'
                            }
                        ]
                    },
                    {
                        channel: 1,
                        offset: 'PT20.22S',
                        duration: 'PT0.42S',
                        offsetInTicks: 202200000.0,
                        durationInTicks: 4200000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                itn: 'OK',
                                lexical: 'OK',
                                display: 'OK.',
                                maskedITN: 'ok',
                                confidence: 0.8893775,
                                sentiment: { positive: 0.09, neutral: 0.85, negative: 0.05 }
                            },
                            {
                                itn: 'o k',
                                lexical: 'o k',
                                display: 'O K',
                                maskedITN: 'o k',
                                confidence: 0.6799939,
                                sentiment: { positive: 0.09, neutral: 0.85, negative: 0.05 }
                            },
                            {
                                itn: 'okay',
                                lexical: 'okay',
                                display: 'okay',
                                maskedITN: 'okay',
                                confidence: 0.42450958,
                                sentiment: { positive: 0.09, neutral: 0.85, negative: 0.05 }
                            },
                            {
                                itn: 'ok ok',
                                lexical: 'ok ok',
                                display: 'OK OK',
                                maskedITN: 'ok ok',
                                confidence: 0.71163285,
                                sentiment: { positive: 0.09, neutral: 0.85, negative: 0.05 }
                            },
                            {
                                itn: 'oh ok',
                                lexical: 'oh ok',
                                display: 'oh OK',
                                maskedITN: 'oh ok',
                                confidence: 0.8482245,
                                sentiment: { positive: 0.09, neutral: 0.85, negative: 0.05 }
                            }
                        ]
                    },
                    {
                        channel: 0,
                        offset: 'PT21.75S',
                        duration: 'PT1.24S',
                        offsetInTicks: 217500000.0,
                        durationInTicks: 12400000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.5325069,
                                itn: "so what's your full name",
                                lexical: "so what's your full name",
                                display: "So what's your full name?",
                                maskedITN: "so what's your full name",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.7008285,
                                itn: "so uh what's your full name",
                                lexical: "so uh what's your full name",
                                display: "so uh what's your full name",
                                maskedITN: "so uh what's your full name",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.66311777,
                                itn: "and so uh what's your full name",
                                lexical: "and so uh what's your full name",
                                display: "and so uh what's your full name",
                                maskedITN: "and so uh what's your full name",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.65085024,
                                itn: "umm so uh what's your full name",
                                lexical: "umm so uh what's your full name",
                                display: "umm so uh what's your full name",
                                maskedITN: "umm so uh what's your full name",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.4886156,
                                itn: "umm so what's your full name",
                                lexical: "umm so what's your full name",
                                display: "umm so what's your full name",
                                maskedITN: "umm so what's your full name",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        channel: 1,
                        offset: 'PT24.66S',
                        duration: 'PT8.87S',
                        offsetInTicks: 246600000.0,
                        durationInTicks: 88700000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.7745253,
                                sentiment: { positive: 0.02, neutral: 0.95, negative: 0.03 },
                                lexical:
                                    'so mary beth rondo last name is R like romeo O like ocean and like nancy D D like dog and O like ocean again',
                                itn:
                                    'so mary beth rondo last name is R like romeo O like ocean and like nancy DD like dog and O like ocean again',
                                maskedITN:
                                    'so mary beth rondo last name is r like romeo o like ocean and like nancy dd like dog and o like ocean again',
                                display:
                                    'So Mary Beth Rondo last name is R like Romeo, O like ocean and like Nancy, DD like dog and O like ocean again.'
                            },
                            {
                                confidence: 0.7700618,
                                sentiment: { positive: 0.02, neutral: 0.95, negative: 0.03 },
                                lexical:
                                    'uh some mary beth rondo last name is r like romeo o like ocean and like nancy d d like dog and o like ocean again',
                                itn:
                                    'uh some mary beth rondo last name is r like romeo o like ocean and like nancy d d like dog and o like ocean again',
                                maskedITN:
                                    'uh some mary beth rondo last name is r like romeo o like ocean and like nancy d d like dog and o like ocean again',
                                display:
                                    'uh some mary beth rondo last name is R like romeo O like ocean and like nancy D D like dog and O like ocean again'
                            },
                            {
                                confidence: 0.77185184,
                                sentiment: { positive: 0.02, neutral: 0.95, negative: 0.03 },
                                lexical:
                                    'uh so mary beth rondo last name is r like romeo o like ocean and like nancy d d like dog and o like ocean again',
                                itn:
                                    'uh so mary beth rondo last name is r like romeo o like ocean and like nancy d d like dog and o like ocean again',
                                maskedITN:
                                    'uh so mary beth rondo last name is r like romeo o like ocean and like nancy d d like dog and o like ocean again',
                                display:
                                    'uh so mary beth rondo last name is R like romeo O like ocean and like nancy D D like dog and O like ocean again'
                            },
                            {
                                confidence: 0.79277545,
                                sentiment: { positive: 0.02, neutral: 0.95, negative: 0.03 },
                                lexical:
                                    'so mary beth rondo last name is r like romeo o like ocean and like nancy d d d like dog and o like ocean again',
                                itn:
                                    'so mary beth rondo last name is r like romeo o like ocean and like nancy d d d like dog and o like ocean again',
                                maskedITN:
                                    'so mary beth rondo last name is r like romeo o like ocean and like nancy d d d like dog and o like ocean again',
                                display:
                                    'so mary beth rondo last name is R like romeo O like ocean and like nancy D D D like dog and O like ocean again'
                            },
                            {
                                confidence: 0.78972787,
                                sentiment: { positive: 0.02, neutral: 0.95, negative: 0.03 },
                                lexical:
                                    'uh so mary beth rondo last name is r like romeo o like ocean and like nancy d d d like dog and o like ocean again',
                                itn:
                                    'uh so mary beth rondo last name is r like romeo o like ocean and like nancy d d d like dog and o like ocean again',
                                maskedITN:
                                    'uh so mary beth rondo last name is r like romeo o like ocean and like nancy d d d like dog and o like ocean again',
                                display:
                                    'uh so mary beth rondo last name is R like romeo O like ocean and like nancy D D D like dog and O like ocean again'
                            }
                        ]
                    },
                    {
                        channel: 1,
                        offset: 'PT34.37S',
                        duration: 'PT0.51S',
                        offsetInTicks: 343700000.0,
                        durationInTicks: 5100000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                itn: 'rondo',
                                lexical: 'rondo',
                                display: 'Rondo.',
                                maskedITN: 'rondo',
                                confidence: 0.6418219,
                                sentiment: { positive: 0.1, neutral: 0.83, negative: 0.07 }
                            },
                            {
                                itn: 'rando',
                                lexical: 'rando',
                                display: 'rando',
                                maskedITN: 'rando',
                                confidence: 0.4320536,
                                sentiment: { positive: 0.1, neutral: 0.83, negative: 0.07 }
                            },
                            {
                                itn: 'radio',
                                lexical: 'radio',
                                display: 'radio',
                                maskedITN: 'radio',
                                confidence: 0.31428802,
                                sentiment: { positive: 0.1, neutral: 0.83, negative: 0.07 }
                            },
                            {
                                itn: 'rado',
                                lexical: 'rado',
                                display: 'rado',
                                maskedITN: 'rado',
                                confidence: 0.20097324,
                                sentiment: { positive: 0.1, neutral: 0.83, negative: 0.07 }
                            },
                            {
                                itn: 'random',
                                lexical: 'random',
                                display: 'random',
                                maskedITN: 'random',
                                confidence: 0.29478824,
                                sentiment: { positive: 0.1, neutral: 0.83, negative: 0.07 }
                            }
                        ]
                    },
                    {
                        channel: 0,
                        offset: 'PT35.95S',
                        duration: 'PT4.11S',
                        offsetInTicks: 359500000.0,
                        durationInTicks: 41100000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.84468174,
                                sentiment: { positive: 0.58, neutral: 0.34, negative: 0.07 },
                                itn: "got it and what's the best callback number in case we get disconnected",
                                lexical: "got it and what's the best callback number in case we get disconnected",
                                maskedITN: "got it and what's the best callback number in case we get disconnected",
                                display: "Got it. And what's the best callback number in case we get disconnected?"
                            },
                            {
                                confidence: 0.788748,
                                sentiment: { positive: 0.58, neutral: 0.34, negative: 0.07 },
                                itn: "got it uh and what's the best callback number in case we get disconnected",
                                lexical: "got it uh and what's the best callback number in case we get disconnected",
                                display: "got it uh and what's the best callback number in case we get disconnected",
                                maskedITN: "got it uh and what's the best callback number in case we get disconnected"
                            },
                            {
                                confidence: 0.8745935,
                                sentiment: { positive: 0.58, neutral: 0.34, negative: 0.07 },
                                itn: "got it and and what's the best callback number in case we get disconnected",
                                lexical: "got it and and what's the best callback number in case we get disconnected",
                                display: "got it and and what's the best callback number in case we get disconnected",
                                maskedITN: "got it and and what's the best callback number in case we get disconnected"
                            },
                            {
                                confidence: 0.8210522,
                                sentiment: { positive: 0.58, neutral: 0.34, negative: 0.07 },
                                itn: "got it uh and and what's the best callback number in case we get disconnected",
                                lexical: "got it uh and and what's the best callback number in case we get disconnected",
                                display: "got it uh and and what's the best callback number in case we get disconnected",
                                maskedITN: "got it uh and and what's the best callback number in case we get disconnected"
                            },
                            {
                                confidence: 0.83174586,
                                sentiment: { positive: 0.58, neutral: 0.34, negative: 0.07 },
                                itn: "got it and what's the best call back number in case we get disconnected",
                                lexical: "got it and what's the best call back number in case we get disconnected",
                                display: "got it and what's the best call back number in case we get disconnected",
                                maskedITN: "got it and what's the best call back number in case we get disconnected"
                            }
                        ]
                    },
                    {
                        channel: 1,
                        offset: 'PT41.65S',
                        duration: 'PT1.78S',
                        offsetInTicks: 416500000.0,
                        durationInTicks: 17800000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.8159973,
                                itn: 'i only have a cell phone so i can give you that',
                                lexical: 'i only have a cell phone so i can give you that',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 },
                                maskedITN: 'i only have a cell phone so i can give you that',
                                display: 'I only have a cell phone, so I can give you that.'
                            },
                            {
                                confidence: 0.80530506,
                                itn: 'um i only have a cell phone so i can give you that',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 },
                                lexical: 'um i only have a cell phone so i can give you that',
                                display: 'um i only have a cell phone so i can give you that',
                                maskedITN: 'um i only have a cell phone so i can give you that'
                            },
                            {
                                confidence: 0.8158581,
                                itn: 'uh i only have a cell phone so i can give you that',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 },
                                lexical: 'uh i only have a cell phone so i can give you that',
                                display: 'uh i only have a cell phone so i can give you that',
                                maskedITN: 'uh i only have a cell phone so i can give you that'
                            },
                            {
                                confidence: 0.82257944,
                                itn: 'umm i only have a cell phone so i can give you that',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 },
                                lexical: 'umm i only have a cell phone so i can give you that',
                                display: 'umm i only have a cell phone so i can give you that',
                                maskedITN: 'umm i only have a cell phone so i can give you that'
                            },
                            {
                                confidence: 0.79422706,
                                itn: 'uhm i only have a cell phone so i can give you that',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 },
                                lexical: 'uhm i only have a cell phone so i can give you that',
                                display: 'uhm i only have a cell phone so i can give you that',
                                maskedITN: 'uhm i only have a cell phone so i can give you that'
                            }
                        ]
                    },
                    {
                        channel: 0,
                        offset: 'PT44.19S',
                        duration: 'PT0.89S',
                        offsetInTicks: 441900000.0,
                        durationInTicks: 8900000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.7716483,
                                itn: "yeah that'll be fine",
                                lexical: "yeah that'll be fine",
                                maskedITN: "yeah that'll be fine",
                                display: "Yeah, that'll be fine.",
                                sentiment: { positive: 0.39, neutral: 0.59, negative: 0.02 }
                            },
                            {
                                confidence: 0.7332887,
                                itn: "yep that'll be fine",
                                lexical: "yep that'll be fine",
                                display: "yep that'll be fine",
                                maskedITN: "yep that'll be fine",
                                sentiment: { positive: 0.39, neutral: 0.59, negative: 0.02 }
                            },
                            {
                                confidence: 0.6477021,
                                itn: "yeah they'll be fine",
                                lexical: "yeah they'll be fine",
                                display: "yeah they'll be fine",
                                maskedITN: "yeah they'll be fine",
                                sentiment: { positive: 0.39, neutral: 0.59, negative: 0.02 }
                            },
                            {
                                confidence: 0.8122522,
                                itn: 'yeah that would be fine',
                                lexical: 'yeah that would be fine',
                                display: 'yeah that would be fine',
                                maskedITN: 'yeah that would be fine',
                                sentiment: { positive: 0.39, neutral: 0.59, negative: 0.02 }
                            },
                            {
                                confidence: 0.7900637,
                                itn: 'yeah that will be fine',
                                lexical: 'yeah that will be fine',
                                display: 'yeah that will be fine',
                                maskedITN: 'yeah that will be fine',
                                sentiment: { positive: 0.39, neutral: 0.59, negative: 0.02 }
                            }
                        ]
                    },
                    {
                        channel: 1,
                        offset: 'PT46.2S',
                        duration: 'PT7.84S',
                        offsetInTicks: 462000000.0,
                        durationInTicks: 78400000.0,
                        recognitionStatus: 'Success',
                        nBest: [
                            {
                                confidence: 0.8422551,
                                lexical: "sure so it's two three four five five four and then nine three one two",
                                itn: "sure so it's 234554 and then 9312",
                                maskedITN: "sure so it's 234554 and then 9312",
                                display: "Sure. So it's 234554 and then 9312.",
                                sentiment: { positive: 0.03, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.85136986,
                                lexical: "sure so it's two two three four five five four and then nine three one two",
                                itn: "sure so it's two two three four five five four and then nine three one two",
                                maskedITN: "sure so it's two two three four five five four and then nine three one two",
                                display: "sure so it's two two three four five five four and then nine three one two",
                                sentiment: { positive: 0.03, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.80159706,
                                lexical: 'sure so is two three four five five four and then nine three one two',
                                itn: 'sure so is two three four five five four and then nine three one two',
                                maskedITN: 'sure so is two three four five five four and then nine three one two',
                                display: 'sure so is two three four five five four and then nine three one two',
                                sentiment: { positive: 0.03, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.83449954,
                                lexical: "sure so it's two three four five five four and then nine nine three one two",
                                itn: "sure so it's two three four five five four and then nine nine three one two",
                                maskedITN: "sure so it's two three four five five four and then nine nine three one two",
                                display: "sure so it's two three four five five four and then nine nine three one two",
                                sentiment: { positive: 0.03, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.8151639,
                                lexical: "sure so it's two three four five five four and then nine three one two two",
                                itn: "sure so it's two three four five five four and then nine three one two two",
                                maskedITN: "sure so it's two three four five five four and then nine three one two two",
                                display: "sure so it's two three four five five four and then nine three one two two",
                                sentiment: { positive: 0.03, neutral: 0.96, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT54.78S',
                        duration: 'PT4.24S',
                        offsetInTicks: 547800000.0,
                        durationInTicks: 42400000.0,
                        nBest: [
                            {
                                confidence: 0.9235197,
                                lexical: "got it so to confirm it's two three four five five four nine three one two",
                                itn: "got it so to confirm it's 2345549312",
                                maskedITN: "got it so to confirm it's 2345549312",
                                display: "Got it. So to confirm, it's 2345549312.",
                                sentiment: { positive: 0.1, neutral: 0.88, negative: 0.02 }
                            },
                            {
                                confidence: 0.9262772,
                                lexical: "i got it so to confirm it's two three four five five four nine three one two",
                                itn: "i got it so to confirm it's two three four five five four nine three one two",
                                maskedITN: "i got it so to confirm it's two three four five five four nine three one two",
                                display: "i got it so to confirm it's two three four five five four nine three one two",
                                sentiment: { positive: 0.1, neutral: 0.88, negative: 0.02 }
                            },
                            {
                                confidence: 0.9151753,
                                lexical: "got it so to confirm it it's two three four five five four nine three one two",
                                itn: "got it so to confirm it it's two three four five five four nine three one two",
                                maskedITN: "got it so to confirm it it's two three four five five four nine three one two",
                                display: "got it so to confirm it it's two three four five five four nine three one two",
                                sentiment: { positive: 0.1, neutral: 0.88, negative: 0.02 }
                            },
                            {
                                confidence: 0.9045776,
                                lexical: "got it so to confirm it's two three four five five four four nine three one two",
                                itn: "got it so to confirm it's two three four five five four four nine three one two",
                                maskedITN: "got it so to confirm it's two three four five five four four nine three one two",
                                display: "got it so to confirm it's two three four five five four four nine three one two",
                                sentiment: { positive: 0.1, neutral: 0.88, negative: 0.02 }
                            },
                            {
                                confidence: 0.9182549,
                                lexical: "got it so to confirm if it's two three four five five four nine three one two",
                                itn: "got it so to confirm if it's two three four five five four nine three one two",
                                maskedITN: "got it so to confirm if it's two three four five five four nine three one two",
                                display: "got it so to confirm if it's two three four five five four nine three one two",
                                sentiment: { positive: 0.1, neutral: 0.88, negative: 0.02 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT1M0.05S',
                        duration: 'PT0.79S',
                        offsetInTicks: 600500000.0,
                        durationInTicks: 7900000.0,
                        nBest: [
                            {
                                confidence: 0.84030807,
                                lexical: "yep that's right",
                                itn: "yep that's right",
                                maskedITN: "yep that's right",
                                display: "Yep, that's right.",
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 }
                            },
                            {
                                confidence: 0.81599486,
                                lexical: "yeah that's right",
                                itn: "yeah that's right",
                                maskedITN: "yeah that's right",
                                display: "yeah that's right",
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 }
                            },
                            {
                                confidence: 0.6863356,
                                lexical: "yup that's right",
                                itn: "yup that's right",
                                maskedITN: "yup that's right",
                                display: "yup that's right",
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 }
                            },
                            {
                                confidence: 0.65776026,
                                lexical: "yes that's right",
                                itn: "yes that's right",
                                maskedITN: "yes that's right",
                                display: "yes that's right",
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 }
                            },
                            {
                                confidence: 0.8554243,
                                lexical: "that's right",
                                itn: "that's right",
                                maskedITN: "that's right",
                                display: "that's right",
                                sentiment: { positive: 0.05, neutral: 0.94, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M1.66S',
                        duration: 'PT5.57S',
                        offsetInTicks: 616600000.0,
                        durationInTicks: 55700000.0,
                        nBest: [
                            {
                                confidence: 0.75885,
                                lexical:
                                    "excellent uh let's get some additional information from your app for your application uh do you have a job",
                                itn:
                                    "excellent uh let's get some additional information from your app for your application uh do you have a job",
                                maskedITN:
                                    "excellent uh let's get some additional information from your app for your application uh do you have a job",
                                display:
                                    "Excellent. Uh, let's get some additional information from your app. For your application, uh, do you have a job?",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.75421417,
                                lexical:
                                    "excellent uh let's get some additional information from your apps for your application uh do you have a job",
                                itn:
                                    "excellent uh let's get some additional information from your apps for your application uh do you have a job",
                                maskedITN:
                                    "excellent uh let's get some additional information from your apps for your application uh do you have a job",
                                display:
                                    "excellent uh let's get some additional information from your apps for your application uh do you have a job",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.7432639,
                                lexical:
                                    "excellent uh let's get some additional information from your apple for your application uh do you have a job",
                                itn:
                                    "excellent uh let's get some additional information from your apple for your application uh do you have a job",
                                maskedITN:
                                    "excellent uh let's get some additional information from your apple for your application uh do you have a job",
                                display:
                                    "excellent uh let's get some additional information from your apple for your application uh do you have a job",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.739411,
                                lexical:
                                    "excellent uh let's get some additional information from your for your application uh do you have a job",
                                itn:
                                    "excellent uh let's get some additional information from your for your application uh do you have a job",
                                maskedITN:
                                    "excellent uh let's get some additional information from your for your application uh do you have a job",
                                display:
                                    "excellent uh let's get some additional information from your for your application uh do you have a job",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.7367319,
                                lexical:
                                    "excellent uh let's get some additional information from your from your application uh do you have a job",
                                itn:
                                    "excellent uh let's get some additional information from your from your application uh do you have a job",
                                maskedITN:
                                    "excellent uh let's get some additional information from your from your application uh do you have a job",
                                display:
                                    "excellent uh let's get some additional information from your from your application uh do you have a job",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT1M8.47S',
                        duration: 'PT1.55S',
                        offsetInTicks: 684700000.0,
                        durationInTicks: 15500000.0,
                        nBest: [
                            {
                                confidence: 0.8601435,
                                lexical: 'uh yes i am self employed',
                                itn: 'uh yes i am self employed',
                                maskedITN: 'uh yes i am self-employed',
                                display: 'Uh, yes, I am self-employed.',
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.01 }
                            },
                            {
                                confidence: 0.8704847,
                                lexical: 'uh yes i am self-employed',
                                itn: 'uh yes i am self-employed',
                                maskedITN: 'uh yes i am self-employed',
                                display: 'uh yes i am self-employed',
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.01 }
                            },
                            {
                                confidence: 0.8483144,
                                lexical: 'ah yes i am self employed',
                                itn: 'ah yes i am self employed',
                                maskedITN: 'ah yes i am self employed',
                                display: 'ah yes i am self employed',
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.01 }
                            },
                            {
                                confidence: 0.7290174,
                                lexical: 'yes i am self employed',
                                itn: 'yes i am self employed',
                                maskedITN: 'yes i am self employed',
                                display: 'yes i am self employed',
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.01 }
                            },
                            {
                                confidence: 0.8246085,
                                lexical: 'oh yes i am self employed',
                                itn: 'oh yes i am self employed',
                                maskedITN: 'oh yes i am self employed',
                                display: 'oh yes i am self employed',
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M11.19S',
                        duration: 'PT2.39S',
                        offsetInTicks: 711900000.0,
                        durationInTicks: 23900000.0,
                        nBest: [
                            {
                                confidence: 0.9331625,
                                lexical: 'OK so then you have a social security number as well',
                                itn: 'OK so then you have a social security number as well',
                                maskedITN: 'ok so then you have a social security number as well',
                                display: 'OK, so then you have a Social Security number as well?',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 }
                            },
                            {
                                confidence: 0.90863967,
                                lexical: 'ok so then you have our social security number as well',
                                itn: 'ok so then you have our social security number as well',
                                maskedITN: 'ok so then you have our social security number as well',
                                display: 'OK so then you have our social security number as well',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 }
                            },
                            {
                                confidence: 0.9029028,
                                lexical: 'ok so then you have your social security number as well',
                                itn: 'ok so then you have your social security number as well',
                                maskedITN: 'ok so then you have your social security number as well',
                                display: 'OK so then you have your social security number as well',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 }
                            },
                            {
                                confidence: 0.9148024,
                                lexical: 'ok ok so then you have a social security number as well',
                                itn: 'ok ok so then you have a social security number as well',
                                maskedITN: 'ok ok so then you have a social security number as well',
                                display: 'OK OK so then you have a social security number as well',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 }
                            },
                            {
                                confidence: 0.9050032,
                                lexical: 'okay so then you have a social security number as well',
                                itn: 'okay so then you have a social security number as well',
                                maskedITN: 'okay so then you have a social security number as well',
                                display: 'okay so then you have a social security number as well',
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.0 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT1M14.6S',
                        duration: 'PT0.67S',
                        offsetInTicks: 746000000.0,
                        durationInTicks: 6700000.0,
                        nBest: [
                            {
                                confidence: 0.8843462,
                                lexical: 'i guess i do',
                                itn: 'i guess i do',
                                maskedITN: 'i guess i do',
                                display: 'I guess I do.',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            },
                            {
                                confidence: 0.8363637,
                                lexical: 'uh yes i do',
                                itn: 'uh yes i do',
                                maskedITN: 'uh yes i do',
                                display: 'uh yes i do',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            },
                            {
                                confidence: 0.74676543,
                                lexical: 'ah yes i do',
                                itn: 'ah yes i do',
                                maskedITN: 'ah yes i do',
                                display: 'ah yes i do',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            },
                            {
                                confidence: 0.80333555,
                                lexical: 'oh yes i do',
                                itn: 'oh yes i do',
                                maskedITN: 'oh yes i do',
                                display: 'oh yes i do',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            },
                            {
                                confidence: 0.54633933,
                                lexical: 'yes i do',
                                itn: 'yes i do',
                                maskedITN: 'yes i do',
                                display: 'yes i do',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M16.18S',
                        duration: 'PT2.74S',
                        offsetInTicks: 761800000.0,
                        durationInTicks: 27400000.0,
                        nBest: [
                            {
                                confidence: 0.7890246,
                                lexical: 'OK and what is your social security number please',
                                itn: 'OK and what is your social security number please',
                                maskedITN: 'ok and what is your social security number please',
                                display: 'OK. And what is your Social Security number please?',
                                sentiment: { positive: 0.05, neutral: 0.92, negative: 0.03 }
                            },
                            {
                                confidence: 0.8375184,
                                lexical: 'ok uh and what is your social security number please',
                                itn: 'ok uh and what is your social security number please',
                                maskedITN: 'ok uh and what is your social security number please',
                                display: 'OK uh and what is your social security number please',
                                sentiment: { positive: 0.05, neutral: 0.92, negative: 0.03 }
                            },
                            {
                                confidence: 0.82725966,
                                lexical: 'ok and and what is your social security number please',
                                itn: 'ok and and what is your social security number please',
                                maskedITN: 'ok and and what is your social security number please',
                                display: 'OK and and what is your social security number please',
                                sentiment: { positive: 0.05, neutral: 0.92, negative: 0.03 }
                            },
                            {
                                confidence: 0.8169377,
                                lexical: 'ok uh in what is your social security number please',
                                itn: 'ok uh in what is your social security number please',
                                maskedITN: 'ok uh in what is your social security number please',
                                display: 'OK uh in what is your social security number please',
                                sentiment: { positive: 0.05, neutral: 0.92, negative: 0.03 }
                            },
                            {
                                confidence: 0.7438267,
                                lexical: 'okay and what is your social security number please',
                                itn: 'okay and what is your social security number please',
                                maskedITN: 'okay and what is your social security number please',
                                display: 'okay and what is your social security number please',
                                sentiment: { positive: 0.05, neutral: 0.92, negative: 0.03 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT1M19.87S',
                        duration: 'PT6.31S',
                        offsetInTicks: 798700000.0,
                        durationInTicks: 63100000.0,
                        nBest: [
                            {
                                confidence: 0.8593908,
                                lexical: "uh sure so it's four one two uh two five six seven eight nine",
                                itn: "uh sure so it's 412 uh 256789",
                                maskedITN: "uh sure so it's 412 uh 256789",
                                display: "Uh, sure. So it's 412, uh 256789.",
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.8415092,
                                lexical: "yeah sure so it's four one two uh two five six seven eight nine",
                                itn: "yeah sure so it's four one two uh two five six seven eight nine",
                                maskedITN: "yeah sure so it's four one two uh two five six seven eight nine",
                                display: "yeah sure so it's four one two uh two five six seven eight nine",
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.85757554,
                                lexical: "uh sure so it's four one two oh two five six seven eight nine",
                                itn: "uh sure so it's four one two oh two five six seven eight nine",
                                maskedITN: "uh sure so it's four one two oh two five six seven eight nine",
                                display: "uh sure so it's four one two oh two five six seven eight nine",
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.8667635,
                                lexical: "uh sure so it's four one two two five six seven eight nine",
                                itn: "uh sure so it's four one two two five six seven eight nine",
                                maskedITN: "uh sure so it's four one two two five six seven eight nine",
                                display: "uh sure so it's four one two two five six seven eight nine",
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.8488875,
                                lexical: "uh sure so it's four one two u h two five six seven eight nine",
                                itn: "uh sure so it's four one two u h two five six seven eight nine",
                                maskedITN: "uh sure so it's four one two u h two five six seven eight nine",
                                display: "uh sure so it's four one two U H two five six seven eight nine",
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M27.15S',
                        duration: 'PT3.87S',
                        offsetInTicks: 871500000.0,
                        durationInTicks: 38700000.0,
                        nBest: [
                            {
                                confidence: 0.84932685,
                                lexical: 'sorry what was that a two five or a two two five you cut out for a bit',
                                itn: 'sorry what was that A-25 or A225 you cut out for a bit',
                                maskedITN: 'sorry what was that a-25 or a225 you cut out for a bit',
                                display: 'Sorry, what was that A-25 or A225 you cut out for a bit?',
                                sentiment: { positive: 0.01, neutral: 0.05, negative: 0.94 }
                            },
                            {
                                confidence: 0.8452801,
                                lexical: 'sorry what was that a two five or a two two five you cut out for a bit',
                                itn: 'sorry what was that a two five or a two two five you cut out for a bit',
                                maskedITN: 'sorry what was that a two five or a two two five you cut out for a bit',
                                display: 'sorry what was that A two five or a two two five you cut out for a bit',
                                sentiment: { positive: 0.01, neutral: 0.05, negative: 0.94 }
                            },
                            {
                                confidence: 0.8416952,
                                lexical: 'sorry what was that at two five or a two two five you cut out for a bit',
                                itn: 'sorry what was that at two five or a two two five you cut out for a bit',
                                maskedITN: 'sorry what was that at two five or a two two five you cut out for a bit',
                                display: 'sorry what was that at two five or a two two five you cut out for a bit',
                                sentiment: { positive: 0.01, neutral: 0.05, negative: 0.94 }
                            },
                            {
                                confidence: 0.8405001,
                                lexical: 'sorry what was that a two five or at two two five you cut out for a bit',
                                itn: 'sorry what was that a two five or at two two five you cut out for a bit',
                                maskedITN: 'sorry what was that a two five or at two two five you cut out for a bit',
                                display: 'sorry what was that a two five or at two two five you cut out for a bit',
                                sentiment: { positive: 0.01, neutral: 0.05, negative: 0.94 }
                            },
                            {
                                confidence: 0.8390302,
                                lexical: 'sorry what was that a two five o r a two two five you cut out for a bit',
                                itn: 'sorry what was that a two five o r a two two five you cut out for a bit',
                                maskedITN: 'sorry what was that a two five o r a two two five you cut out for a bit',
                                display: 'sorry what was that a two five O R A two two five you cut out for a bit',
                                sentiment: { positive: 0.01, neutral: 0.05, negative: 0.94 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT1M31.99S',
                        duration: 'PT1.55S',
                        offsetInTicks: 919900000.0,
                        durationInTicks: 15500000.0,
                        nBest: [
                            {
                                confidence: 0.80090487,
                                lexical: "uh it's double two so",
                                itn: "uh it's 22 so",
                                maskedITN: "uh it's 22 so",
                                display: "Uh, it's 22, so.",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.75100183,
                                lexical: "uh it's double too so",
                                itn: "uh it's double too so",
                                maskedITN: "uh it's double too so",
                                display: "uh it's double too so",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.633697,
                                lexical: "it's double two so",
                                itn: "it's double two so",
                                maskedITN: "it's double two so",
                                display: "it's double two so",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.7337306,
                                lexical: "uh it's double two so",
                                itn: "uh it's double two so",
                                maskedITN: "uh it's double two so",
                                display: "uh it's double two SO",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.689333,
                                lexical: "uh it's doubled two so",
                                itn: "uh it's doubled two so",
                                maskedITN: "uh it's doubled two so",
                                display: "uh it's doubled two so",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT1M34.64S',
                        duration: 'PT2.92S',
                        offsetInTicks: 946400000.0,
                        durationInTicks: 29200000.0,
                        nBest: [
                            {
                                confidence: 0.788596,
                                lexical: 'four one two then another two then five',
                                itn: '412 then another two then five',
                                maskedITN: '412 then another two then five',
                                display: '412, then another two, then five.',
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.77360415,
                                lexical: 'for one two then another two then five',
                                itn: 'for one two then another two then five',
                                maskedITN: 'for one two then another two then five',
                                display: 'for one two then another two then five',
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.7603413,
                                lexical: 'four one two then another two than five',
                                itn: 'four one two then another two than five',
                                maskedITN: 'four one two then another two than five',
                                display: 'four one two then another two than five',
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.7934927,
                                lexical: 'four one two two then another two then five',
                                itn: 'four one two two then another two then five',
                                maskedITN: 'four one two two then another two then five',
                                display: 'four one two two then another two then five',
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.7803465,
                                lexical: 'four oh one two then another two then five',
                                itn: 'four oh one two then another two then five',
                                maskedITN: 'four oh one two then another two then five',
                                display: 'four oh one two then another two then five',
                                sentiment: { positive: 0.01, neutral: 0.98, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M38.52S',
                        duration: 'PT4.78S',
                        offsetInTicks: 985200000.0,
                        durationInTicks: 47800000.0,
                        nBest: [
                            {
                                confidence: 0.86421967,
                                lexical: 'thank you so much and could i have your email address please',
                                itn: 'thank you so much and could i have your email address please',
                                maskedITN: 'thank you so much and could i have your e-mail address please',
                                display: 'Thank you so much and could I have your e-mail address please?',
                                sentiment: { positive: 0.77, neutral: 0.21, negative: 0.02 }
                            },
                            {
                                confidence: 0.8882714,
                                lexical: 'thank thank you so much and could i have your email address please',
                                itn: 'thank thank you so much and could i have your email address please',
                                maskedITN: 'thank thank you so much and could i have your email address please',
                                display: 'thank thank you so much and could i have your email address please',
                                sentiment: { positive: 0.77, neutral: 0.21, negative: 0.02 }
                            },
                            {
                                confidence: 0.8815518,
                                lexical: 'hey thank you so much and could i have your email address please',
                                itn: 'hey thank you so much and could i have your email address please',
                                maskedITN: 'hey thank you so much and could i have your email address please',
                                display: 'hey thank you so much and could i have your email address please',
                                sentiment: { positive: 0.77, neutral: 0.21, negative: 0.02 }
                            },
                            {
                                confidence: 0.8836837,
                                lexical: 'ok thank you so much and could i have your email address please',
                                itn: 'ok thank you so much and could i have your email address please',
                                maskedITN: 'ok thank you so much and could i have your email address please',
                                display: 'OK thank you so much and could i have your email address please',
                                sentiment: { positive: 0.77, neutral: 0.21, negative: 0.02 }
                            },
                            {
                                confidence: 0.8535357,
                                lexical: 'thank you so much and i could i have your email address please',
                                itn: 'thank you so much and i could i have your email address please',
                                maskedITN: 'thank you so much and i could i have your email address please',
                                display: 'thank you so much and i could i have your email address please',
                                sentiment: { positive: 0.77, neutral: 0.21, negative: 0.02 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT1M44.33S',
                        duration: 'PT7.05S',
                        offsetInTicks: 1043300000.0,
                        durationInTicks: 70500000.0,
                        nBest: [
                            {
                                confidence: 0.85194737,
                                lexical:
                                    "yeah it's mary rondo at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                itn: "yeah it's mary rondo@gmail.com so myfirstandlastname@gmail.com no periods no dashes",
                                maskedITN: "yeah it's mary rondo@gmail.com so myfirstandlastname@gmail.com no periods no dashes",
                                display: "Yeah, it's Mary rondo@gmail.com, so myfirstandlastname@gmail.com. No periods, no dashes.",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            },
                            {
                                confidence: 0.83294094,
                                lexical:
                                    "yeah it's mary rhondo at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                itn:
                                    "yeah it's mary rhondo at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                maskedITN:
                                    "yeah it's mary rhondo at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                display:
                                    "yeah it's mary rhondo at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            },
                            {
                                confidence: 0.8496681,
                                lexical:
                                    "yeah it's mary rondo at gmail dot com so my first and last name at gmail dot com no period no dashes",
                                itn: "yeah it's mary rondo at gmail dot com so my first and last name at gmail dot com no period no dashes",
                                maskedITN:
                                    "yeah it's mary rondo at gmail dot com so my first and last name at gmail dot com no period no dashes",
                                display:
                                    "yeah it's mary rondo at gmail dot com so my first and last name at gmail dot com no period no dashes",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            },
                            {
                                confidence: 0.811947,
                                lexical:
                                    "yeah it's mary rolando at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                itn:
                                    "yeah it's mary rolando at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                maskedITN:
                                    "yeah it's mary rolando at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                display:
                                    "yeah it's mary rolando at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            },
                            {
                                confidence: 0.8660088,
                                lexical:
                                    "yeah hi it's mary rondo at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                itn:
                                    "yeah hi it's mary rondo at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                maskedITN:
                                    "yeah hi it's mary rondo at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                display:
                                    "yeah hi it's mary rondo at gmail dot com so my first and last name at gmail dot com no periods no dashes",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.02 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT1M52.81S',
                        duration: 'PT14.83S',
                        offsetInTicks: 1128100000.0,
                        durationInTicks: 148300000.0,
                        nBest: [
                            {
                                confidence: 0.8394234,
                                lexical:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                itn:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about 24 hours or so to confirm your application",
                                maskedITN:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about 24 hours or so to confirm your application",
                                display:
                                    "Great. Uh, that is the last question. So let me take your information and I'll be able to get you signed up right away. Thank you for calling Contoso, and I'll be able to get you signed up immediately. One of our agents will call you back in about 24 hours or so to confirm your application.",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.83628196,
                                lexical:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away uh thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                itn:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away uh thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                maskedITN:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away uh thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                display:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away uh thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.83209544,
                                lexical:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and uh i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                itn:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and uh i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                maskedITN:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and uh i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                display:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and uh i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.8290177,
                                lexical:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away uh thank you for calling contoso and uh i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                itn:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away uh thank you for calling contoso and uh i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                maskedITN:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away uh thank you for calling contoso and uh i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                display:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away uh thank you for calling contoso and uh i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.8408977,
                                lexical:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling uh contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                itn:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling uh contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                maskedITN:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling uh contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                display:
                                    "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling uh contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT2M8.81S',
                        duration: 'PT1.21S',
                        offsetInTicks: 1288100000.0,
                        durationInTicks: 12100000.0,
                        nBest: [
                            {
                                confidence: 0.7607079,
                                lexical: "that's great thank you",
                                itn: "that's great thank you",
                                maskedITN: "that's great thank you",
                                display: "That's great. Thank you.",
                                sentiment: { positive: 0.86, neutral: 0.12, negative: 0.03 }
                            },
                            {
                                confidence: 0.8767555,
                                lexical: 'that was quick thank you',
                                itn: 'that was quick thank you',
                                maskedITN: 'that was quick thank you',
                                display: 'that was quick thank you',
                                sentiment: { positive: 0.86, neutral: 0.12, negative: 0.03 }
                            },
                            {
                                confidence: 0.7591521,
                                lexical: 'that was great thank you',
                                itn: 'that was great thank you',
                                maskedITN: 'that was great thank you',
                                display: 'that was great thank you',
                                sentiment: { positive: 0.86, neutral: 0.12, negative: 0.03 }
                            },
                            {
                                confidence: 0.7151923,
                                lexical: "that's that's great thank you",
                                itn: "that's that's great thank you",
                                maskedITN: "that's that's great thank you",
                                display: "that's that's great thank you",
                                sentiment: { positive: 0.86, neutral: 0.12, negative: 0.03 }
                            },
                            {
                                confidence: 0.63612413,
                                lexical: 'sounds great thank you',
                                itn: 'sounds great thank you',
                                maskedITN: 'sounds great thank you',
                                display: 'sounds great thank you',
                                sentiment: { positive: 0.86, neutral: 0.12, negative: 0.03 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT2M11.27S',
                        duration: 'PT9.79S',
                        offsetInTicks: 1312700000.0,
                        durationInTicks: 97900000.0,
                        nBest: [
                            {
                                confidence: 0.82904434,
                                lexical:
                                    'absolutely um if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                itn:
                                    'absolutely um if you need anything else please give us a call at 1-800-555-5564 ext 123 thank you very much for calling contoso',
                                maskedITN:
                                    'absolutely um if you need anything else please give us a call at 1-800-555-5564 ext 123 thank you very much for calling contoso',
                                display:
                                    'Absolutely. Um, if you need anything else, please give us a call at 1-800-555-5564, ext 123. Thank you very much for calling Contoso.',
                                sentiment: { positive: 0.89, neutral: 0.1, negative: 0.01 }
                            },
                            {
                                confidence: 0.872239,
                                lexical:
                                    'absolutely if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                itn:
                                    'absolutely if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                maskedITN:
                                    'absolutely if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                display:
                                    'absolutely if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                sentiment: { positive: 0.89, neutral: 0.1, negative: 0.01 }
                            },
                            {
                                confidence: 0.8295775,
                                lexical:
                                    'absolutely uh if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                itn:
                                    'absolutely uh if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                maskedITN:
                                    'absolutely uh if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                display:
                                    'absolutely uh if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                sentiment: { positive: 0.89, neutral: 0.1, negative: 0.01 }
                            },
                            {
                                confidence: 0.8295785,
                                lexical:
                                    'absolutely umm if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                itn:
                                    'absolutely umm if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                maskedITN:
                                    'absolutely umm if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                display:
                                    'absolutely umm if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                sentiment: { positive: 0.89, neutral: 0.1, negative: 0.01 }
                            },
                            {
                                confidence: 0.83263963,
                                lexical:
                                    'absolutely and if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                itn:
                                    'absolutely and if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                maskedITN:
                                    'absolutely and if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                display:
                                    'absolutely and if you need anything else please give us a call at one eight hundred five five five five five six four extension one two three thank you very much for calling contoso',
                                sentiment: { positive: 0.89, neutral: 0.1, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT2M22.2S',
                        duration: 'PT1.99S',
                        offsetInTicks: 1422000000.0,
                        durationInTicks: 19900000.0,
                        nBest: [
                            {
                                confidence: 0.8164943,
                                lexical: 'actually uh i have one more question',
                                itn: 'actually uh i have one more question',
                                maskedITN: 'actually uh i have one more question',
                                display: 'Actually, uh, I have one more question.',
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            },
                            {
                                confidence: 0.85681933,
                                lexical: 'actually uh so i have one more question',
                                itn: 'actually uh so i have one more question',
                                maskedITN: 'actually uh so i have one more question',
                                display: 'actually uh so i have one more question',
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            },
                            {
                                confidence: 0.78733176,
                                lexical: 'actually uh sorry one more question',
                                itn: 'actually uh sorry one more question',
                                maskedITN: 'actually uh sorry one more question',
                                display: 'actually uh sorry one more question',
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            },
                            {
                                confidence: 0.7662485,
                                lexical: 'actually i have one more question',
                                itn: 'actually i have one more question',
                                maskedITN: 'actually i have one more question',
                                display: 'actually i have one more question',
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            },
                            {
                                confidence: 0.7776453,
                                lexical: "actually i'm sorry one more question",
                                itn: "actually i'm sorry one more question",
                                maskedITN: "actually i'm sorry one more question",
                                display: "actually i'm sorry one more question",
                                sentiment: { positive: 0.01, neutral: 0.96, negative: 0.03 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT2M24.9S',
                        duration: 'PT1.11S',
                        offsetInTicks: 1449000000.0,
                        durationInTicks: 11100000.0,
                        nBest: [
                            {
                                confidence: 0.933597,
                                lexical: 'uh yes of course',
                                itn: 'uh yes of course',
                                maskedITN: 'uh yes of course',
                                display: 'Uh, yes, of course.',
                                sentiment: { positive: 0.02, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.83338994,
                                lexical: 'ah yes of course',
                                itn: 'ah yes of course',
                                maskedITN: 'ah yes of course',
                                display: 'ah yes of course',
                                sentiment: { positive: 0.02, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.91648465,
                                lexical: 'oh yes of course',
                                itn: 'oh yes of course',
                                maskedITN: 'oh yes of course',
                                display: 'oh yes of course',
                                sentiment: { positive: 0.02, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.79377747,
                                lexical: 'yes of course',
                                itn: 'yes of course',
                                maskedITN: 'yes of course',
                                display: 'yes of course',
                                sentiment: { positive: 0.02, neutral: 0.98, negative: 0.01 }
                            },
                            {
                                confidence: 0.7827567,
                                lexical: 'ohh yes of course',
                                itn: 'ohh yes of course',
                                maskedITN: 'ohh yes of course',
                                display: 'ohh yes of course',
                                sentiment: { positive: 0.02, neutral: 0.98, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT2M26.98S',
                        duration: 'PT3.02S',
                        offsetInTicks: 1469800000.0,
                        durationInTicks: 30200000.0,
                        nBest: [
                            {
                                confidence: 0.8816513,
                                lexical: "i'm curious what i'd be getting a physical card as proof of coverage",
                                itn: "i'm curious what i'd be getting a physical card as proof of coverage",
                                maskedITN: "i'm curious what i'd be getting a physical card as proof of coverage",
                                display: "I'm curious what I'd be getting a physical card as proof of coverage.",
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.02 }
                            },
                            {
                                confidence: 0.8727468,
                                lexical: "i'm curious what i'll be getting a physical card as proof of coverage",
                                itn: "i'm curious what i'll be getting a physical card as proof of coverage",
                                maskedITN: "i'm curious what i'll be getting a physical card as proof of coverage",
                                display: "i'm curious what i'll be getting a physical card as proof of coverage",
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.02 }
                            },
                            {
                                confidence: 0.83651626,
                                lexical: "i'm curious we'll be getting a physical card as proof of coverage",
                                itn: "i'm curious we'll be getting a physical card as proof of coverage",
                                maskedITN: "i'm curious we'll be getting a physical card as proof of coverage",
                                display: "i'm curious we'll be getting a physical card as proof of coverage",
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.02 }
                            },
                            {
                                confidence: 0.86478686,
                                lexical: "i'm curious what i be getting a physical card as proof of coverage",
                                itn: "i'm curious what i be getting a physical card as proof of coverage",
                                maskedITN: "i'm curious what i be getting a physical card as proof of coverage",
                                display: "i'm curious what i be getting a physical card as proof of coverage",
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.02 }
                            },
                            {
                                confidence: 0.8688649,
                                lexical: "i'm curious well i'd be getting a physical card as proof of coverage",
                                itn: "i'm curious well i'd be getting a physical card as proof of coverage",
                                maskedITN: "i'm curious well i'd be getting a physical card as proof of coverage",
                                display: "i'm curious well i'd be getting a physical card as proof of coverage",
                                sentiment: { positive: 0.01, neutral: 0.97, negative: 0.02 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT2M31.51S',
                        duration: 'PT4.9S',
                        offsetInTicks: 1515100000.0,
                        durationInTicks: 49000000.0,
                        nBest: [
                            {
                                confidence: 0.91685164,
                                lexical: 'so the default is a digital membership card but we can send you a physical card if you prefer',
                                itn: 'so the default is a digital membership card but we can send you a physical card if you prefer',
                                maskedITN: 'so the default is a digital membership card but we can send you a physical card if you prefer',
                                display: 'So the default is a digital membership card, but we can send you a physical card if you prefer.',
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.0 }
                            },
                            {
                                confidence: 0.9252255,
                                lexical: 'so the default is a digital membership card uh but we can send you a physical card if you prefer',
                                itn: 'so the default is a digital membership card uh but we can send you a physical card if you prefer',
                                maskedITN:
                                    'so the default is a digital membership card uh but we can send you a physical card if you prefer',
                                display: 'so the default is a digital membership card uh but we can send you a physical card if you prefer',
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.0 }
                            },
                            {
                                confidence: 0.9134801,
                                lexical:
                                    'so the default is the digital membership card uh but we can send you a physical card if you prefer',
                                itn: 'so the default is the digital membership card uh but we can send you a physical card if you prefer',
                                maskedITN:
                                    'so the default is the digital membership card uh but we can send you a physical card if you prefer',
                                display:
                                    'so the default is the digital membership card uh but we can send you a physical card if you prefer',
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.0 }
                            },
                            {
                                confidence: 0.90886855,
                                lexical:
                                    'so the default is a digital membership card yeah but we can send you a physical card if you prefer',
                                itn: 'so the default is a digital membership card yeah but we can send you a physical card if you prefer',
                                maskedITN:
                                    'so the default is a digital membership card yeah but we can send you a physical card if you prefer',
                                display:
                                    'so the default is a digital membership card yeah but we can send you a physical card if you prefer',
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.0 }
                            },
                            {
                                confidence: 0.91508055,
                                lexical:
                                    "so the default is a digital membership card 's uh but we can send you a physical card if you prefer",
                                itn: "so the default is a digital membership card 's uh but we can send you a physical card if you prefer",
                                maskedITN:
                                    "so the default is a digital membership card 's uh but we can send you a physical card if you prefer",
                                display:
                                    "so the default is a digital membership card 's uh but we can send you a physical card if you prefer",
                                sentiment: { positive: 0.0, neutral: 0.99, negative: 0.0 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT2M37.78S',
                        duration: 'PT4.67S',
                        offsetInTicks: 1577800000.0,
                        durationInTicks: 46700000.0,
                        nBest: [
                            {
                                confidence: 0.7654014,
                                lexical:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address",
                                itn:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address",
                                maskedITN:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address",
                                display:
                                    "Uh, yes. Could you please mail it to me when it's ready? I'd like to have it shipped to you for my address.",
                                sentiment: { positive: 0.22, neutral: 0.76, negative: 0.02 }
                            },
                            {
                                confidence: 0.7675828,
                                lexical:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to you from my address",
                                itn:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to you from my address",
                                maskedITN:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to you from my address",
                                display:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to you from my address",
                                sentiment: { positive: 0.22, neutral: 0.76, negative: 0.02 }
                            },
                            {
                                confidence: 0.8143262,
                                lexical:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm already for my address",
                                itn:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm already for my address",
                                maskedITN:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm already for my address",
                                display:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm already for my address",
                                sentiment: { positive: 0.22, neutral: 0.76, negative: 0.02 }
                            },
                            {
                                confidence: 0.83081293,
                                lexical:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm i ready for my address",
                                itn:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm i ready for my address",
                                maskedITN:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm i ready for my address",
                                display:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm i ready for my address",
                                sentiment: { positive: 0.22, neutral: 0.76, negative: 0.02 }
                            },
                            {
                                confidence: 0.83315974,
                                lexical:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm are you for my address",
                                itn:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm are you for my address",
                                maskedITN:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm are you for my address",
                                display:
                                    "uh yes could you please mail it to me when it's ready i'd like to have it shipped to umm are you for my address",
                                sentiment: { positive: 0.22, neutral: 0.76, negative: 0.02 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT2M43.54S',
                        duration: 'PT0.48S',
                        offsetInTicks: 1635400000.0,
                        durationInTicks: 4800000.0,
                        nBest: [
                            {
                                confidence: 0.69161385,
                                lexical: 'uh yeah',
                                itn: 'uh yeah',
                                maskedITN: 'uh yeah',
                                display: 'Uh, yeah.',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.63200885,
                                lexical: 'ohh yeah',
                                itn: 'ohh yeah',
                                maskedITN: 'ohh yeah',
                                display: 'ohh yeah',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.87254375,
                                lexical: 'yeah',
                                itn: 'yeah',
                                maskedITN: 'yeah',
                                display: 'yeah',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.79430157,
                                lexical: 'yeah yeah',
                                itn: 'yeah yeah',
                                maskedITN: 'yeah yeah',
                                display: 'yeah yeah',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.8562709,
                                lexical: 'oh yeah',
                                itn: 'oh yeah',
                                maskedITN: 'oh yeah',
                                display: 'oh yeah',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT2M45.02S',
                        duration: 'PT9.94S',
                        offsetInTicks: 1650200000.0,
                        durationInTicks: 99400000.0,
                        nBest: [
                            {
                                confidence: 0.86835873,
                                lexical:
                                    "uh so it's two six six zero unit A on maple avenue southeast lansing and then zip code is four eight eight two three",
                                itn: "uh so it's 2660 unit A on maple ave SE lansing and then zip code is 48823",
                                maskedITN: "uh so it's 2660 unit a on maple ave se lansing and then zip code is 48823",
                                display: "Uh, so it's 2660, unit A on Maple Ave, SE Lansing and then ZIP code is 48823.",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.8644589,
                                lexical:
                                    "uh so it's two six six zero unit a 's on maple avenue southeast lansing and then zip code is four eight eight two three",
                                itn:
                                    "uh so it's two six six zero unit a 's on maple avenue southeast lansing and then zip code is four eight eight two three",
                                maskedITN:
                                    "uh so it's two six six zero unit a 's on maple avenue southeast lansing and then zip code is four eight eight two three",
                                display:
                                    "uh so it's two six six zero unit A 's on maple avenue southeast lansing and then zip code is four eight eight two three",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.86389476,
                                lexical:
                                    "uh so it's two six six zero unit a on maple avenue southeast lansing and then zip code is four eight eight two three",
                                itn:
                                    "uh so it's two six six zero unit a on maple avenue southeast lansing and then zip code is four eight eight two three",
                                maskedITN:
                                    "uh so it's two six six zero unit a on maple avenue southeast lansing and then zip code is four eight eight two three",
                                display:
                                    "uh so it's two six six zero unit a on maple avenue southeast lansing and then zip code is four eight eight two three",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.86661613,
                                lexical:
                                    "so it's two six six zero unit a on maple avenue southeast lansing and then zip code is four eight eight two three",
                                itn:
                                    "so it's two six six zero unit a on maple avenue southeast lansing and then zip code is four eight eight two three",
                                maskedITN:
                                    "so it's two six six zero unit a on maple avenue southeast lansing and then zip code is four eight eight two three",
                                display:
                                    "so it's two six six zero unit A on maple avenue southeast lansing and then zip code is four eight eight two three",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            },
                            {
                                confidence: 0.8650027,
                                lexical:
                                    "uh so it's two six six zero unit a on maple avenue south east lansing and then zip code is four eight eight two three",
                                itn:
                                    "uh so it's two six six zero unit a on maple avenue south east lansing and then zip code is four eight eight two three",
                                maskedITN:
                                    "uh so it's two six six zero unit a on maple avenue south east lansing and then zip code is four eight eight two three",
                                display:
                                    "uh so it's two six six zero unit A on maple avenue south east lansing and then zip code is four eight eight two three",
                                sentiment: { positive: 0.01, neutral: 0.99, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT2M56.3S',
                        duration: 'PT1.88S',
                        offsetInTicks: 1763000000.0,
                        durationInTicks: 18800000.0,
                        nBest: [
                            {
                                confidence: 0.828954,
                                lexical: "absolutely i've made a note on your file",
                                itn: "absolutely i've made a note on your file",
                                maskedITN: "absolutely i've made a note on your file",
                                display: "Absolutely. I've made a note on your file.",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.79915166,
                                lexical: 'absolutely i made a note on your file',
                                itn: 'absolutely i made a note on your file',
                                maskedITN: 'absolutely i made a note on your file',
                                display: 'absolutely i made a note on your file',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.8512958,
                                lexical: 'absolutely i have made a note on your file',
                                itn: 'absolutely i have made a note on your file',
                                maskedITN: 'absolutely i have made a note on your file',
                                display: 'absolutely i have made a note on your file',
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.8144592,
                                lexical: "absolutely i've made a note in your file",
                                itn: "absolutely i've made a note in your file",
                                maskedITN: "absolutely i've made a note in your file",
                                display: "absolutely i've made a note in your file",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            },
                            {
                                confidence: 0.83469826,
                                lexical: "absolutely i i've made a note on your file",
                                itn: "absolutely i i've made a note on your file",
                                maskedITN: "absolutely i i've made a note on your file",
                                display: "absolutely i i've made a note on your file",
                                sentiment: { positive: 0.02, neutral: 0.96, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 1,
                        offset: 'PT2M59.6S',
                        duration: 'PT0.94S',
                        offsetInTicks: 1796000000.0,
                        durationInTicks: 9400000.0,
                        nBest: [
                            {
                                confidence: 0.87778336,
                                lexical: 'awesome thanks so much',
                                itn: 'awesome thanks so much',
                                maskedITN: 'awesome thanks so much',
                                display: 'Awesome. Thanks so much.',
                                sentiment: { positive: 0.97, neutral: 0.02, negative: 0.01 }
                            },
                            {
                                confidence: 0.8610428,
                                lexical: 'awesome thank you so much',
                                itn: 'awesome thank you so much',
                                maskedITN: 'awesome thank you so much',
                                display: 'awesome thank you so much',
                                sentiment: { positive: 0.97, neutral: 0.02, negative: 0.01 }
                            },
                            {
                                confidence: 0.76485157,
                                lexical: 'awesome thank so much',
                                itn: 'awesome thank so much',
                                maskedITN: 'awesome thank so much',
                                display: 'awesome thank so much',
                                sentiment: { positive: 0.97, neutral: 0.02, negative: 0.01 }
                            },
                            {
                                confidence: 0.8334137,
                                lexical: 'awesome thanks so so much',
                                itn: 'awesome thanks so so much',
                                maskedITN: 'awesome thanks so so much',
                                display: 'awesome thanks so so much',
                                sentiment: { positive: 0.97, neutral: 0.02, negative: 0.01 }
                            },
                            {
                                confidence: 0.8095077,
                                lexical: 'awesome thanks so much so',
                                itn: 'awesome thanks so much so',
                                maskedITN: 'awesome thanks so much so',
                                display: 'awesome thanks so much so',
                                sentiment: { positive: 0.97, neutral: 0.02, negative: 0.01 }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        offset: 'PT3M1.54S',
                        duration: 'PT2.76S',
                        offsetInTicks: 1815400000.0,
                        durationInTicks: 27600000.0,
                        nBest: [
                            {
                                confidence: 0.7518286,
                                lexical: "you're very welcome thank you for calling contoso and have a great day",
                                itn: "you're very welcome thank you for calling contoso and have a great day",
                                maskedITN: "you're very welcome thank you for calling contoso and have a great day",
                                display: "You're very welcome. Thank you for calling Contoso and have a great day.",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.7652579,
                                lexical: 'you are very welcome thank you for calling contoso and have a great day',
                                itn: 'you are very welcome thank you for calling contoso and have a great day',
                                maskedITN: 'you are very welcome thank you for calling contoso and have a great day',
                                display: 'you are very welcome thank you for calling contoso and have a great day',
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.74688596,
                                lexical: "you're very welcome thank you for calling contessa and have a great day",
                                itn: "you're very welcome thank you for calling contessa and have a great day",
                                maskedITN: "you're very welcome thank you for calling contessa and have a great day",
                                display: "you're very welcome thank you for calling contessa and have a great day",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.7389692,
                                lexical: "you're very welcome thank you for calling gonzo and have a great day",
                                itn: "you're very welcome thank you for calling gonzo and have a great day",
                                maskedITN: "you're very welcome thank you for calling gonzo and have a great day",
                                display: "you're very welcome thank you for calling gonzo and have a great day",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            },
                            {
                                confidence: 0.7720045,
                                lexical: "you're very welcome thank you for calling contest so and have a great day",
                                itn: "you're very welcome thank you for calling contest so and have a great day",
                                maskedITN: "you're very welcome thank you for calling contest so and have a great day",
                                display: "you're very welcome thank you for calling contest so and have a great day",
                                sentiment: { positive: 0.99, neutral: 0.01, negative: 0.0 }
                            }
                        ]
                    }
                ]
            },
            conversationAnalyticsResults: {
                conversationSummaryResults: {
                    conversations: [
                        {
                            id: 'conversation1',
                            summaries: [
                                { aspect: 'issue', text: 'Customer wants to sign up for insurance' },
                                { aspect: 'resolution', text: 'Customer was advised that the application will be submitted in 24 hours' }
                            ]
                        }
                    ]
                },
                conversationPiiResults: {
                    combinedRedactedContent: [
                        {
                            channel: '0',
                            display:
                                "Hello, thank you for calling Contoso. Who am I speaking with today? Hi, ****. Uh, are you calling because you need health insurance? Great. If you can answer a few questions, uh, we can get you signed up in the Jiffy. So what's your full name? Got it. And what's the best callback number in case we get disconnected? Yeah, that'll be fine. Got it. So to confirm, it's **********. Excellent. Uh, let's get some additional information from your app. For your application, uh, do you have a job? OK, so then you have a Social Security number as well? OK. And what is your Social Security number please? Sorry, what was that A-25 or A225 you cut out for a bit? Thank you so much and could I have your e-mail address please? Great. Uh, that is the last question. So let me take your information and I'll be able to get you signed up right away. Thank you for calling Contoso, and I'll be able to get you signed up immediately. One of our agents will call you back in about 24 hours or so to confirm your application. Absolutely. Um, if you need anything else, please give us a call at ***********************. Thank you very much for calling Contoso. Uh, yes, of course. So the default is a digital membership card, but we can send you a physical card if you prefer. Uh, yeah. Absolutely. I've made a note on your file. You're very welcome. Thank you for calling Contoso and have a great day. ",
                            itn:
                                "hello thank you for calling contoso who am i speaking with today hi **** uh are you calling because you need health insurance great if you can answer a few questions uh we can get you signed up in the jiffy so what's your full name got it and what's the best callback number in case we get disconnected yeah that'll be fine got it so to confirm it's ********** excellent uh let's get some additional information from your app for your application uh do you have a job OK so then you have a social security number as well OK and what is your social security number please sorry what was that A-25 or A225 you cut out for a bit thank you so much and could i have your email address please great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about 24 hours or so to confirm your application absolutely um if you need anything else please give us a call at ********************** thank you very much for calling contoso uh yes of course so the default is a digital membership card but we can send you a physical card if you prefer uh yeah absolutely i've made a note on your file you're very welcome thank you for calling contoso and have a great day ",
                            lexical:
                                "hello thank you for calling contoso who am i speaking with today hi **** uh are you calling because you need health insurance great if you can answer a few questions uh we can get you signed up in the jiffy so what's your full name got it and what's the best callback number in case we get disconnected yeah that'll be fine got it so to confirm it's ************************************************ excellent uh let's get some additional information from your app for your application uh do you have a job OK so then you have a social security number as well OK and what is your social security number please sorry what was that a two five or a two two five you cut out for a bit thank you so much and could i have your email address please great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application absolutely um if you need anything else please give us a call at *************************************************************************** thank you very much for calling contoso uh yes of course so the default is a digital membership card but we can send you a physical card if you prefer uh yeah absolutely i've made a note on your file you're very welcome thank you for calling contoso and have a great day "
                        },
                        {
                            channel: '1',
                            display:
                                "Hi, my name is **********. I'm trying to enroll myself with Contoso. Yes. Yeah, I'm calling to sign up for insurance. OK. So **********************************************************************************************************. *****. I only have a cell phone, so I can give you that. Sure. So it's ********************. Yep, that's right. Uh, yes, I am self-employed. I guess I do. Uh, sure. So it's ***, uh ******. Uh, it's 22, so. 412, then another two, then five. Yeah, it's *****************************************************. *********************. That's great. Thank you. Actually, uh, I have one more question. I'm curious what I'd be getting a physical card as proof of coverage. Uh, yes. Could you please mail it to me when it's ready? I'd like to have it shipped to you for my address. Uh, so it's ****************************************************************. Awesome. Thanks so much. ",
                            itn:
                                "hi my name is ********** i'm trying to enroll myself with contoso yes yeah i'm calling to sign up for insurance OK so ******************************************************************************************************** ***** i only have a cell phone so i can give you that sure so it's ******************** yep that's right uh yes i am self employed i guess i do uh sure so it's *** uh ****** uh it's 22 so 412 then another two then five yeah it's **************************************************** ******************** that's great thank you actually uh i have one more question i'm curious what i'd be getting a physical card as proof of coverage uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address uh so it's ************************************************************** awesome thanks so much ",
                            lexical:
                                "hi my name is ********** i'm trying to enroll myself with contoso yes yeah i'm calling to sign up for insurance OK so ********************************************************************************************************* ***** i only have a cell phone so i can give you that sure so it's ********************************************************* yep that's right uh yes i am self employed i guess i do uh sure so it's ************ uh ***************************** uh it's double two so four one two then another two then five yeah it's ********************************************************************** ******************** that's great thank you actually uh i have one more question i'm curious what i'd be getting a physical card as proof of coverage uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address uh so it's ********************************************************************************************************* awesome thanks so much "
                        }
                    ],
                    conversations: [
                        {
                            id: 'conversation1',
                            conversationItems: [
                                {
                                    id: '0',
                                    redactedContent: {
                                        itn: 'hello thank you for calling contoso who am i speaking with today',
                                        lexical: 'hello thank you for calling contoso who am i speaking with today',
                                        text: 'Hello, thank you for calling Contoso. Who am I speaking with today?'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT0.77S'
                                },
                                {
                                    id: '1',
                                    redactedContent: {
                                        itn: "hi my name is ********** i'm trying to enroll myself with contoso",
                                        lexical: "hi my name is ********** i'm trying to enroll myself with contoso",
                                        text: "Hi, my name is **********. I'm trying to enroll myself with Contoso."
                                    },
                                    entities: [
                                        {
                                            text: 'Mary Rondo',
                                            category: 'Name',
                                            offset: 15,
                                            length: 10,
                                            confidenceScore: 0.97
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT4.55S'
                                },
                                {
                                    id: '2',
                                    redactedContent: {
                                        itn: 'hi **** uh are you calling because you need health insurance',
                                        lexical: 'hi **** uh are you calling because you need health insurance',
                                        text: 'Hi, ****. Uh, are you calling because you need health insurance?'
                                    },
                                    entities: [
                                        {
                                            text: 'Mary',
                                            category: 'Name',
                                            offset: 4,
                                            length: 4,
                                            confidenceScore: 0.94
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT9.53S'
                                },
                                {
                                    id: '3',
                                    redactedContent: {
                                        itn: "yes yeah i'm calling to sign up for insurance",
                                        lexical: "yes yeah i'm calling to sign up for insurance",
                                        text: "Yes. Yeah, I'm calling to sign up for insurance."
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT13.09S'
                                },
                                {
                                    id: '4',
                                    redactedContent: {
                                        itn: 'great if you can answer a few questions uh we can get you signed up in the jiffy',
                                        lexical: 'great if you can answer a few questions uh we can get you signed up in the jiffy',
                                        text: 'Great. If you can answer a few questions, uh, we can get you signed up in the Jiffy.'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT15.99S'
                                },
                                {
                                    id: '5',
                                    redactedContent: {
                                        itn: 'OK',
                                        lexical: 'OK',
                                        text: 'OK.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT20.22S'
                                },
                                {
                                    id: '6',
                                    redactedContent: {
                                        itn: "so what's your full name",
                                        lexical: "so what's your full name",
                                        text: "So what's your full name?"
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT21.75S'
                                },
                                {
                                    id: '7',
                                    redactedContent: {
                                        itn:
                                            'so ********************************************************************************************************',
                                        lexical:
                                            'so *********************************************************************************************************',
                                        text:
                                            'So **********************************************************************************************************.'
                                    },
                                    entities: [
                                        {
                                            text:
                                                'Mary Beth Rondo last name is R like Romeo, O like ocean and like Nancy, DD like dog and O like ocean again',
                                            category: 'Name',
                                            offset: 3,
                                            length: 106,
                                            confidenceScore: 0.97
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT24.66S'
                                },
                                {
                                    id: '8',
                                    redactedContent: {
                                        itn: '*****',
                                        lexical: '*****',
                                        text: '*****.'
                                    },
                                    entities: [
                                        {
                                            text: 'Rondo',
                                            category: 'Name',
                                            offset: 0,
                                            length: 5,
                                            confidenceScore: 0.99
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT34.37S'
                                },
                                {
                                    id: '9',
                                    redactedContent: {
                                        itn: "got it and what's the best callback number in case we get disconnected",
                                        lexical: "got it and what's the best callback number in case we get disconnected",
                                        text: "Got it. And what's the best callback number in case we get disconnected?"
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT35.95S'
                                },
                                {
                                    id: '10',
                                    redactedContent: {
                                        itn: 'i only have a cell phone so i can give you that',
                                        lexical: 'i only have a cell phone so i can give you that',
                                        text: 'I only have a cell phone, so I can give you that.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT41.65S'
                                },
                                {
                                    id: '11',
                                    redactedContent: {
                                        itn: "yeah that'll be fine",
                                        lexical: "yeah that'll be fine",
                                        text: "Yeah, that'll be fine."
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT44.19S'
                                },
                                {
                                    id: '12',
                                    redactedContent: {
                                        itn: "sure so it's ********************",
                                        lexical: "sure so it's *********************************************************",
                                        text: "Sure. So it's ********************."
                                    },
                                    entities: [
                                        {
                                            text: '234554 and then 9312',
                                            category: 'Phone',
                                            offset: 14,
                                            length: 20,
                                            confidenceScore: 0.93
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT46.2S'
                                },
                                {
                                    id: '13',
                                    redactedContent: {
                                        itn: "got it so to confirm it's **********",
                                        lexical: "got it so to confirm it's ************************************************",
                                        text: "Got it. So to confirm, it's **********."
                                    },
                                    entities: [
                                        {
                                            text: '2345549312',
                                            category: 'Phone',
                                            offset: 28,
                                            length: 10,
                                            confidenceScore: 0.95
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT54.78S'
                                },
                                {
                                    id: '14',
                                    redactedContent: {
                                        itn: "yep that's right",
                                        lexical: "yep that's right",
                                        text: "Yep, that's right."
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M0.05S'
                                },
                                {
                                    id: '15',
                                    redactedContent: {
                                        itn:
                                            "excellent uh let's get some additional information from your app for your application uh do you have a job",
                                        lexical:
                                            "excellent uh let's get some additional information from your app for your application uh do you have a job",
                                        text:
                                            "Excellent. Uh, let's get some additional information from your app. For your application, uh, do you have a job?"
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M1.66S'
                                },
                                {
                                    id: '16',
                                    redactedContent: {
                                        itn: 'uh yes i am self employed',
                                        lexical: 'uh yes i am self employed',
                                        text: 'Uh, yes, I am self-employed.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M8.47S'
                                },
                                {
                                    id: '17',
                                    redactedContent: {
                                        itn: 'OK so then you have a social security number as well',
                                        lexical: 'OK so then you have a social security number as well',
                                        text: 'OK, so then you have a Social Security number as well?'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M11.19S'
                                },
                                {
                                    id: '18',
                                    redactedContent: {
                                        itn: 'i guess i do',
                                        lexical: 'i guess i do',
                                        text: 'I guess I do.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M14.6S'
                                },
                                {
                                    id: '19',
                                    redactedContent: {
                                        itn: 'OK and what is your social security number please',
                                        lexical: 'OK and what is your social security number please',
                                        text: 'OK. And what is your Social Security number please?'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M16.18S'
                                },
                                {
                                    id: '20',
                                    redactedContent: {
                                        itn: "uh sure so it's *** uh ******",
                                        lexical: "uh sure so it's ************ uh *****************************",
                                        text: "Uh, sure. So it's ***, uh ******."
                                    },
                                    entities: [
                                        {
                                            text: '412',
                                            category: 'CreditCard',
                                            offset: 18,
                                            length: 3,
                                            confidenceScore: 0.52
                                        },
                                        {
                                            text: '256789',
                                            category: 'CreditCard',
                                            offset: 26,
                                            length: 6,
                                            confidenceScore: 0.53
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT1M19.87S'
                                },
                                {
                                    id: '21',
                                    redactedContent: {
                                        itn: 'sorry what was that A-25 or A225 you cut out for a bit',
                                        lexical: 'sorry what was that a two five or a two two five you cut out for a bit',
                                        text: 'Sorry, what was that A-25 or A225 you cut out for a bit?'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M27.15S'
                                },
                                {
                                    id: '22',
                                    redactedContent: {
                                        itn: "uh it's 22 so",
                                        lexical: "uh it's double two so",
                                        text: "Uh, it's 22, so."
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M31.99S'
                                },
                                {
                                    id: '23',
                                    redactedContent: {
                                        itn: '412 then another two then five',
                                        lexical: 'four one two then another two then five',
                                        text: '412, then another two, then five.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT1M34.64S'
                                },
                                {
                                    id: '24',
                                    redactedContent: {
                                        itn: 'thank you so much and could i have your email address please',
                                        lexical: 'thank you so much and could i have your email address please',
                                        text: 'Thank you so much and could I have your e-mail address please?'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M38.52S'
                                },
                                {
                                    id: '25',
                                    redactedContent: {
                                        itn: "yeah it's **************************************************** ********************",
                                        lexical:
                                            "yeah it's ********************************************************************** ********************",
                                        text: "Yeah, it's *****************************************************. *********************."
                                    },
                                    entities: [
                                        {
                                            text: 'Mary rondo@gmail.com, so myfirstandlastname@gmail.com',
                                            category: 'Email',
                                            offset: 11,
                                            length: 53,
                                            confidenceScore: 0.98
                                        },
                                        {
                                            text: 'No periods, no dashes',
                                            category: 'Email',
                                            offset: 66,
                                            length: 21,
                                            confidenceScore: 0.85
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT1M44.33S'
                                },
                                {
                                    id: '26',
                                    redactedContent: {
                                        itn:
                                            "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about 24 hours or so to confirm your application",
                                        lexical:
                                            "great uh that is the last question so let me take your information and i'll be able to get you signed up right away thank you for calling contoso and i'll be able to get you signed up immediately one of our agents will call you back in about twenty four hours or so to confirm your application",
                                        text:
                                            "Great. Uh, that is the last question. So let me take your information and I'll be able to get you signed up right away. Thank you for calling Contoso, and I'll be able to get you signed up immediately. One of our agents will call you back in about 24 hours or so to confirm your application."
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M52.81S'
                                },
                                {
                                    id: '27',
                                    redactedContent: {
                                        itn: "that's great thank you",
                                        lexical: "that's great thank you",
                                        text: "That's great. Thank you."
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT2M8.81S'
                                },
                                {
                                    id: '28',
                                    redactedContent: {
                                        itn:
                                            'absolutely um if you need anything else please give us a call at ********************** thank you very much for calling contoso',
                                        lexical:
                                            'absolutely um if you need anything else please give us a call at *************************************************************************** thank you very much for calling contoso',
                                        text:
                                            'Absolutely. Um, if you need anything else, please give us a call at ***********************. Thank you very much for calling Contoso.'
                                    },
                                    entities: [
                                        {
                                            text: '1-800-555-5564, ext 123',
                                            category: 'Phone',
                                            offset: 68,
                                            length: 23,
                                            confidenceScore: 0.89
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT2M11.27S'
                                },
                                {
                                    id: '29',
                                    redactedContent: {
                                        itn: 'actually uh i have one more question',
                                        lexical: 'actually uh i have one more question',
                                        text: 'Actually, uh, I have one more question.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT2M22.2S'
                                },
                                {
                                    id: '30',
                                    redactedContent: {
                                        itn: 'uh yes of course',
                                        lexical: 'uh yes of course',
                                        text: 'Uh, yes, of course.'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT2M24.9S'
                                },
                                {
                                    id: '31',
                                    redactedContent: {
                                        itn: "i'm curious what i'd be getting a physical card as proof of coverage",
                                        lexical: "i'm curious what i'd be getting a physical card as proof of coverage",
                                        text: "I'm curious what I'd be getting a physical card as proof of coverage."
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT2M26.98S'
                                },
                                {
                                    id: '32',
                                    redactedContent: {
                                        itn:
                                            'so the default is a digital membership card but we can send you a physical card if you prefer',
                                        lexical:
                                            'so the default is a digital membership card but we can send you a physical card if you prefer',
                                        text:
                                            'So the default is a digital membership card, but we can send you a physical card if you prefer.'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT2M31.51S'
                                },
                                {
                                    id: '33',
                                    redactedContent: {
                                        itn:
                                            "uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address",
                                        lexical:
                                            "uh yes could you please mail it to me when it's ready i'd like to have it shipped to you for my address",
                                        text:
                                            "Uh, yes. Could you please mail it to me when it's ready? I'd like to have it shipped to you for my address."
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT2M37.78S'
                                },
                                {
                                    id: '34',
                                    redactedContent: {
                                        itn: 'uh yeah',
                                        lexical: 'uh yeah',
                                        text: 'Uh, yeah.'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT2M43.54S'
                                },
                                {
                                    id: '35',
                                    redactedContent: {
                                        itn: "uh so it's **************************************************************",
                                        lexical:
                                            "uh so it's *********************************************************************************************************",
                                        text: "Uh, so it's ****************************************************************."
                                    },
                                    entities: [
                                        {
                                            text: '2660, unit A on Maple Ave, SE Lansing and then ZIP code is 48823',
                                            category: 'Address',
                                            offset: 12,
                                            length: 64,
                                            confidenceScore: 0.97
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT2M45.02S'
                                },
                                {
                                    id: '36',
                                    redactedContent: {
                                        itn: "absolutely i've made a note on your file",
                                        lexical: "absolutely i've made a note on your file",
                                        text: "Absolutely. I've made a note on your file."
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT2M56.3S'
                                },
                                {
                                    id: '37',
                                    redactedContent: {
                                        itn: 'awesome thanks so much',
                                        lexical: 'awesome thanks so much',
                                        text: 'Awesome. Thanks so much.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT2M59.6S'
                                },
                                {
                                    id: '38',
                                    redactedContent: {
                                        itn: "you're very welcome thank you for calling contoso and have a great day",
                                        lexical: "you're very welcome thank you for calling contoso and have a great day",
                                        text: "You're very welcome. Thank you for calling Contoso and have a great day."
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT3M1.54S'
                                }
                            ]
                        }
                    ]
                }
            }
        }
    },
    {
        textInput: '',
        sampleType: 'audio',
        title: 'Apply for a loan',
        isSingleConversation: true,
        document: 'Customer’s initial call to inquire about applying for a loan.',
        audioLink: getAssetsUrl('assets/images/audio/Call6_mono_16k_az_apply_loan.wav'),
        output: {
            transcription: {
                source: '',
                timestamp: '2022-09-29T03:10:19Z',
                durationInTicks: 1824400000,
                duration: 'PT3M2.44S',
                combinedRecognizedPhrases: [
                    {
                        channel: 0,
                        lexical:
                            "good afternoon this is sam thank you for calling contessa how may i help hi there my name is mary i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan OK i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there and are you planning to sell your current home yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells i see um would you mind holding for a moment while i take your information down yeah no problem thank you for your help one moment alright thank you for your patience ma'am uh may i have your first and last name please yes my name is mary smith thank you miss smith may i have your current address please yes so my address is one two three main street in los angeles california and the zip code is nine zero nine two three sorry that was a nine zero what nine zero nine two three nine zero nine two three on main street got it thank you uh may i have your phone number as well please yes my phone number is five zero four five two nine two three five one and then five one got it um and uh do you have an email address um we i can associate with this application uh yes so my email address is mary dot A dot S M seventy eight at gmail dot com mary dot a was that a S N as in november or M as in mike uh M as in mike like seventy eight uh got it right thank you miss smith um do you currently have any other loans uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about fourteen hundred per month combined and my interest rate is eight percent i see and you're currently paying those loans off monthly is that right yes of course i do OK thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at WWW dot contoso loans dot com yeah that sounds good i can go ahead and get started thank you for your help thank you",
                        itn:
                            "good afternoon this is sam thank you for calling contessa how may i help hi there my name is mary i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan OK i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there and are you planning to sell your current home yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells i see um would you mind holding for a moment while i take your information down yeah no problem thank you for your help one moment alright thank you for your patience ma'am uh may i have your first and last name please yes my name is mary smith thank you miss smith may i have your current address please yes so my address is 123 main st in los angeles CA and the zip code is 90923 sorry that was a 90 what 90923 90923 on main street got it thank you uh may i have your phone number as well please yes my phone number is 504 5292351 and then 51 got it um and uh do you have an email address um we i can associate with this application uh yes so my email address is mary.a.sm78@gmail.com mary dot a was that a SN as in november or M as in mike uh M as in mike like 78 uh got it right thank you miss smith um do you currently have any other loans uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about 1400 per month combined and my interest rate is 8% i see and you're currently paying those loans off monthly is that right yes of course i do OK thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at www.contosoloans.com yeah that sounds good i can go ahead and get started thank you for your help thank you",
                        maskedITN:
                            "good afternoon this is sam thank you for calling contessa how may i help hi there my name is mary i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan ok i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there and are you planning to sell your current home yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells i see um would you mind holding for a moment while i take your information down yeah no problem thank you for your help one moment alright thank you for your patience ma'am uh may i have your first and last name please yes my name is mary smith thank you miss smith may i have your current address please yes so my address is 123 main st in los angeles ca and the zip code is 90923 sorry that was a 90 what 90923 90923 on main street got it thank you uh may i have your phone number as well please yes my phone number is 504 5292351 and then 51 got it um and uh do you have an e-mail address um we i can associate with this application uh yes so my e-mail address is mary.a.sm78@gmail.com mary dot a was that a sn as in november or m as in mike uh m as in mike like 78 uh got it right thank you miss smith um do you currently have any other loans uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about 1400 per month combined and my interest rate is 8% i see and you're currently paying those loans off monthly is that right yes of course i do ok thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at www.contosoloans.com yeah that sounds good i can go ahead and get started thank you for your help thank you",
                        display:
                            "Good afternoon. This is Sam. Thank you for calling Contessa. How may I help? Hi there, my name is Mary. I'm currently living in Los Angeles, but I'm planning to move to Las Vegas and I would like to apply for a loan. OK, I see you're living in California. Let me make sure I understand you correctly. Uh, you'd like to apply for a loan, even though you'll be moving soon, is that right? Yes, exactly. So I'm planning to relocate soon, but I would like to apply for the loan first so that I can purchase a new home once I move there. And are you planning to sell your current home? Yes, I will be listing it on the market soon and hopefully it'll sell quickly. That's why I'm applying for a loan now so that I can purchase a new house in Nevada and close on it quickly as well once my current home sells. I see. Um, would you mind holding for a moment while I take your information down? Yeah, no problem. Thank you for your help. One moment. Alright, thank you for your patience, ma'am. Uh, may I have your first and last name please? Yes, my name is Mary Smith. Thank you, Miss Smith. May I have your current address please? Yes, so my address is 123 Main St in Los Angeles, CA and the ZIP code is 90923. Sorry, that was a 90. What? 90923. 90923 on Main Street. Got it. Thank you. Uh, may I have your phone number as well, please? Yes, my phone number is 504. 5292351 and then? 51. Got it. Um and uh, do you have an e-mail address? Um, we I can associate with this application? Uh, yes. So my e-mail address is mary.a.sm78@gmail.com. Mary dot a. Was that a SN as in November or M as in Mike? Uh, M as in Mike. Like 78? Uh, got it. Right. Thank you, Miss Smith. Um, do you currently have any other loans? Uh, yes. So I currently have two other loans through CONTOSO. Uh, so my first one is my car loan and then my other is my student loan. Uh. They total about 1400 per month combined and my interest rate is 8%. I see. And you're currently paying those loans off monthly, is that right? Yes, of course I do. OK, thank you. Umm, here's what I suggest we do. Uh, let me place you on a brief hold again so I can talk with one of my loan officers and get this started for you immediately. And in the meantime, uh would be great if you could take a few minutes and complete the remainder of the secure application online at www.contosoloans.com. Yeah, that sounds good. I can go ahead and get started. Thank you for your help. Thank you."
                    }
                ],
                recognizedPhrases: [
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT0.99S',
                        duration: 'PT3.01S',
                        offsetInTicks: 9900000.0,
                        durationInTicks: 30100000.0,
                        nBest: [
                            {
                                confidence: 0.8169576,
                                lexical: 'good afternoon this is sam thank you for calling contessa how may i help',
                                itn: 'good afternoon this is sam thank you for calling contessa how may i help',
                                maskedITN: 'good afternoon this is sam thank you for calling contessa how may i help',
                                display: 'Good afternoon. This is Sam. Thank you for calling Contessa. How may I help?',
                                sentiment: {
                                    positive: 0.72,
                                    neutral: 0.26,
                                    negative: 0.02
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT5.11S',
                        duration: 'PT5.58S',
                        offsetInTicks: 51100000.0,
                        durationInTicks: 55800000.0,
                        nBest: [
                            {
                                confidence: 0.90344167,
                                lexical:
                                    "hi there my name is mary i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan",
                                itn:
                                    "hi there my name is mary i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan",
                                maskedITN:
                                    "hi there my name is mary i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan",
                                display:
                                    "Hi there, my name is Mary. I'm currently living in Los Angeles, but I'm planning to move to Las Vegas and I would like to apply for a loan.",
                                sentiment: {
                                    positive: 0.1,
                                    neutral: 0.87,
                                    negative: 0.02
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT12.64S',
                        duration: 'PT7.61S',
                        offsetInTicks: 126400000.0,
                        durationInTicks: 76100000.0,
                        nBest: [
                            {
                                confidence: 0.8522715,
                                lexical:
                                    "OK i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right",
                                itn:
                                    "OK i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right",
                                maskedITN:
                                    "ok i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right",
                                display:
                                    "OK, I see you're living in California. Let me make sure I understand you correctly. Uh, you'd like to apply for a loan, even though you'll be moving soon, is that right?",
                                sentiment: {
                                    positive: 0.13,
                                    neutral: 0.84,
                                    negative: 0.03
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT21.46S',
                        duration: 'PT5.9S',
                        offsetInTicks: 214600000.0,
                        durationInTicks: 59000000.0,
                        nBest: [
                            {
                                confidence: 0.85348654,
                                lexical:
                                    "yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there",
                                itn:
                                    "yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there",
                                maskedITN:
                                    "yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there",
                                display:
                                    "Yes, exactly. So I'm planning to relocate soon, but I would like to apply for the loan first so that I can purchase a new home once I move there.",
                                sentiment: {
                                    positive: 0.19,
                                    neutral: 0.76,
                                    negative: 0.05
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT28.95S',
                        duration: 'PT1.96S',
                        offsetInTicks: 289500000.0,
                        durationInTicks: 19600000.0,
                        nBest: [
                            {
                                confidence: 0.86648816,
                                lexical: 'and are you planning to sell your current home',
                                itn: 'and are you planning to sell your current home',
                                maskedITN: 'and are you planning to sell your current home',
                                display: 'And are you planning to sell your current home?',
                                sentiment: {
                                    positive: 0.03,
                                    neutral: 0.95,
                                    negative: 0.02
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT32.12S',
                        duration: 'PT9.06S',
                        offsetInTicks: 321200000.0,
                        durationInTicks: 90600000.0,
                        nBest: [
                            {
                                confidence: 0.8375811,
                                lexical:
                                    "yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells",
                                itn:
                                    "yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells",
                                maskedITN:
                                    "yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells",
                                display:
                                    "Yes, I will be listing it on the market soon and hopefully it'll sell quickly. That's why I'm applying for a loan now so that I can purchase a new house in Nevada and close on it quickly as well once my current home sells.",
                                sentiment: {
                                    positive: 0.63,
                                    neutral: 0.35,
                                    negative: 0.03
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT42.65S',
                        duration: 'PT3.73S',
                        offsetInTicks: 426500000.0,
                        durationInTicks: 37300000.0,
                        nBest: [
                            {
                                confidence: 0.78709954,
                                lexical: 'i see um would you mind holding for a moment while i take your information down',
                                itn: 'i see um would you mind holding for a moment while i take your information down',
                                maskedITN: 'i see um would you mind holding for a moment while i take your information down',
                                display: 'I see. Um, would you mind holding for a moment while I take your information down?',
                                sentiment: {
                                    positive: 0.03,
                                    neutral: 0.93,
                                    negative: 0.03
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT47.31S',
                        duration: 'PT1.65S',
                        offsetInTicks: 473100000.0,
                        durationInTicks: 16500000.0,
                        nBest: [
                            {
                                confidence: 0.93808013,
                                lexical: 'yeah no problem thank you for your help',
                                itn: 'yeah no problem thank you for your help',
                                maskedITN: 'yeah no problem thank you for your help',
                                display: 'Yeah, no problem. Thank you for your help.',
                                sentiment: {
                                    positive: 0.97,
                                    neutral: 0.02,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT50.75S',
                        duration: 'PT1.02S',
                        offsetInTicks: 507500000.0,
                        durationInTicks: 10200000.0,
                        nBest: [
                            {
                                confidence: 0.024596669,
                                lexical: 'one moment',
                                itn: 'one moment',
                                maskedITN: 'one moment',
                                display: 'One moment.',
                                sentiment: {
                                    positive: 0.05,
                                    neutral: 0.9,
                                    negative: 0.04
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT58.84S',
                        duration: 'PT3.83S',
                        offsetInTicks: 588400000.0,
                        durationInTicks: 38300000.0,
                        nBest: [
                            {
                                confidence: 0.9088119,
                                lexical: "alright thank you for your patience ma'am uh may i have your first and last name please",
                                itn: "alright thank you for your patience ma'am uh may i have your first and last name please",
                                maskedITN: "alright thank you for your patience ma'am uh may i have your first and last name please",
                                display: "Alright, thank you for your patience, ma'am. Uh, may I have your first and last name please?",
                                sentiment: {
                                    positive: 0.95,
                                    neutral: 0.04,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT1M3.61S',
                        duration: 'PT2.11S',
                        offsetInTicks: 636100000.0,
                        durationInTicks: 21100000.0,
                        nBest: [
                            {
                                confidence: 0.8887075,
                                lexical: 'yes my name is mary smith',
                                itn: 'yes my name is mary smith',
                                maskedITN: 'yes my name is mary smith',
                                display: 'Yes, my name is Mary Smith.',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.98,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT1M7.62S',
                        duration: 'PT2.97S',
                        offsetInTicks: 676200000.0,
                        durationInTicks: 29700000.0,
                        nBest: [
                            {
                                confidence: 0.8426259,
                                lexical: 'thank you miss smith may i have your current address please',
                                itn: 'thank you miss smith may i have your current address please',
                                maskedITN: 'thank you miss smith may i have your current address please',
                                display: 'Thank you, Miss Smith. May I have your current address please?',
                                sentiment: {
                                    positive: 0.95,
                                    neutral: 0.04,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT1M11.54S',
                        duration: 'PT7.15S',
                        offsetInTicks: 715400000.0,
                        durationInTicks: 71500000.0,
                        nBest: [
                            {
                                confidence: 0.9402247,
                                lexical:
                                    'yes so my address is one two three main street in los angeles california and the zip code is nine zero nine two three',
                                itn: 'yes so my address is 123 main st in los angeles CA and the zip code is 90923',
                                maskedITN: 'yes so my address is 123 main st in los angeles ca and the zip code is 90923',
                                display: 'Yes, so my address is 123 Main St in Los Angeles, CA and the ZIP code is 90923.',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.98,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT1M20.54S',
                        duration: 'PT2.38S',
                        offsetInTicks: 805400000.0,
                        durationInTicks: 23800000.0,
                        nBest: [
                            {
                                confidence: 0.7568138,
                                lexical: 'sorry that was a nine zero what',
                                itn: 'sorry that was a 90 what',
                                maskedITN: 'sorry that was a 90 what',
                                display: 'Sorry, that was a 90. What?',
                                sentiment: {
                                    positive: 0.0,
                                    neutral: 0.02,
                                    negative: 0.98
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT1M24.19S',
                        duration: 'PT2.04S',
                        offsetInTicks: 841900000.0,
                        durationInTicks: 20400000.0,
                        nBest: [
                            {
                                confidence: 0.87473255,
                                lexical: 'nine zero nine two three',
                                itn: '90923',
                                maskedITN: '90923',
                                display: '90923.',
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.96,
                                    negative: 0.02
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT1M27.63S',
                        duration: 'PT6.17S',
                        offsetInTicks: 876300000.0,
                        durationInTicks: 61700000.0,
                        nBest: [
                            {
                                confidence: 0.68036044,
                                lexical:
                                    'nine zero nine two three on main street got it thank you uh may i have your phone number as well please',
                                itn: '90923 on main street got it thank you uh may i have your phone number as well please',
                                maskedITN: '90923 on main street got it thank you uh may i have your phone number as well please',
                                display: '90923 on Main Street. Got it. Thank you. Uh, may I have your phone number as well, please?',
                                sentiment: {
                                    positive: 0.72,
                                    neutral: 0.23,
                                    negative: 0.05
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT1M34.91S',
                        duration: 'PT2.75S',
                        offsetInTicks: 949100000.0,
                        durationInTicks: 27500000.0,
                        nBest: [
                            {
                                confidence: 0.947477,
                                lexical: 'yes my phone number is five zero four',
                                itn: 'yes my phone number is 504',
                                maskedITN: 'yes my phone number is 504',
                                display: 'Yes, my phone number is 504.',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.99,
                                    negative: 0.0
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT1M38.35S',
                        duration: 'PT4.25S',
                        offsetInTicks: 983500000.0,
                        durationInTicks: 42500000.0,
                        nBest: [
                            {
                                confidence: 0.79971415,
                                lexical: 'five two nine two three five one and then',
                                itn: '5292351 and then',
                                maskedITN: '5292351 and then',
                                display: '5292351 and then?',
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.97,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT1M44.15S',
                        duration: 'PT0.97S',
                        offsetInTicks: 1041500000.0,
                        durationInTicks: 9700000.0,
                        nBest: [
                            {
                                confidence: 0.24136835,
                                lexical: 'five one',
                                itn: '51',
                                maskedITN: '51',
                                display: '51.',
                                sentiment: {
                                    positive: 0.07,
                                    neutral: 0.86,
                                    negative: 0.07
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT1M46.61S',
                        duration: 'PT5.95S',
                        offsetInTicks: 1066100000.0,
                        durationInTicks: 59500000.0,
                        nBest: [
                            {
                                confidence: 0.74738973,
                                lexical: 'got it um and uh do you have an email address um we i can associate with this application',
                                itn: 'got it um and uh do you have an email address um we i can associate with this application',
                                maskedITN: 'got it um and uh do you have an e-mail address um we i can associate with this application',
                                display: 'Got it. Um and uh, do you have an e-mail address? Um, we I can associate with this application?',
                                sentiment: {
                                    positive: 0.07,
                                    neutral: 0.91,
                                    negative: 0.02
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT1M53.54S',
                        duration: 'PT7.47S',
                        offsetInTicks: 1135400000.0,
                        durationInTicks: 74700000.0,
                        nBest: [
                            {
                                confidence: 0.8758763,
                                lexical: 'uh yes so my email address is mary dot A dot S M seventy eight at gmail dot com',
                                itn: 'uh yes so my email address is mary.a.sm78@gmail.com',
                                maskedITN: 'uh yes so my e-mail address is mary.a.sm78@gmail.com',
                                display: 'Uh, yes. So my e-mail address is mary.a.sm78@gmail.com.',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.98,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT2M2.65S',
                        duration: 'PT5.68S',
                        offsetInTicks: 1226500000.0,
                        durationInTicks: 56800000.0,
                        nBest: [
                            {
                                confidence: 0.68149215,
                                lexical: 'mary dot a was that a S N as in november or M as in mike',
                                itn: 'mary dot a was that a SN as in november or M as in mike',
                                maskedITN: 'mary dot a was that a sn as in november or m as in mike',
                                display: 'Mary dot a. Was that a SN as in November or M as in Mike?',
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.97,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT2M9.24S',
                        duration: 'PT1.18S',
                        offsetInTicks: 1292400000.0,
                        durationInTicks: 11800000.0,
                        nBest: [
                            {
                                confidence: 0.6924885,
                                lexical: 'uh M as in mike',
                                itn: 'uh M as in mike',
                                maskedITN: 'uh m as in mike',
                                display: 'Uh, M as in Mike.',
                                sentiment: {
                                    positive: 0.01,
                                    neutral: 0.98,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT2M11.42S',
                        duration: 'PT6.55S',
                        offsetInTicks: 1314200000.0,
                        durationInTicks: 65500000.0,
                        nBest: [
                            {
                                confidence: 0.7034824,
                                lexical: 'like seventy eight uh got it right thank you miss smith um do you currently have any other loans',
                                itn: 'like 78 uh got it right thank you miss smith um do you currently have any other loans',
                                maskedITN: 'like 78 uh got it right thank you miss smith um do you currently have any other loans',
                                display: 'Like 78? Uh, got it. Right. Thank you, Miss Smith. Um, do you currently have any other loans?',
                                sentiment: {
                                    positive: 0.95,
                                    neutral: 0.04,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT2M19.29S',
                        duration: 'PT11.82S',
                        offsetInTicks: 1392900000.0,
                        durationInTicks: 118200000.0,
                        nBest: [
                            {
                                confidence: 0.84657955,
                                lexical:
                                    'uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about fourteen hundred per month combined and my interest rate is eight percent',
                                itn:
                                    'uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about 1400 per month combined and my interest rate is 8%',
                                maskedITN:
                                    'uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about 1400 per month combined and my interest rate is 8%',
                                display:
                                    'Uh, yes. So I currently have two other loans through CONTOSO. Uh, so my first one is my car loan and then my other is my student loan. Uh. They total about 1400 per month combined and my interest rate is 8%.',
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.96,
                                    negative: 0.02
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT2M32.58S',
                        duration: 'PT2.87S',
                        offsetInTicks: 1525800000.0,
                        durationInTicks: 28700000.0,
                        nBest: [
                            {
                                confidence: 0.81369615,
                                lexical: "i see and you're currently paying those loans off monthly is that right",
                                itn: "i see and you're currently paying those loans off monthly is that right",
                                maskedITN: "i see and you're currently paying those loans off monthly is that right",
                                display: "I see. And you're currently paying those loans off monthly, is that right?",
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.96,
                                    negative: 0.02
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT2M36.46S',
                        duration: 'PT1.13S',
                        offsetInTicks: 1564600000.0,
                        durationInTicks: 11300000.0,
                        nBest: [
                            {
                                confidence: 0.8240527,
                                lexical: 'yes of course i do',
                                itn: 'yes of course i do',
                                maskedITN: 'yes of course i do',
                                display: 'Yes, of course I do.',
                                sentiment: {
                                    positive: 0.02,
                                    neutral: 0.98,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT2M39.32S',
                        duration: 'PT18.19S',
                        offsetInTicks: 1593200000.0,
                        durationInTicks: 181900000.0,
                        nBest: [
                            {
                                confidence: 0.79872763,
                                lexical:
                                    "OK thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at WWW dot contoso loans dot com",
                                itn:
                                    "OK thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at www.contosoloans.com",
                                maskedITN:
                                    "ok thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at www.contosoloans.com",
                                display:
                                    "OK, thank you. Umm, here's what I suggest we do. Uh, let me place you on a brief hold again so I can talk with one of my loan officers and get this started for you immediately. And in the meantime, uh would be great if you could take a few minutes and complete the remainder of the secure application online at www.contosoloans.com.",
                                sentiment: {
                                    positive: 0.98,
                                    neutral: 0.01,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 2,
                        offset: 'PT2M58.53S',
                        duration: 'PT2.91S',
                        offsetInTicks: 1785300000.0,
                        durationInTicks: 29100000.0,
                        nBest: [
                            {
                                confidence: 0.8605536,
                                lexical: 'yeah that sounds good i can go ahead and get started thank you for your help',
                                itn: 'yeah that sounds good i can go ahead and get started thank you for your help',
                                maskedITN: 'yeah that sounds good i can go ahead and get started thank you for your help',
                                display: 'Yeah, that sounds good. I can go ahead and get started. Thank you for your help.',
                                sentiment: {
                                    positive: 0.96,
                                    neutral: 0.03,
                                    negative: 0.01
                                }
                            }
                        ]
                    },
                    {
                        recognitionStatus: 'Success',
                        channel: 0,
                        speaker: 1,
                        offset: 'PT3M1.97S',
                        duration: 'PT0.43S',
                        offsetInTicks: 1819700000.0,
                        durationInTicks: 4300000.0,
                        nBest: [
                            {
                                confidence: 0.8605536,
                                lexical: 'thank you',
                                itn: 'thank you',
                                maskedITN: 'thank you',
                                display: 'Thank you.',
                                sentiment: {
                                    positive: 0.72,
                                    neutral: 0.23,
                                    negative: 0.05
                                }
                            }
                        ]
                    }
                ]
            },
            conversationAnalyticsResults: {
                conversationSummaryResults: {
                    conversations: [
                        {
                            id: 'conversation1',
                            summaries: [
                                {
                                    aspect: 'issue',
                                    text: 'Customer wants to apply for a loan'
                                },
                                {
                                    aspect: 'resolution',
                                    text:
                                        'Asked for customer information and then asked for e-mail address | Asked for the phone number | Placed customer on a brief hold again'
                                }
                            ]
                        }
                    ]
                },
                conversationPiiResults: {
                    combinedRedactedContent: [
                        {
                            channel: '0',
                            display:
                                "Good afternoon. This is ***. Thank you for calling ********. How may I help? OK, I see you're living in California. Let me make sure I understand you correctly. Uh, you'd like to apply for a loan, even though you'll be moving soon, is that right? And are you planning to sell your current home? I see. Um, would you mind holding for a moment while I take your information down? One moment. Alright, thank you for your patience, ma'am. Uh, may I have your first and last name please? Thank you, Miss *****. May I have your current address please? ********************. ****? ********************. Got it. Thank you. Uh, may I have your phone number as well, please? **. Got it. Um and uh, do you have an e-mail address? Um, we I can associate with this application? **********. ********************************************? *******? Uh, got it. Right. Thank you, Miss *****. Um, do you currently have any other loans? I see. And you're currently paying those loans off monthly, is that right? OK, thank you. Umm, here's what I suggest we do. Uh, let me place you on a brief hold again so I can talk with one of my loan officers and get this started for you immediately. And in the meantime, uh would be great if you could take a few minutes and complete the remainder of the secure application online at ********************. Thank you. ",
                            itn:
                                "good afternoon this is *** thank you for calling ******** how may i help OK i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right and are you planning to sell your current home i see um would you mind holding for a moment while i take your information down one moment alright thank you for your patience ma'am uh may i have your first and last name please thank you miss ***** may i have your current address please ******************* **** ******************** got it thank you uh may i have your phone number as well please ** got it um and uh do you have an email address um we i can associate with this application ********** ******************************************** ******* uh got it right thank you miss ***** um do you currently have any other loans i see and you're currently paying those loans off monthly is that right OK thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at ******************** thank you ",
                            lexical:
                                "good afternoon this is *** thank you for calling ******** how may i help OK i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right and are you planning to sell your current home i see um would you mind holding for a moment while i take your information down one moment alright thank you for your patience ma'am uh may i have your first and last name please thank you miss ***** may i have your current address please ************************** **** *************************************** got it thank you uh may i have your phone number as well please ******** got it um and uh do you have an email address um we i can associate with this application ********** ********************************************* ****************** uh got it right thank you miss ***** um do you currently have any other loans i see and you're currently paying those loans off monthly is that right OK thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at ***************************** thank you "
                        },
                        {
                            channel: '1',
                            display:
                                "Hi there, my name is ****. I'm currently living in Los Angeles, but I'm planning to move to Las Vegas and I would like to apply for a loan. Yes, exactly. So I'm planning to relocate soon, but I would like to apply for the loan first so that I can purchase a new home once I move there. Yes, I will be listing it on the market soon and hopefully it'll sell quickly. That's why I'm applying for a loan now so that I can purchase a new house in Nevada and close on it quickly as well once my current home sells. Yeah, no problem. Thank you for your help. Yes, my name is **********. Yes, so my address is ********************************************************* *****. Yes, my phone number is *04. ****************? Uh, yes. So my e-mail address is *********************. ****************. Uh, yes. So I currently have two other loans through CONTOSO. Uh, so my first one is my car loan and then my other is my student loan. Uh. They total about 1400 per month combined and my interest rate is 8%. Yes, of course I do. Yeah, that sounds good. I can go ahead and get started. Thank you for your help. ",
                            itn:
                                "hi there my name is **** i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells yeah no problem thank you for your help yes my name is ********** yes so my address is ******************************************************* ***** yes my phone number is *** **************** uh yes so my email address is ********************* *************** uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about 1400 per month combined and my interest rate is 8% yes of course i do yeah that sounds good i can go ahead and get started thank you for your help ",
                            lexical:
                                "hi there my name is **** i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells yeah no problem thank you for your help yes my name is ********** yes so my address is ************************************************************************************************ ************************ yes my phone number is ************** ***************************************** uh yes so my email address is ************************************************* *************** uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about fourteen hundred per month combined and my interest rate is eight percent yes of course i do yeah that sounds good i can go ahead and get started thank you for your help "
                        }
                    ],
                    conversations: [
                        {
                            id: 'conversation1',
                            conversationItems: [
                                {
                                    id: '0',
                                    redactedContent: {
                                        itn: 'good afternoon this is *** thank you for calling ******** how may i help',
                                        lexical: 'good afternoon this is *** thank you for calling ******** how may i help',
                                        text: 'Good afternoon. This is ***. Thank you for calling ********. How may I help?'
                                    },
                                    entities: [
                                        {
                                            text: 'Sam',
                                            category: 'Name',
                                            offset: 24,
                                            length: 3,
                                            confidenceScore: 0.95
                                        },
                                        {
                                            text: 'Contessa',
                                            category: 'Name',
                                            offset: 51,
                                            length: 8,
                                            confidenceScore: 0.89
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT0.99S'
                                },
                                {
                                    id: '1',
                                    redactedContent: {
                                        itn:
                                            "hi there my name is **** i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan",
                                        lexical:
                                            "hi there my name is **** i'm currently living in los angeles but i'm planning to move to las vegas and i would like to apply for a loan",
                                        text:
                                            "Hi there, my name is ****. I'm currently living in Los Angeles, but I'm planning to move to Las Vegas and I would like to apply for a loan."
                                    },
                                    entities: [
                                        {
                                            text: 'Mary',
                                            category: 'Name',
                                            offset: 21,
                                            length: 4,
                                            confidenceScore: 0.96
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT5.11S'
                                },
                                {
                                    id: '2',
                                    redactedContent: {
                                        itn:
                                            "OK i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right",
                                        lexical:
                                            "OK i see you're living in california let me make sure i understand you correctly uh you'd like to apply for a loan even though you'll be moving soon is that right",
                                        text:
                                            "OK, I see you're living in California. Let me make sure I understand you correctly. Uh, you'd like to apply for a loan, even though you'll be moving soon, is that right?"
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT12.64S'
                                },
                                {
                                    id: '3',
                                    redactedContent: {
                                        itn:
                                            "yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there",
                                        lexical:
                                            "yes exactly so i'm planning to relocate soon but i would like to apply for the loan first so that i can purchase a new home once i move there",
                                        text:
                                            "Yes, exactly. So I'm planning to relocate soon, but I would like to apply for the loan first so that I can purchase a new home once I move there."
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT21.46S'
                                },
                                {
                                    id: '4',
                                    redactedContent: {
                                        itn: 'and are you planning to sell your current home',
                                        lexical: 'and are you planning to sell your current home',
                                        text: 'And are you planning to sell your current home?'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT28.95S'
                                },
                                {
                                    id: '5',
                                    redactedContent: {
                                        itn:
                                            "yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells",
                                        lexical:
                                            "yes i will be listing it on the market soon and hopefully it'll sell quickly that's why i'm applying for a loan now so that i can purchase a new house in nevada and close on it quickly as well once my current home sells",
                                        text:
                                            "Yes, I will be listing it on the market soon and hopefully it'll sell quickly. That's why I'm applying for a loan now so that I can purchase a new house in Nevada and close on it quickly as well once my current home sells."
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT32.12S'
                                },
                                {
                                    id: '6',
                                    redactedContent: {
                                        itn: 'i see um would you mind holding for a moment while i take your information down',
                                        lexical: 'i see um would you mind holding for a moment while i take your information down',
                                        text: 'I see. Um, would you mind holding for a moment while I take your information down?'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT42.65S'
                                },
                                {
                                    id: '7',
                                    redactedContent: {
                                        itn: 'yeah no problem thank you for your help',
                                        lexical: 'yeah no problem thank you for your help',
                                        text: 'Yeah, no problem. Thank you for your help.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT47.31S'
                                },
                                {
                                    id: '8',
                                    redactedContent: {
                                        itn: 'one moment',
                                        lexical: 'one moment',
                                        text: 'One moment.'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT50.75S'
                                },
                                {
                                    id: '9',
                                    redactedContent: {
                                        itn: "alright thank you for your patience ma'am uh may i have your first and last name please",
                                        lexical: "alright thank you for your patience ma'am uh may i have your first and last name please",
                                        text: "Alright, thank you for your patience, ma'am. Uh, may I have your first and last name please?"
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT58.84S'
                                },
                                {
                                    id: '10',
                                    redactedContent: {
                                        itn: 'yes my name is **********',
                                        lexical: 'yes my name is **********',
                                        text: 'Yes, my name is **********.'
                                    },
                                    entities: [
                                        {
                                            text: 'Mary Smith',
                                            category: 'Name',
                                            offset: 16,
                                            length: 10,
                                            confidenceScore: 0.98
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT1M3.61S'
                                },
                                {
                                    id: '11',
                                    redactedContent: {
                                        itn: 'thank you miss ***** may i have your current address please',
                                        lexical: 'thank you miss ***** may i have your current address please',
                                        text: 'Thank you, Miss *****. May I have your current address please?'
                                    },
                                    entities: [
                                        {
                                            text: 'Smith',
                                            category: 'Name',
                                            offset: 16,
                                            length: 5,
                                            confidenceScore: 0.95
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT1M7.62S'
                                },
                                {
                                    id: '12',
                                    redactedContent: {
                                        itn: 'yes so my address is *******************************************************',
                                        lexical:
                                            'yes so my address is ************************************************************************************************',
                                        text: 'Yes, so my address is *********************************************************'
                                    },
                                    entities: [
                                        {
                                            text: '123 Main St in Los Angeles, CA and the ZIP code is 90923.',
                                            category: 'Address',
                                            offset: 22,
                                            length: 57,
                                            confidenceScore: 0.82
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT1M11.54S'
                                },
                                {
                                    id: '13',
                                    redactedContent: {
                                        itn: '******************* ****',
                                        lexical: '************************** ****',
                                        text: '********************. ****?'
                                    },
                                    entities: [
                                        {
                                            text: 'Sorry, that was a 90',
                                            category: 'Address',
                                            offset: 0,
                                            length: 20,
                                            confidenceScore: 0.82
                                        },
                                        {
                                            text: 'What',
                                            category: 'Address',
                                            offset: 22,
                                            length: 4,
                                            confidenceScore: 0.93
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT1M20.54S'
                                },
                                {
                                    id: '14',
                                    redactedContent: {
                                        itn: '*****',
                                        lexical: '************************',
                                        text: '*****.'
                                    },
                                    entities: [
                                        {
                                            text: '90923',
                                            category: 'Address',
                                            offset: 0,
                                            length: 5,
                                            confidenceScore: 0.99
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT1M24.19S'
                                },
                                {
                                    id: '15',
                                    redactedContent: {
                                        itn: '******************** got it thank you uh may i have your phone number as well please',
                                        lexical:
                                            '*************************************** got it thank you uh may i have your phone number as well please',
                                        text: '********************. Got it. Thank you. Uh, may I have your phone number as well, please?'
                                    },
                                    entities: [
                                        {
                                            text: '90923 on Main Street',
                                            category: 'Address',
                                            offset: 0,
                                            length: 20,
                                            confidenceScore: 0.99
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT1M27.63S'
                                },
                                {
                                    id: '16',
                                    redactedContent: {
                                        itn: 'yes my phone number is ***',
                                        lexical: 'yes my phone number is **************',
                                        text: 'Yes, my phone number is *04.'
                                    },
                                    entities: [
                                        {
                                            text: '5',
                                            category: 'Phone',
                                            offset: 24,
                                            length: 1,
                                            confidenceScore: 0.98
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT1M34.91S'
                                },
                                {
                                    id: '17',
                                    redactedContent: {
                                        itn: '****************',
                                        lexical: '*****************************************',
                                        text: '****************?'
                                    },
                                    entities: [
                                        {
                                            text: '5292351 and then',
                                            category: 'Phone',
                                            offset: 0,
                                            length: 16,
                                            confidenceScore: 0.97
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT1M38.35S'
                                },
                                {
                                    id: '18',
                                    redactedContent: {
                                        itn: '**',
                                        lexical: '********',
                                        text: '**.'
                                    },
                                    entities: [
                                        {
                                            text: '52',
                                            category: 'Phone',
                                            offset: 0,
                                            length: 2,
                                            confidenceScore: 0.98
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT1M44.15S'
                                },
                                {
                                    id: '19',
                                    redactedContent: {
                                        itn: 'got it um and uh do you have an email address um we i can associate with this application',
                                        lexical:
                                            'got it um and uh do you have an email address um we i can associate with this application',
                                        text:
                                            'Got it. Um and uh, do you have an e-mail address? Um, we I can associate with this application?'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT1M46.61S'
                                },
                                {
                                    id: '20',
                                    redactedContent: {
                                        itn: 'uh yes so my email address is *********************',
                                        lexical: 'uh yes so my email address is *************************************************',
                                        text: 'Uh, yes. So my e-mail address is *********************.'
                                    },
                                    entities: [
                                        {
                                            text: 'mary.a.sm78@gmail.com',
                                            category: 'Email',
                                            offset: 33,
                                            length: 21,
                                            confidenceScore: 0.99
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT1M53.54S'
                                },
                                {
                                    id: '21',
                                    redactedContent: {
                                        itn: '********** ********************************************',
                                        lexical: '********** *********************************************',
                                        text: '**********. ********************************************?'
                                    },
                                    entities: [
                                        {
                                            text: 'Mary dot a',
                                            category: 'Email',
                                            offset: 0,
                                            length: 10,
                                            confidenceScore: 0.98
                                        },
                                        {
                                            text: 'Was that a SN as in November or M as in Mike',
                                            category: 'Email',
                                            offset: 12,
                                            length: 44,
                                            confidenceScore: 0.97
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT2M2.65S'
                                },
                                {
                                    id: '22',
                                    redactedContent: {
                                        itn: '***************',
                                        lexical: '***************',
                                        text: '****************.'
                                    },
                                    entities: [
                                        {
                                            text: 'Uh, M as in Mike',
                                            category: 'Email',
                                            offset: 0,
                                            length: 16,
                                            confidenceScore: 0.98
                                        }
                                    ],
                                    channel: '1',
                                    offset: 'PT2M9.24S'
                                },
                                {
                                    id: '23',
                                    redactedContent: {
                                        itn: '******* uh got it right thank you miss ***** um do you currently have any other loans',
                                        lexical:
                                            '****************** uh got it right thank you miss ***** um do you currently have any other loans',
                                        text:
                                            '*******? Uh, got it. Right. Thank you, Miss *****. Um, do you currently have any other loans?'
                                    },
                                    entities: [
                                        {
                                            text: 'Like 78',
                                            category: 'Email',
                                            offset: 0,
                                            length: 7,
                                            confidenceScore: 0.98
                                        },
                                        {
                                            text: 'Smith',
                                            category: 'Name',
                                            offset: 44,
                                            length: 5,
                                            confidenceScore: 0.93
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT2M11.42S'
                                },
                                {
                                    id: '24',
                                    redactedContent: {
                                        itn:
                                            'uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about 1400 per month combined and my interest rate is 8%',
                                        lexical:
                                            'uh yes so i currently have two other loans through contoso uh so my first one is my car loan and then my other is my student loan uh they total about fourteen hundred per month combined and my interest rate is eight percent',
                                        text:
                                            'Uh, yes. So I currently have two other loans through CONTOSO. Uh, so my first one is my car loan and then my other is my student loan. Uh. They total about 1400 per month combined and my interest rate is 8%.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT2M19.29S'
                                },
                                {
                                    id: '25',
                                    redactedContent: {
                                        itn: "i see and you're currently paying those loans off monthly is that right",
                                        lexical: "i see and you're currently paying those loans off monthly is that right",
                                        text: "I see. And you're currently paying those loans off monthly, is that right?"
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT2M32.58S'
                                },
                                {
                                    id: '26',
                                    redactedContent: {
                                        itn: 'yes of course i do',
                                        lexical: 'yes of course i do',
                                        text: 'Yes, of course I do.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT2M36.46S'
                                },
                                {
                                    id: '27',
                                    redactedContent: {
                                        itn:
                                            "OK thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at ********************",
                                        lexical:
                                            "OK thank you umm here's what i suggest we do uh let me place you on a brief hold again so i can talk with one of my loan officers and get this started for you immediately and in the meantime uh would be great if you could take a few minutes and complete the remainder of the secure application online at *****************************",
                                        text:
                                            "OK, thank you. Umm, here's what I suggest we do. Uh, let me place you on a brief hold again so I can talk with one of my loan officers and get this started for you immediately. And in the meantime, uh would be great if you could take a few minutes and complete the remainder of the secure application online at ********************."
                                    },
                                    entities: [
                                        {
                                            text: 'www.contosoloans.com',
                                            category: 'Email',
                                            offset: 311,
                                            length: 20,
                                            confidenceScore: 0.69
                                        }
                                    ],
                                    channel: '0',
                                    offset: 'PT2M39.32S'
                                },
                                {
                                    id: '28',
                                    redactedContent: {
                                        itn: 'yeah that sounds good i can go ahead and get started thank you for your help',
                                        lexical: 'yeah that sounds good i can go ahead and get started thank you for your help',
                                        text: 'Yeah, that sounds good. I can go ahead and get started. Thank you for your help.'
                                    },
                                    entities: [],
                                    channel: '1',
                                    offset: 'PT2M58.53S'
                                },
                                {
                                    id: '29',
                                    redactedContent: {
                                        itn: 'thank you',
                                        lexical: 'thank you',
                                        text: 'Thank you.'
                                    },
                                    entities: [],
                                    channel: '0',
                                    offset: 'PT3M1.97S'
                                }
                            ]
                        }
                    ]
                }
            }
        }
    }
];
